/**
 * CyberSweep React Query hooks
 * Uses Gobodo via recordStorageService when USE_GOBODO_API is enabled.
 * Uses Traversal API for CSAudits (by client) and PAWRecords (by audit) when IDs provided.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import type { GetRecordByIdOptions } from '../recordStorageService';
import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig from '../../config/gobodoConfig';
import {
  getCSAuditClientRelKey,
  getCSAuditTemplateRelKey,
  getPAWAuditWebsiteRelKey,
  getPAWCSAuditRelKey,
} from '../../config/gobodoAccountRelationshipKeys';
import {
  fetchClientCSAuditsViaTraversal,
  fetchMyCSAuditsViaAuditorFieldIndexes,
  fetchMyCSAuditsViaTraversal,
} from './csAuditTraversalService';
import { hydrateCSAuditRecordsForListUi } from './csAuditHydration';
import { findGraphUserBySfId } from '../gobodo/gobodoUserSfIdLookup';
import { resolveSfUserIdFromEmail } from '../../utils/sfUserIdFromEmail';
import { DEFAULT_CYBERSWEEP_STAGES } from './appConfigQueries';
import type { GenericRecord } from '../recordStorageService';
import { convertFromGobodoRecord, getGobodoRecordById, getGobodoRecordByKey } from '../gobodo/gobodoRecordService';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import {
  getRecordsForObject,
  getRecordsForObjectPage,
  getRecordById,
  createRecord,
  createRecordsBatch,
  updateRecord,
  deleteRecord,
} from '../recordStorageService';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { formatCyberSweepDisplayDate, parseCyberSweepDateToMs } from '../../utils/cyberSweepDisplayDate';
import { ensureGobodoCustomSuffixOnRecordKey } from '../../utils/gobodoRecordKeySuffix';
import {
  readCyberSweepClientTraversalString,
  readCSAuditClientNameFromClientTraversal,
  clientRecordExposesCyberSweepBoardTraversalField,
  getCyberSweepClientBoardTraversalApiName,
  getCSAuditClientNameTraversalFieldApiName,
  type CyberSweepClientBoardTraversalKey,
} from '../../utils/cyberSweepClientBoardTraversalFields';
import { normalizeToGobodoFormat } from '../../utils/objectApiNameNormalizer';
import { getRecordDisplayName } from '../../utils/recordDisplayName';
import { resolveGraphUserNamesBySfIds, findGraphUserByObjectId } from '../gobodo/gobodoUserSfIdLookup';
import {
  makeCacheOpts,
  CACHE_TTL,
  makeAuditTemplateByIdReadCacheOptsFromRecordId,
  makeAuditTemplateByKeyReadCacheOpts,
  makeAuditTemplateListReadCacheOpts,
  makeCyberSweepCsAuditToTemplateTraversalCacheOpts,
  type GobodoCacheOpts,
} from '../gobodo/gobodoCacheConfig';

export { hydrateCSAuditRecordsForListUi };

const CSAUDIT_OBJECT = 'CSAudit__c';
const AUDIT_TEMPLATE_OBJECT = 'Cyber_Sweep_Audit_Temp__stg5_c';
const PAWRECORD_OBJECT = 'PAWRecord__c';
// Object name matches RDS sync: Cyber_Sweep_Audit_Webs (abbreviated)
// Uses __stg5_c suffix to match staging environment (same as AUDIT_TEMPLATE_OBJECT)
const AUDIT_WEBSITE_OBJECT = 'Cyber_Sweep_Audit_Webs__stg5_c';
const CYBERSWEEPCLIENT_OBJECT = 'CyberSweepClient__c';
const CONTACT_OBJECT = 'Contact__c';
const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
const ACCOUNT_OWNER_REL_FIELD = `AccountOwnerRelField${CUSTOM_SUFFIX}`;
const CREATED_BY_REL_FIELD = `CreatedByRelField${CUSTOM_SUFFIX}`;
const LAST_MODIFIED_BY_REL_FIELD = `LastModifiedByRelField${CUSTOM_SUFFIX}`;
const CYBERSWEEP_PAW_TRAVERSAL_PAGE_SIZE = 30;
const CYBERSWEEP_PAW_TRAVERSAL_MAX_PAGES = 100;
const AUDIT_WEBSITE_TEMPLATE_PAGE_SIZE = 6;
const AUDIT_WEBSITE_TEMPLATE_MAX_PAGES = 100;
const REFERRAL_COMPANY_REL_FIELD = `ReferralCompanyRelField${CUSTOM_SUFFIX}`;
const REFERRAL_PERSON_ACCOUNT_REL_FIELD = `ReferralPersonAccountRelField${CUSTOM_SUFFIX}`;
const ACCOUNT_MANAGER_COMPANY_REL_FIELD = `AccountManagerCompanyRelField${CUSTOM_SUFFIX}`;
const REFERRAL_PARTNER_CONTACT_REL_FIELD = `ReferralPartnerContactRelField${CUSTOM_SUFFIX}`;
const ACCOUNT_MANAGER_REL_FIELD = `AccountManagerRelField${CUSTOM_SUFFIX}`;
const DAY_TO_DAY_CONTACT_REL_FIELD = `DayToDayContactRelField${CUSTOM_SUFFIX}`;
const CS_AUDIT_TEMPLATE_REL_FIELD = `CSAuditTemplateRelField${CUSTOM_SUFFIX}`;
const AUDIT_WEBSITE_TEMPLATE_REL_FIELD = `AuditTemplateRelField${CUSTOM_SUFFIX}`;
const AUDIT_WEBSITE_TEMPLATE_REL_KEY = `cs_audit_website_template_rel${CUSTOM_SUFFIX}`;
const AUDIT_WEBSITE_CONFIGURATION_REL_FIELD = `WebsiteConfigurationRelField${CUSTOM_SUFFIX}`;
const AUDIT_WEBSITE_CONFIGURATION_REL_KEY = `cs_audit_website_configuration_rel${CUSTOM_SUFFIX}`;
/** Gobodo skin traversal: CSAudit → template, projects AuditTypeDisplayName__c (setup: cybersweep-relationships Step 14). */
const CS_AUDIT_TEMPLATE_TRAV_DISPLAY_FIELD = `CSAuditTemplateTravAuditTypeDisplay${CUSTOM_SUFFIX}`;
const CYBERSWEEP_BOARD_TRAVERSAL_KEYS: readonly CyberSweepClientBoardTraversalKey[] = [
  'phaseStatus',
  'dueDate',
  'roadblock',
  'quarter',
  'fiscalYear',
  'phase1Start',
  'phase2Start',
  'auditor',
  'auditType',
  'auditKey',
] as const;
const CYBERSWEEP_BOARD_CLIENT_DATA_FIELDS = [
  'ClientName__c',
  'clientName',
  'AccountName__c',
  'Name__c',
  'Name',
  'name',
  'FirstName__c',
  'First_Name__c',
  'custom__first_name',
  'LastName__c',
  'Last_Name__c',
  'custom__last_name',
  'CreatedDate__c',
  'createdDate',
  'created_date',
  'cyberSweepStatus',
  'Cyber_Sweep_Status__c',
  'CyberSweepStatus__c',
  'custom__cyber_sweep_status',
  `Cyber_Sweep_Status${CUSTOM_SUFFIX}`,
  'CyberSweep_Start_Date__c',
  'cyberSweepStartDate',
  'custom__cyber_sweep_start_date',
  ...CYBERSWEEP_BOARD_TRAVERSAL_KEYS.map((key) =>
    getCyberSweepClientBoardTraversalApiName(key, CUSTOM_SUFFIX)
  ),
].filter(Boolean).join(',');
const AUDIT_TEMPLATE_LIST_DATA_FIELDS = [
  'Name',
  'name',
  'Name__c',
  'Phase_1_Due_Date_Days_c',
  'Phase_1_Due_Date_Days__c',
  'Phase_1_Due_Date_Days__stg5_c',
  'phase1DueDateDays',
  'Phase1DueDateDays__c',
  'Phase_2_Due_Date_Days_c',
  'Phase_2_Due_Date_Days__c',
  'Phase_2_Due_Date_Days__stg5_c',
  'phase2DueDateDays',
  'Phase2DueDateDays__c',
  'Checklist_Type__c',
  'checklistType',
  'OwnerId',
  'OwnerId__c',
  'ownerId',
  'OwnerName',
  'ownerName',
  'CreatedById',
  'CreatedById__c',
  'createdById',
  'CreatedByName',
  'createdByName',
  'CreatedDate',
  'CreatedDate__c',
  'createdDate',
  'LastModifiedById',
  'LastModifiedById__c',
  'lastModifiedById',
  'LastModifiedByName',
  'lastModifiedByName',
  'LastModifiedDate',
  'LastModifiedDate__c',
  'lastModifiedDate',
].join(',');
const CSAUDIT_FULL_SCAN_DATA_FIELDS = [
  'Name',
  'name',
  'Name__c',
  'Auditor__c',
  'auditor',
  'custom__auditor',
  'Auditor_Name__c',
  'auditorName',
  'AuditorName__c',
  'Phase_Status__c',
  'PhaseStatus__c',
  'phaseStatus',
  'custom__phase_status',
  'Due_Date__c',
  'DueDate__c',
  'dueDate',
  'custom__due_date',
  'AuditTypeDisplayName__c',
  'auditTypeDisplayName',
  'AuditTemplateName__c',
  'auditTemplateName',
  'Audit_Type__c',
  'AuditType__c',
  'auditType',
  'custom__audit_type',
  'Audit_Template__c',
  CS_AUDIT_TEMPLATE_REL_FIELD,
  'CSAuditTemplateRelField__c',
  'csAuditTemplateRelField',
  'auditTemplateLookupId',
  'Account__c',
  'ClientId__c',
  'clientId',
  'relatedClientId',
  'custom__account',
  'ClientName__c',
  'AccountName__c',
  'Account_Name__c',
  'FirstName__c',
  'First_Name__c',
  'custom__first_name',
  'LastName__c',
  'Last_Name__c',
  'custom__last_name',
  getCSAuditClientNameTraversalFieldApiName(CUSTOM_SUFFIX),
].filter(Boolean).join(',');
const CSAUDIT_TEMPLATE_PAW_DISCOVERY_DATA_FIELDS = [
  CS_AUDIT_TEMPLATE_REL_FIELD,
  'CSAuditTemplateRelField__c',
  'csAuditTemplateRelField',
  'Audit_Template__c',
  'Audit_Type__c',
  'AuditType__c',
  'auditTemplateLookupId',
].filter(Boolean).join(',');
const PAW_RELATED_LIST_DATA_FIELDS = [
  'Name',
  'name',
  'Name__c',
  'WebsiteDisplayName__c',
  'websiteDisplayName',
  'Website_Name__c',
  'websiteName',
  'Website__c',
  'website',
  'AuditWebsiteRelField__c',
  'auditWebsiteRelField',
  'Audit_Website__c',
  'custom__audit_website',
  'CreatedDate__c',
  'createdDate',
].filter(Boolean).join(',');

type GobodoTraversalReadOpts = GobodoCacheOpts & {
  objectSections?: string;
  dataFields?: string;
  maxObjectTypeFields?: number;
  includeExpandedFieldData?: boolean;
  includeRelatedMedia?: boolean;
};

function buildTraversalProjectionOpts(
  cacheOpts: GobodoCacheOpts,
  dataFields: string
): GobodoTraversalReadOpts {
  return {
    ...cacheOpts,
    objectSections: 'data',
    dataFields,
    maxObjectTypeFields: 0,
    includeExpandedFieldData: false,
    includeRelatedMedia: false,
  };
}

/**
 * When enabled, `fetchCSAuditTemplateInfo` tries `GraphTraversalApi.traverseObjects` (one hop) before key/id lookup.
 * Default **off** so the app does not call `/graph/data/traverse/objects` for CSAudit→template; use the skin field above
 * plus `fetchCSAuditTemplateRecordViaLookup` instead. Set `REACT_APP_CYBERSWEEP_USE_CSAUDIT_TEMPLATE_TRAVERSE_API=true`
 * only for legacy envs that lack template keys on CSAudit and still need graph hop resolution.
 */
const USE_CSAUDIT_TEMPLATE_TRAVERSE_API =
  process.env.REACT_APP_CYBERSWEEP_USE_CSAUDIT_TEMPLATE_TRAVERSE_API === '1' ||
  /^(true|yes)$/i.test(String(process.env.REACT_APP_CYBERSWEEP_USE_CSAUDIT_TEMPLATE_TRAVERSE_API ?? ''));

const ACCOUNT_LOOKUP_FIELDS = [
  REFERRAL_COMPANY_REL_FIELD,
  REFERRAL_PERSON_ACCOUNT_REL_FIELD,
  ACCOUNT_MANAGER_COMPANY_REL_FIELD,
  'Account__c',
  'clientId',
  'ClientId__c',
  'relatedClientId',
  'custom__account',
  'ReferralCompany__c',
  'Referral_Company__c',
  'PersonReferral__c',
  'Person_Referral__c',
  'Account_Manager_Company__c',
];

const CONTACT_LOOKUP_FIELDS = [
  REFERRAL_PARTNER_CONTACT_REL_FIELD,
  'ReferralContact__c',
  'Referral_Contact__c',
  ACCOUNT_MANAGER_REL_FIELD,
  'Contact__c',
  'AccountManager__c',
  DAY_TO_DAY_CONTACT_REL_FIELD,
  'Daily_Contact__c',
  'Day_to_Day_Contact__c',
];

const USER_LOOKUP_FIELDS = [
  ACCOUNT_OWNER_REL_FIELD,
  'OwnerId__c',
  CREATED_BY_REL_FIELD,
  'CreatedById__c',
  LAST_MODIFIED_BY_REL_FIELD,
  'LastModifiedById__c',
];

/** Gobodo list APIs return one page; production has many Clients/CSAudits — avoid a single 500-row slice or default 50-row cap. */
const CYBERSWEEP_LIST_PAGE_SIZE = 500;
const CYBERSWEEP_LIST_MAX_PAGES = 100;

/** Salesforce / graph field API name for CyberSweep enrollment (traversal index + client filtering). */
const CYBERSWEEP_START_DATE_FIELD = 'CyberSweep_Start_Date__c';

/**
 * Graph index name for enrolled-client traversal (`NOT_NULL` on the start-date index).
 * Defaults to {@link CYBERSWEEP_START_DATE_FIELD}; override only if the graph index key differs.
 */
const CYBERSWEEP_START_DATE_INDEX_NAME = (
  process.env.REACT_APP_CYBERSWEEP_START_DATE_INDEX_NAME ?? CYBERSWEEP_START_DATE_FIELD
).trim();

/** Salesforce / graph field for board column filter (must match indexed field in Gobodo). */
const CYBERSWEEP_STATUS_FIELD = 'Cyber_Sweep_Status__c';

const CYBERSWEEP_STATUS_INDEX_NAME = (
  process.env.REACT_APP_CYBERSWEEP_STATUS_INDEX_NAME ?? CYBERSWEEP_STATUS_FIELD
).trim();

/**
 * Bumps TanStack and Gobodo cache keys when the enrolled/board traversal OR semantics change
 * (primary / optional second start-date index on `Client`; CSAudit→`Client` one-hop union).
 */
const ENROLLED_CLIENT_TRAVERSAL_KEY = 'enrolled:start-date-union-csaudit-v4-projected-clients';

/** CSAudit `Phase_Status__c` graph index for roadblocked summary count (override if Gobodo index key differs). */
const CYBERSWEEP_PHASE_STATUS_INDEX_NAME = (
  process.env.REACT_APP_CYBERSWEEP_PHASE_STATUS_INDEX_NAME ?? 'Phase_Status__c'
).trim();

/** Traversal `count` / infinite-query page size for board columns (also in {@link cyberSweepKeys.boardColumn}). */
const CYBERSWEEP_BOARD_PAGE_SIZE = 15;
/**
 * Union graph can return the same `Client` multiple times per batch. One React Query "page" may need several
 * `traverseObjects` calls to collect enough **unique** clients; cap prevents runaway in pathological cases.
 */
const CYBERSWEEP_BOARD_UNION_DEDUPE_MAX_CHUNKS = 20;

/** TanStack Query: My Work CSAudit list stays fresh for 60s; unused data kept 5m after unmount. */
const MY_WORK_CSAUDITS_STALE_MS = 60_000;
const MY_WORK_CSAUDITS_GC_MS = 5 * 60_000;

export const cyberSweepKeys = {
  /** My Work: audits for current user via one-hop auditor traversal (or fallback list). Includes graph id so cache splits per environment. */
  myWorkCsAudits: (email: string) => ['cybersweep', 'my_work_cs_audits', email, GobodoConfig.graph()] as const,
  /**
   * CSAudit list queries: `mode` is `single` (one client id), `batch` (loaded client keys + traversal + field index union), or `full` (legacy paged scan — Team dashboard).
   */
  csAudits: (
    clientId?: string,
    clientKeysSig?: string,
    mode?: 'single' | 'batch' | 'full'
  ) => ['cybersweep', 'cs_audits', clientId ?? 'none', clientKeysSig ?? '', mode ?? 'single', GobodoConfig.graph()] as const,
  /** Single-audit detail: `id` is the Gobodo object `key` when known (or route param until `useCSAudit` resolves and promotes the key). */
  csAudit: (id: string) => ['cybersweep', 'cs_audit', id] as const,
  pawRecords: (auditId?: string) => ['cybersweep', 'paw_records', auditId] as const,
  pawRecordsInfinite: (auditId: string, pageSize: number) =>
    [
      'cybersweep',
      'paw_records',
      'infinite',
      auditId,
      pageSize,
      GobodoConfig.graph(),
      'paw-audit-traversal-v1',
    ] as const,
  pawRecordsCount: (auditId: string) =>
    [
      'cybersweep',
      'paw_records',
      'count',
      auditId,
      GobodoConfig.graph(),
      'paw-audit-traversal-v1',
    ] as const,
  auditWebsitesByTemplate: (templateId: string) =>
    ['cybersweep', 'audit_websites_by_template', GobodoConfig.graph(), templateId] as const,
  auditWebsitesByTemplateInfinite: (templateId: string, pageSize: number) =>
    [
      'cybersweep',
      'audit_websites_by_template',
      'infinite',
      templateId,
      pageSize,
      GobodoConfig.graph(),
      'audit-website-template-traversal-v1',
    ] as const,
  auditWebsiteCountByTemplate: (templateId: string) =>
    [
      'cybersweep',
      'audit_websites_by_template',
      'count',
      templateId,
      GobodoConfig.graph(),
      'audit-website-template-traversal-v1',
    ] as const,
  pawRecord: (id: string) => ['cybersweep', 'paw_record', id] as const,
  cyberSweepClients: () => ['cybersweep', 'clients'] as const,
  /** Paginated enrolled-client list (list view); pageParam is traversal `startIndex`. */
  cyberSweepClientsInfinite: (graphId: number, startDateIndex: string, pageSize: number) =>
    [
      'cybersweep',
      'clients',
      'infinite',
      graphId,
      startDateIndex,
      pageSize,
      getCyberSweepStartDateSecondaryIndexName() ?? '',
      ENROLLED_CLIENT_TRAVERSAL_KEY,
    ] as const,
  /** Server count for enrolled clients (NOT_NULL start date [+ optional second date index]). */
  cyberSweepClientsEnrolledCount: (graphId: number, startDateIndex: string) =>
    [
      'cybersweep',
      'clients',
      'enrolled-count',
      graphId,
      startDateIndex,
      getCyberSweepStartDateSecondaryIndexName() ?? '',
      ENROLLED_CLIENT_TRAVERSAL_KEY,
    ] as const,
  /** Board column infinite query; `pageParam` === traversal startIndex. See {@link useCyberSweepBoardColumn}. */
  boardColumn: (stageKey: string, searchQuerySig?: string) =>
    [
      'cybersweep',
      'board-column',
      stageKey,
      'startIndex',
      CYBERSWEEP_BOARD_PAGE_SIZE,
      'board-col-csaudit-union-v5',
      CYBERSWEEP_START_DATE_INDEX_NAME,
      CYBERSWEEP_STATUS_INDEX_NAME,
      GobodoConfig.graph(),
      searchQuerySig ?? '',
      ENROLLED_CLIENT_TRAVERSAL_KEY,
    ] as const,
  /** Same filters as boardColumn; paired with traverseObjectsCount. */
  boardColumnCount: (stageKey: string, searchQuerySig?: string) =>
    [
      'cybersweep',
      'board-column',
      'count',
      stageKey,
      CYBERSWEEP_START_DATE_INDEX_NAME,
      CYBERSWEEP_STATUS_INDEX_NAME,
      GobodoConfig.graph(),
      searchQuerySig ?? '',
      ENROLLED_CLIENT_TRAVERSAL_KEY,
    ] as const,
  /** Zero-hop CSAudit traversal count: Phase_Status__c IN roadblock picklist values. See {@link useCyberSweepRoadblockedCsAuditCount}. */
  roadblockedCsAuditPhaseCount: (
    graphId: number,
    phaseIndex: string,
    roadblockPicklistSig: string,
    searchQuerySig?: string
  ) =>
    [
      'cybersweep',
      'roadblocked-cs-audit-count',
      graphId,
      phaseIndex,
      roadblockPicklistSig,
      searchQuerySig ?? '',
    ] as const,
};

/** Canonical workflow stage keys for the kanban; aligns with {@link DEFAULT_CYBERSWEEP_STAGES}. */
export type CyberSweepBoardStageKey = (typeof DEFAULT_CYBERSWEEP_STAGES)[number];

/**
 * Allowed `Cyber_Sweep_Status__c` picklist values per kanban column, passed as one `IndexFilter` `EQUALS` with
 * multiple `values` (OR within the same field). Baseline matches production; extra strings cover staging / legacy spellings.
 *
 * For OR across **different indexed fields**, use `GraphTraversalInput.setUnionWith` (see Credit Block case pattern)
 * instead of extending this map.
 */
export const CYBERSWEEP_BOARD_STAGE_STATUS_VALUES: Record<CyberSweepBoardStageKey, string[]> = {
  'Pending Enrollments': ['Pending Enrollments', 'Pending Enrollment'],
  'Lacking Requirements': ['Lacking Requirements', 'Missing Requirements'],
  'Awaiting Assignment': ['Awaiting Assignment'],
  // Prod: "Pending Checklist"; staging may use plural.
  'Pending Checklists': ['Pending Checklist', 'Pending Checklists'],
  'Awaiting Initial Report': ['Awaiting Initial Report'],
  'Awaiting Final Report': ['Awaiting Final Report'],
  'Quarterly Report 1': ['Quarterly Report 1'],
  'Quarterly Report 2': ['Quarterly Report 2'],
  'Quarterly Report 3': ['Quarterly Report 3'],
  'Quarterly Report 4': ['Quarterly Report 4'],
};

function dedupeStringsPreserveOrder(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of values) {
    const v = cleanString(raw);
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

/**
 * Phase_Status__c picklist values meaning “roadblocked” for {@link buildCyberSweepRoadblockedCsAuditTraversalInput}.
 * Extends the legacy UI heuristic (lowercase includes both "road" and "block"). Add env-specific spellings as needed.
 */
const CYBERSWEEP_ROADBLOCK_PHASE_STATUS_VALUES = dedupeStringsPreserveOrder([
  'Phase 1 - Road Block',
  'Phase 2 - Road Block',
  'P1 - Road Block',
  'P2 - Road Block',
  'Roadblocked',
]);

/** Hydration: max parallel client→audit traversals per page (avoid API stampede). */
const CYBERSWEEP_BOARD_AUDIT_FETCH_CONCURRENCY = 6;

const CYBERSWEEP_CLIENT_TRAVERSAL_PAGE_SIZE = 500;
const CYBERSWEEP_CLIENT_TRAVERSAL_MAX_PAGES = 100;

/**
 * List/table: smaller traversal pages for faster first paint (same “listed clients” union as count and board data).
 * Full client scans still use {@link CYBERSWEEP_CLIENT_TRAVERSAL_PAGE_SIZE} internally.
 */
const CYBERSWEEP_CLIENT_LIST_TRAVERSAL_PAGE_SIZE = 15;

/** Union branch fetch width: short `count` on the zero-hop head was starving the CSAudit→Client OR leg. */
const CYBERSWEEP_CSAUDIT_CLIENT_UNION_BRANCH_MIN_FETCH = 500;

function widenCsAuditToClientUnionBranchCount(requestedCount: number): number {
  return requestedCount > 1 ? Math.max(requestedCount, CYBERSWEEP_CSAUDIT_CLIENT_UNION_BRANCH_MIN_FETCH) : 1;
}

/**
 * CSAudit → `Client` (one hop, OUTGOING on `cs_audit_client_rel`). Listed clients: no CSAudit filters — ORs
 * clients reachable from any CSAudit with clients missing a start date on the Account.
 */
function buildCyberSweepListedClientFromCsAuditRelationshipTraversalInput(
  pagination?: { startIndex: number; count: number }
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const csType = normalizeToGobodoFormat(CSAUDIT_OBJECT);
  const clientTypeKey = normalizeToGobodoFormat('Client');
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  input.setStartObjectTypes([csType]);
  input.setEndObjectTypes([clientTypeKey, normalizeToGobodoFormat('Account')]); // TEMP: Include Account for test records
  input.setRelationshipTypes([getCSAuditClientRelKey()]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.OUTGOING);
  input.setMaxPathLength(1);
  if (pagination) {
    input.setStartIndex(pagination.startIndex);
    input.setCount(widenCsAuditToClientUnionBranchCount(pagination.count));
  }
  return input;
}

/**
 * Board: CSAudits in column phase → `Client`. Uses `Phase_Status__c` with the same stage strings as
 * the Client `Cyber_Sweep_Status__c` column (picklists stay aligned in Salesforce/Gobodo).
 */
function buildCyberSweepBoardClientFromCsAuditRelationshipTraversalInput(
  phaseStatusValues: string[],
  pagination?: { startIndex: number; count: number }
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const csType = normalizeToGobodoFormat(CSAUDIT_OBJECT);
  const clientTypeKey = normalizeToGobodoFormat('Client');
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  input.setStartObjectTypes([csType]);
  input.setEndObjectTypes([clientTypeKey, normalizeToGobodoFormat('Account')]); // TEMP: Include Account for test records
  input.setRelationshipTypes([getCSAuditClientRelKey()]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.OUTGOING);
  input.setMaxPathLength(1);
  const phaseFilter = new GobodoApi.IndexFilter();
  phaseFilter.setIndexName(CYBERSWEEP_PHASE_STATUS_INDEX_NAME);
  phaseFilter.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
  phaseFilter.setValues(phaseStatusValues);
  input.setStartObjectFieldFilters([phaseFilter]);
  if (pagination) {
    input.setStartIndex(pagination.startIndex);
    input.setCount(widenCsAuditToClientUnionBranchCount(pagination.count));
  }
  return input;
}

/** Optional second Gobodo index for CyberSweep start date — unioned (OR) with the primary index when set. */
function getCyberSweepStartDateSecondaryIndexName(): string | undefined {
  const v = String(process.env.REACT_APP_CYBERSWEEP_START_DATE_ALT_INDEX_NAME ?? '').trim();
  if (!v) return undefined;
  const primary = CYBERSWEEP_START_DATE_INDEX_NAME;
  return v.toLowerCase() === primary.toLowerCase() ? undefined : v;
}

/** Chain nested `setUnionWith` (same pattern as Credit Block cases and My Audits CSAudit branches). */
function chainUnionTraversals(
  head: InstanceType<typeof GobodoApi.GraphTraversalInput>,
  branches: Array<InstanceType<typeof GobodoApi.GraphTraversalInput>>
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  if (branches.length === 0) return head;
  let tail = head;
  for (const branch of branches) {
    tail.setUnionWith(branch);
    tail = branch;
  }
  return head;
}

function applyCyberSweepTraversalSearch(
  input: InstanceType<typeof GobodoApi.GraphTraversalInput>,
  searchQuery?: string
): void {
  const q = searchQuery?.trim();
  if (!q) return;
  try {
    input.setSearchQuery(q);
    input.setSearchGraphId(GobodoConfig.graph());
  } catch {
    // Backend may not support traversal search on all path shapes; zero-hop list still works.
  }
}

function cyberSweepClientTraversalCacheOpts(indexName: string, startIndex: number, count: number): GobodoCacheOpts {
  return makeCacheOpts(
    [
      'cybersweep',
      'clients',
      'client',
      'start-date-index',
      indexName,
      getCyberSweepStartDateSecondaryIndexName() ?? '',
      String(startIndex),
      String(count),
      ENROLLED_CLIENT_TRAVERSAL_KEY,
    ],
    CACHE_TTL.SHORT
  );
}

function buildCyberSweepEnrolledClientGraphTraversalInput(
  clientTypeKey: string,
  startDateIndexName: string,
  pagination?: { startIndex: number; count: number }
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const input = new GobodoApi.GraphTraversalInput();
  const graphId = GobodoConfig.graph();
  input.setGraphId(graphId);
  input.setStartObjectTypes([clientTypeKey, normalizeToGobodoFormat('Account')]); // TEMP: Include Account for test records
  input.setEndObjectTypes([clientTypeKey, normalizeToGobodoFormat('Account')]); // TEMP: Include Account for test records
  input.setMaxPathLength(0);
  const dateFilter = new GobodoApi.IndexFilter();
  dateFilter.setIndexName(startDateIndexName);
  // Gobodo supports NOT_NULL for index filters; SDK FunctionEnum may not list it yet.
  dateFilter.setFunction('NOT_NULL');
  // NOT_NULL must not send compare operands (contrast GREATER_THAN + CYBERSWEEP_START_DATE_INDEX_SENTINEL).
  dateFilter.setValues([]);
  input.setStartObjectFieldFilters([dateFilter]);
  if (pagination) {
    input.setStartIndex(pagination.startIndex);
    input.setCount(pagination.count);
  }
  return input;
}

/**
 * Enrolled Client set: (primary start-date `NOT_NULL`) ∪ (optional second start-date index) ∪ (CSAudit → `Client` one hop).
 */
function buildCyberSweepEnrolledClientListTraversalInput(
  clientTypeKey: string,
  startDateIndexName: string,
  pagination?: { startIndex: number; count: number }
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const primary = buildCyberSweepEnrolledClientGraphTraversalInput(clientTypeKey, startDateIndexName, pagination);
  const secondary = getCyberSweepStartDateSecondaryIndexName();
  const csAuditToClient = buildCyberSweepListedClientFromCsAuditRelationshipTraversalInput(pagination);
  if (secondary) {
    return chainUnionTraversals(primary, [
      buildCyberSweepEnrolledClientGraphTraversalInput(clientTypeKey, secondary, pagination),
      csAuditToClient,
    ]);
  }
  return chainUnionTraversals(primary, [csAuditToClient]);
}

/**
 * Single graph definition for the CyberSweep **listed clients** set: same OR as
 * {@link buildCyberSweepEnrolledClientListTraversalInput}. Use for `traverseObjects` (list / board data load) and
 * `traverseObjectsCount` (Total Clients) so the count never diverges from the list.
 */
export function buildCyberSweepListedClientsTraversalInput(
  startDateIndexName: string,
  pagination: { startIndex: number; count: number }
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const clientTypeKey = normalizeToGobodoFormat('Client');
  return buildCyberSweepEnrolledClientListTraversalInput(clientTypeKey, startDateIndexName, pagination);
}

function buildCyberSweepListedClientsCountTraversalInput(
  startDateIndexName: string
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const clientTypeKey = normalizeToGobodoFormat('Client');
  return buildCyberSweepEnrolledClientListTraversalInput(clientTypeKey, startDateIndexName);
}

/**
 * Board column: zero-hop `Client` (start date `NOT_NULL` AND status) ∪ one-hop CSAudit → `Client` (phase in column).
 */
export function buildCyberSweepBoardColumnTraversalInput(
  stageKey: string,
  pagination: { startIndex: number; count: number },
  options?: { searchQuery?: string }
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const rawValues = CYBERSWEEP_BOARD_STAGE_STATUS_VALUES[stageKey as CyberSweepBoardStageKey];
  if (!rawValues?.length) {
    throw new Error(`Unknown CyberSweep board stage: "${stageKey}"`);
  }
  const statusValues = dedupeStringsPreserveOrder(rawValues);
  const clientTypeKey = normalizeToGobodoFormat('Client');
  const zeroHop = new GobodoApi.GraphTraversalInput();
  zeroHop.setGraphId(GobodoConfig.graph());
  zeroHop.setStartObjectTypes([clientTypeKey, normalizeToGobodoFormat('Account')]); // TEMP: Include Account for test records
  zeroHop.setEndObjectTypes([clientTypeKey, normalizeToGobodoFormat('Account')]); // TEMP: Include Account for test records
  zeroHop.setMaxPathLength(0);
  const dateFilter = new GobodoApi.IndexFilter();
  dateFilter.setIndexName(CYBERSWEEP_START_DATE_INDEX_NAME);
  dateFilter.setFunction('NOT_NULL');
  dateFilter.setValues([]);
  const statusFilter = new GobodoApi.IndexFilter();
  statusFilter.setIndexName(CYBERSWEEP_STATUS_INDEX_NAME);
  statusFilter.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
  statusFilter.setValues(statusValues);
  zeroHop.setStartObjectFieldFilters([dateFilter, statusFilter]);
  zeroHop.setStartIndex(pagination.startIndex);
  zeroHop.setCount(pagination.count);
  applyCyberSweepTraversalSearch(zeroHop, options?.searchQuery);
  const csAuditBranch = buildCyberSweepBoardClientFromCsAuditRelationshipTraversalInput(statusValues, pagination);
  zeroHop.setUnionWith(csAuditBranch);
  return zeroHop;
}

function buildCyberSweepBoardColumnCountTraversalInput(
  stageKey: string,
  options?: { searchQuery?: string }
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const rawValues = CYBERSWEEP_BOARD_STAGE_STATUS_VALUES[stageKey as CyberSweepBoardStageKey];
  if (!rawValues?.length) {
    throw new Error(`Unknown CyberSweep board stage: "${stageKey}"`);
  }
  const statusValues = dedupeStringsPreserveOrder(rawValues);
  const clientTypeKey = normalizeToGobodoFormat('Client');
  const zeroHop = new GobodoApi.GraphTraversalInput();
  zeroHop.setGraphId(GobodoConfig.graph());
  zeroHop.setStartObjectTypes([clientTypeKey, normalizeToGobodoFormat('Account')]);
  zeroHop.setEndObjectTypes([clientTypeKey, normalizeToGobodoFormat('Account')]);
  zeroHop.setMaxPathLength(0);
  const dateFilter = new GobodoApi.IndexFilter();
  dateFilter.setIndexName(CYBERSWEEP_START_DATE_INDEX_NAME);
  dateFilter.setFunction('NOT_NULL');
  dateFilter.setValues([]);
  const statusFilter = new GobodoApi.IndexFilter();
  statusFilter.setIndexName(CYBERSWEEP_STATUS_INDEX_NAME);
  statusFilter.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
  statusFilter.setValues(statusValues);
  zeroHop.setStartObjectFieldFilters([dateFilter, statusFilter]);
  applyCyberSweepTraversalSearch(zeroHop, options?.searchQuery);
  zeroHop.setUnionWith(buildCyberSweepBoardClientFromCsAuditRelationshipTraversalInput(statusValues));
  return zeroHop;
}

/**
 * Zero-hop **CSAudit** traversal: `Phase_Status__c` `EQUALS` with all roadblock picklist strings in one filter
 * (same multi-value OR pattern as board columns). Counts CSAudit rows in a roadblock phase (typically one per client).
 * Optional `searchQuery` matches board column server-side name search when provided.
 */
export function buildCyberSweepRoadblockedCsAuditTraversalInput(
  pagination: { startIndex: number; count: number },
  options?: { searchQuery?: string }
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  if (!CYBERSWEEP_ROADBLOCK_PHASE_STATUS_VALUES.length) {
    throw new Error('CyberSweep roadblock phase status values are empty');
  }
  const csType = normalizeToGobodoFormat(CSAUDIT_OBJECT);
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  input.setStartObjectTypes([csType]);
  input.setEndObjectTypes([csType]);
  input.setMaxPathLength(0);
  const phaseFilter = new GobodoApi.IndexFilter();
  phaseFilter.setIndexName(CYBERSWEEP_PHASE_STATUS_INDEX_NAME);
  phaseFilter.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
  phaseFilter.setValues(CYBERSWEEP_ROADBLOCK_PHASE_STATUS_VALUES);
  input.setStartObjectFieldFilters([phaseFilter]);
  input.setStartIndex(pagination.startIndex);
  input.setCount(pagination.count);
  const q = options?.searchQuery?.trim();
  if (q) {
    input.setSearchQuery(q);
  }
  return input;
}

function buildCyberSweepRoadblockedCsAuditCountTraversalInput(
  options?: { searchQuery?: string }
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  if (!CYBERSWEEP_ROADBLOCK_PHASE_STATUS_VALUES.length) {
    throw new Error('CyberSweep roadblock phase status values are empty');
  }
  const csType = normalizeToGobodoFormat(CSAUDIT_OBJECT);
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  input.setStartObjectTypes([csType]);
  input.setEndObjectTypes([csType]);
  input.setMaxPathLength(0);
  const phaseFilter = new GobodoApi.IndexFilter();
  phaseFilter.setIndexName(CYBERSWEEP_PHASE_STATUS_INDEX_NAME);
  phaseFilter.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
  phaseFilter.setValues(CYBERSWEEP_ROADBLOCK_PHASE_STATUS_VALUES);
  input.setStartObjectFieldFilters([phaseFilter]);
  const q = options?.searchQuery?.trim();
  if (q) {
    input.setSearchQuery(q);
  }
  return input;
}

function cyberSweepRoadblockedCountCacheOpts(searchQuery?: string): GobodoCacheOpts {
  const picklistSig = [...CYBERSWEEP_ROADBLOCK_PHASE_STATUS_VALUES].sort().join('|');
  return makeCacheOpts(
    [
      'cybersweep',
      'roadblocked-cs-audit',
      'traverse-count',
      String(GobodoConfig.graph()),
      CYBERSWEEP_PHASE_STATUS_INDEX_NAME,
      picklistSig,
      searchQuery ?? '',
    ],
    CACHE_TTL.SHORT
  );
}

async function fetchCyberSweepRoadblockedCsAuditCount(searchQuery?: string): Promise<number> {
  const countInput = buildCyberSweepRoadblockedCsAuditCountTraversalInput({ searchQuery });
  const cacheOpts = cyberSweepRoadblockedCountCacheOpts(searchQuery);
  const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
  const countRaw = await retryWithBackoff(
    () => traversalApi.traverseObjectsCount(countInput, cacheOpts),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  return parseCyberSweepBoardTraversalCount(countRaw);
}

function cyberSweepBoardColumnCacheOpts(
  stageKey: string,
  startIndex: number,
  count: number,
  searchQuery?: string
): GobodoCacheOpts {
  return makeCacheOpts(
    [
      'cybersweep',
      'board-column',
      'traverse',
      stageKey,
      String(GobodoConfig.graph()),
      CYBERSWEEP_START_DATE_INDEX_NAME,
      CYBERSWEEP_STATUS_INDEX_NAME,
      String(startIndex),
      String(count),
      searchQuery ?? '',
      ENROLLED_CLIENT_TRAVERSAL_KEY,
    ],
    CACHE_TTL.SHORT
  );
}

function cyberSweepBoardColumnCountCacheOpts(stageKey: string, searchQuery?: string): GobodoCacheOpts {
  return makeCacheOpts(
    [
      'cybersweep',
      'board-column',
      'traverse-count',
      stageKey,
      String(GobodoConfig.graph()),
      CYBERSWEEP_START_DATE_INDEX_NAME,
      CYBERSWEEP_STATUS_INDEX_NAME,
      searchQuery ?? '',
      ENROLLED_CLIENT_TRAVERSAL_KEY,
    ],
    CACHE_TTL.SHORT
  );
}

/** Parses GraphTraversalApi.traverseObjectsCount response (same shapes as Credit Block case counts). */
export function parseCyberSweepBoardTraversalCount(raw: unknown): number {
  const obj = raw != null && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  const data = obj?.data != null && typeof obj.data === 'object'
    ? (obj.data as Record<string, unknown>)
    : null;
  const directCandidates = [
    obj?.totalElements,
    obj?.totalCount,
    obj?.total,
    obj?.count,
    data?.totalElements,
    data?.totalCount,
    data?.total,
    data?.count,
  ];
  for (const candidate of directCandidates) {
    if (typeof candidate === 'number' && !Number.isNaN(candidate)) {
      return candidate;
    }
  }
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  return 0;
}

async function fetchCyberSweepBoardColumnTraversalCount(stageKey: string, searchQuery?: string): Promise<number> {
  const countInput = buildCyberSweepBoardColumnCountTraversalInput(stageKey, { searchQuery });
  const cacheOpts = cyberSweepBoardColumnCountCacheOpts(stageKey, searchQuery);
  const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
  const countRaw = await retryWithBackoff(
    () => traversalApi.traverseObjectsCount(countInput, cacheOpts),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  return parseCyberSweepBoardTraversalCount(countRaw);
}

function requireCyberSweepTraverseObjectsArray(result: unknown): unknown[] {
  if (Array.isArray(result)) return result;
  throw new Error(
    result == null
      ? 'CyberSweep Client graph traversal returned no data.'
      : `CyberSweep Client graph traversal returned an invalid response (${typeof result}).`
  );
}

/**
 * One page of CyberSweep list clients: start date `NOT_NULL` (optional second date index) **or** one hop
 * CSAudit → Client — same `setUnionWith` OR semantics as My Work CSAudit traversals.
 */
export type CyberSweepClientTraversalPageFetch = {
  records: Record<string, unknown>[];
  /** Pre-dedupe `traverseObjects` count; use for infinite-query “has more” (not `records.length`) after union. */
  rawObjectCount: number;
};

export async function fetchCyberSweepClientsTraversalPage(
  startDateIndexName: string,
  startIndex: number,
  count: number
): Promise<CyberSweepClientTraversalPageFetch> {
  const clientTypeKey = normalizeToGobodoFormat('Client');
  const client = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(client);
  const input = buildCyberSweepListedClientsTraversalInput(startDateIndexName, { startIndex, count });
  const cacheOpts = cyberSweepClientTraversalCacheOpts(startDateIndexName, startIndex, count);
  const readOpts = buildTraversalProjectionOpts(cacheOpts, CYBERSWEEP_BOARD_CLIENT_DATA_FIELDS);
  const result = await retryWithBackoff(
    () => traversalApi.traverseObjects(input, readOpts),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  const objects = requireCyberSweepTraverseObjectsArray(result);
  const rawObjectCount = objects.length;
  const out: Record<string, unknown>[] = [];
  const seen = new Set<string>();
  for (const gobodoObj of objects) {
    try {
      const converted = convertFromGobodoRecord(
        gobodoObj as Record<string, unknown>,
        clientTypeKey
      ) as Record<string, unknown>;
      const dedupeKey = String(converted.id ?? converted.key ?? '');
      if (!dedupeKey || seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      out.push(converted);
    } catch {
      /* skip malformed row */
    }
  }
  return { records: out, rawObjectCount };
}

/**
 * Full CyberSweep client list for **board** (paged `traverseObjects` using
 * {@link buildCyberSweepListedClientsTraversalInput}). Same set as list infinite query and total count.
 */
async function fetchCyberSweepClientsViaStartDateIndexTraversal(
  startDateIndexName: string
): Promise<Record<string, unknown>[]> {
  const acc: Record<string, unknown>[] = [];
  const seen = new Set<string>();

  for (let page = 0; page < CYBERSWEEP_CLIENT_TRAVERSAL_MAX_PAGES; page++) {
    const startIndex = page * CYBERSWEEP_CLIENT_TRAVERSAL_PAGE_SIZE;
    const { records, rawObjectCount } = await fetchCyberSweepClientsTraversalPage(
      startDateIndexName,
      startIndex,
      CYBERSWEEP_CLIENT_TRAVERSAL_PAGE_SIZE
    );
    if (rawObjectCount === 0) break;
    for (const converted of records) {
      const dedupeKey = String(converted.id ?? converted.key ?? '');
      if (!dedupeKey || seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      acc.push(converted);
    }
    if (rawObjectCount < CYBERSWEEP_CLIENT_TRAVERSAL_PAGE_SIZE) break;
  }

  return acc;
}

/** Server total for {@link useCyberSweepEnrolledClientsCount}; same input as a list page with `count: 1`. */
async function fetchCyberSweepEnrolledClientsCount(startDateIndexName: string): Promise<number> {
  const input = buildCyberSweepListedClientsCountTraversalInput(startDateIndexName);
  const cacheOpts = makeCacheOpts(
    [
      'cybersweep',
      'clients',
      'enrolled-count',
      String(GobodoConfig.graph()),
      startDateIndexName,
      getCyberSweepStartDateSecondaryIndexName() ?? '',
      ENROLLED_CLIENT_TRAVERSAL_KEY,
    ],
    CACHE_TTL.SHORT
  );
  const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
  const countRaw = await retryWithBackoff(
    () => traversalApi.traverseObjectsCount(input, cacheOpts),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  return parseCyberSweepBoardTraversalCount(countRaw);
}

function extractClientKeyFromCsAuditRecord(audit: Record<string, unknown>): string {
  const data = (audit.data as Record<string, unknown>) ?? {};
  return cleanString(
    audit.Account__c ??
      audit.clientId ??
      audit.ClientId__c ??
      data.Account__c ??
      data.clientId ??
      data.custom__account ??
      data.Custom__account__c
  );
}

function registerCyberSweepClientRefKey(target: Set<string>, ref: unknown): void {
  const t = cleanString(ref);
  if (!t) return;
  target.add(t);
  const suffixed = ensureGobodoCustomSuffixOnRecordKey(t);
  if (suffixed) target.add(suffixed);
  const stripped = stripKnownSuffix(t);
  if (stripped && stripped !== t) target.add(stripped);
}

function clientRefMatchesCsAuditKeySet(r: Record<string, unknown>, keys: Set<string>): boolean {
  const data = (r.data as Record<string, unknown>) ?? {};
  const candidates = [r.id, r.key, data.id, data.key].map((x) => cleanString(x)).filter(Boolean) as string[];
  for (const c of candidates) {
    if (keys.has(c)) return true;
    if (keys.has(ensureGobodoCustomSuffixOnRecordKey(c))) return true;
    const st = stripKnownSuffix(c);
    if (st && keys.has(st)) return true;
  }
  return false;
}

/**
 * Paged `CSAudit__c` scan for “has audit” (fallback; mirrors CSAudit → Client union when traversal is off).
 */
async function collectClientKeysWithCsAuditsForFallback(): Promise<Set<string>> {
  const keys = new Set<string>();
  for (let page = 0; page < CYBERSWEEP_LIST_MAX_PAGES; page++) {
    const { records, totalRecords } = await getRecordsForObjectPage(CSAUDIT_OBJECT, {
      page,
      size: CYBERSWEEP_LIST_PAGE_SIZE,
    });
    for (const r of records as Record<string, unknown>[]) {
      registerCyberSweepClientRefKey(keys, extractClientKeyFromCsAuditRecord(r));
    }
    if (records.length < CYBERSWEEP_LIST_PAGE_SIZE) break;
    if (totalRecords > 0 && (page + 1) * CYBERSWEEP_LIST_PAGE_SIZE >= totalRecords) break;
  }
  return keys;
}

/** List inclusion when not using the Gobodo enrolled traversal: start date set OR a CSAudit row references the client. */
function recordIsCyberSweepListedClient(
  r: Record<string, unknown>,
  clientKeysWithCsAudit: Set<string>
): boolean {
  return recordHasCyberSweepStartDate(r) || clientRefMatchesCsAuditKeySet(r, clientKeysWithCsAudit);
}

async function fallbackFetchCyberSweepClientsByPaging(): Promise<Record<string, unknown>[]> {
  const clientKeysWithCsAudit = await collectClientKeysWithCsAuditsForFallback();
  const cyberSweepClients: Record<string, unknown>[] = [];
  const seen = new Set<string>();
  let totalRowsScanned = 0;

  for (let page = 0; page < CYBERSWEEP_LIST_MAX_PAGES; page++) {
    const { records, totalRecords } = await getRecordsForObjectPage('Client', {
      page,
      size: CYBERSWEEP_LIST_PAGE_SIZE,
    });
    totalRowsScanned += records.length;

    for (const r of records as Record<string, unknown>[]) {
      const dedupeKey = String(r.id ?? r.key ?? '');
      if (!dedupeKey) continue;
      if (!recordIsCyberSweepListedClient(r, clientKeysWithCsAudit)) continue;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      cyberSweepClients.push(r);
    }

    if (records.length < CYBERSWEEP_LIST_PAGE_SIZE) break;
    if (totalRecords > 0 && (page + 1) * CYBERSWEEP_LIST_PAGE_SIZE >= totalRecords) break;
  }

  if (process.env.NODE_ENV === 'development') {
    console.log(
      '[CyberSweep] Fallback Client paging: scanned',
      totalRowsScanned,
      'rows; clients with start date or CS_Audit (refs:',
      clientKeysWithCsAudit.size,
      '):',
      cyberSweepClients.length
    );
  }
  return cyberSweepClients;
}

async function fetchAllRecordsViaPages(
  objectApiName: string,
  options?: { pageSize?: number; maxPages?: number; dataFields?: string; cacheKeyTag?: string }
): Promise<Record<string, unknown>[]> {
  const pageSize = options?.pageSize ?? CYBERSWEEP_LIST_PAGE_SIZE;
  const maxPages = options?.maxPages ?? CYBERSWEEP_LIST_MAX_PAGES;
  const acc: Record<string, unknown>[] = [];
  const projectedDataFields = options?.dataFields?.trim() ?? '';
  const normalizedObjectApiName = normalizeToGobodoFormat(objectApiName);
  const canUseProjectedObjectReads = USE_GOBODO_API && !!projectedDataFields;
  const projectedObjectsApi = canUseProjectedObjectReads
    ? new GobodoApi.GraphObjectsApi(getGobodoClient())
    : null;

  for (let page = 0; page < maxPages; page++) {
    const { records, totalRecords } = canUseProjectedObjectReads
      ? await retryWithBackoff(async () => {
          const raw = await projectedObjectsApi!.getObjectsInGraphByType(
            GobodoConfig.graph(),
            normalizedObjectApiName,
            {
              page,
              size: pageSize,
              ...buildTraversalProjectionOpts(
                makeCacheOpts(
                  [
                    'cybersweep',
                    'projected-object-page',
                    normalizedObjectApiName,
                    String(page),
                    String(pageSize),
                    options?.cacheKeyTag ?? 'default',
                  ],
                  CACHE_TTL.SHORT,
                  { group: ['cybersweep', 'projected-object-page', normalizedObjectApiName] }
                ),
                projectedDataFields
              ),
            }
          );

          const records = Array.isArray(raw)
            ? raw
            : Array.isArray((raw as { content?: unknown[] }).content)
              ? ((raw as { content?: unknown[] }).content ?? [])
              : Array.isArray((raw as { items?: unknown[] }).items)
                ? ((raw as { items?: unknown[] }).items ?? [])
                : [];
          const totalCandidates = [
            (raw as { totalRecords?: unknown })?.totalRecords,
            (raw as { totalElements?: unknown })?.totalElements,
            (raw as { total?: unknown })?.total,
            (raw as { count?: unknown })?.count,
          ];
          const parsedTotal = totalCandidates.find(
            (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0
          ) as number | undefined;
          return {
            records: records as Record<string, unknown>[],
            totalRecords: parsedTotal ?? (records as unknown[]).length,
          };
        }, { maxRetries: 2, initialDelayMs: 1000 })
      : await getRecordsForObjectPage(objectApiName, { page, size: pageSize });
    acc.push(...(records as Record<string, unknown>[]));
    if (records.length < pageSize) break;
    if (totalRecords > 0 && (page + 1) * pageSize >= totalRecords) break;
  }
  return acc;
}

/** True when `CyberSweep_Start_Date__c` (or graph snake_case mirror) is populated. */
function recordHasCyberSweepStartDate(r: Record<string, unknown>): boolean {
  const data = (r.data as Record<string, unknown>) ?? r;
  const startDate =
    r[CYBERSWEEP_START_DATE_FIELD] ??
    data[CYBERSWEEP_START_DATE_FIELD] ??
    r.cyberSweepStartDate ??
    r.custom__cyber_sweep_start_date ??
    data.cyberSweepStartDate ??
    data.custom__cyber_sweep_start_date;
  return startDate !== null && startDate !== undefined && startDate !== '';
}

function cleanString(value: unknown): string {
  return value == null ? '' : String(value).trim();
}

function stripKnownSuffix(value: string): string {
  const raw = cleanString(value);
  if (!raw) return '';
  for (const suffix of [CUSTOM_SUFFIX, '__stg3_c', '__stg_c', '__dev_c', '__stg', '__c']) {
    if (suffix && raw.toLowerCase().endsWith(String(suffix).toLowerCase())) {
      return raw.slice(0, raw.length - String(suffix).length);
    }
  }
  return raw;
}

function uniqueLookupTokens(values: unknown[]): string[] {
  return Array.from(
    new Set(
      values
        .map(cleanString)
        .flatMap((value) => {
          const stripped = stripKnownSuffix(value);
          return [value, stripped].filter(Boolean);
        })
    )
  );
}

function extractLookupValues(records: Record<string, unknown>[], fieldNames: string[]): string[] {
  const values: unknown[] = [];
  for (const record of records) {
    // Check both top-level fields and nested data object (Gobodo often stores fields in data)
    const data = record.data as Record<string, unknown> | undefined;
    for (const fieldName of fieldNames) {
      const value = record[fieldName] ?? data?.[fieldName];
      if (value != null && value !== '') values.push(value);
    }
  }
  return uniqueLookupTokens(values);
}

export function extractCyberSweepAccountLookups(records: Record<string, unknown>[]): string[] {
  return extractLookupValues(records, ACCOUNT_LOOKUP_FIELDS);
}

/**
 * Client/Account reference on a CS_Audit row (Gobodo top-level and `data`). Same field priority as
 * relationship validation and account-name lookups, so it matches {@link useAccountIdToName} keys.
 * If this returns empty, the audit has no linked client (or only legacy field names we do not read).
 */
export function getCsAuditClientAccountRef(audit: Record<string, unknown>): string {
  const data = (audit.data as Record<string, unknown>) ?? {};
  return cleanString(
    audit.relatedClientId ??
      audit.clientId ??
      audit.ClientId__c ??
      audit.Account__c ??
      audit.custom__account ??
      data.relatedClientId ??
      data.clientId ??
      data.ClientId__c ??
      data.Account__c ??
      data.custom__account
  );
}

/** Root + `data` field read for CS_Audit list rows (same idea as board `boardF`). */
function csAuditRowField(
  r: Record<string, unknown>,
  d: Record<string, unknown>,
  ...keys: string[]
): unknown {
  for (const k of keys) {
    if (r[k] !== undefined && r[k] !== null) return r[k];
  }
  for (const k of keys) {
    if (d[k] !== undefined && d[k] !== null) return d[k];
  }
  return undefined;
}

function isPlaceholderCSAuditListName(s: string): boolean {
  const t = (s ?? '').trim();
  if (!t || t === 'Unknown' || t === 'Unknown Client') return true;
  return isGobodoLabelCyberSweep(t);
}

/**
 * Display name for a **CS_Audit** row in list UIs (My Work, team views) when the Client record
 * is not loaded per row. Matches Client Profile / audit header resolution: traversal → account map
 * → denormalized name fields on the audit → email/phone on the audit payload.
 */
export function resolveCSAuditListClientName(
  audit: Record<string, unknown>,
  customSuffix: string,
  accountIdToName?: Record<string, string> | null
): string {
  const fromTrav = readCSAuditClientNameFromClientTraversal(audit, customSuffix);
  if (fromTrav) return fromTrav;

  const ref = getCsAuditClientAccountRef(audit);
  if (ref && accountIdToName) {
    const fromMap = lookupAccountDisplayName(accountIdToName, ref);
    if (fromMap && !isPlaceholderCSAuditListName(fromMap)) return fromMap;
  }

  const d = (audit.data as Record<string, unknown>) ?? {};
  const rawName = String(
    csAuditRowField(
      audit,
      d,
      'clientName',
      'ClientName__c',
      'AccountName__c',
      'Account_Name__c',
      'Name__c',
      'Name'
    ) ?? ''
  ).trim();
  if (rawName && !isPlaceholderCSAuditListName(rawName)) return rawName;

  const f = String(
    csAuditRowField(audit, d, 'firstName', 'FirstName__c', 'First_Name__c', 'custom__first_name') ?? ''
  ).trim();
  const l = String(
    csAuditRowField(audit, d, 'lastName', 'LastName__c', 'Last_Name__c', 'custom__last_name') ?? ''
  ).trim();
  const parts = [f, l].filter(Boolean).join(' ').trim();
  if (parts && !isGobodoLabelCyberSweep(parts)) return parts;

  const label = String(csAuditRowField(audit, d, 'label') ?? '').trim();
  if (label && !isGobodoLabelCyberSweep(label) && !label.startsWith('Record ')) return label;

  const email = String(
    csAuditRowField(
      audit,
      d,
      'PersonEmail',
      'PersonEmail__c',
      `PersonEmail${customSuffix}`,
      'personalEmailAddress',
      'email',
      'Email__c',
      'PersonalEmailAddress__c',
      'custom__person_email'
    ) ?? ''
  ).trim();
  if (email) return email;

  const phone = String(
    csAuditRowField(
      audit,
      d,
      'PersonPhone',
      'PersonPhone__c',
      'PersonMobilePhone',
      'clientPersonalPhone',
      'Client_Personal_Phone__c',
      'Phone',
      'Phone__c',
      'phone',
      'Mobile_Phone__c',
      'MobilePhone'
    ) ?? ''
  ).trim();
  if (phone) return phone;

  return 'Unknown Client';
}

export function extractCyberSweepContactLookups(records: Record<string, unknown>[]): string[] {
  return extractLookupValues(records, CONTACT_LOOKUP_FIELDS);
}

export function extractCyberSweepUserLookups(records: Record<string, unknown>[]): string[] {
  // Include both SF-style IDs and numeric Gobodo IDs
  return extractLookupValues(records, USER_LOOKUP_FIELDS);
}

function lookupResolvedDisplayName(
  map: Record<string, string> | undefined,
  lookup: string
): string {
  if (!map || !lookup) return '';
  const stripped = stripKnownSuffix(lookup);
  return map[lookup] ?? map[stripped] ?? '';
}

async function fetchLookupDisplayName(objectApiName: string, lookup: string): Promise<string> {
  const raw = cleanString(lookup);
  if (!raw) return '';

  if (/^\d+$/.test(raw)) {
    try {
      const record = await getRecordById(objectApiName, raw);
      if (record) return getRecordDisplayName(objectApiName, record);
    } catch {
      // Try key fallback next.
    }
  } else {
    for (const candidate of Array.from(new Set([raw, stripKnownSuffix(raw), raw.endsWith(CUSTOM_SUFFIX) ? raw : `${raw}${CUSTOM_SUFFIX}`].filter(Boolean)))) {
      try {
        const record = await getGobodoRecordByKey(candidate);
        if (record) return getRecordDisplayName(objectApiName, record);
      } catch {
        // Try the next candidate.
      }
    }
  }

  try {
    const record = await getGobodoRecordById(raw);
    if (record) return getRecordDisplayName(objectApiName, record);
  } catch {
    // Final fallback below.
  }
  return '';
}

function firstNonEmpty(...values: unknown[]): string {
  for (const value of values) {
    const cleaned = cleanString(value);
    if (cleaned) return cleaned;
  }
  return '';
}

function looksLikeTechnicalReference(value: unknown): boolean {
  const raw = cleanString(value);
  if (!raw) return false;
  if (raw.toLowerCase().endsWith(CUSTOM_SUFFIX.toLowerCase())) return true;
  if (/^[a-zA-Z0-9]{15,18}$/.test(raw)) return true;
  // Filter out auto-generated names like CyberAudit-12345, CSA-12345, PAW-123, etc.
  if (/^(CyberAudit|CSAudit|CSA|PAWRecord|PAW|Client)-\d+$/i.test(raw)) return true;
  // Filter out pure numeric IDs
  if (/^\d+$/.test(raw)) return true;
  return false;
}

export function buildPAWWebsiteDisplayFields(
  record: Record<string, unknown> | null | undefined
): { websiteDisplayName: string; websiteUrl: string } {
  if (!record) return { websiteDisplayName: '', websiteUrl: '' };

  // Check nested data object for Gobodo traversal results
  const data = (record.data as Record<string, unknown>) ?? {};

  const humanName = firstNonEmpty(
    record.websiteDisplayName,
    data.websiteDisplayName,
    record.WebsiteDisplayName__c,
    data.WebsiteDisplayName__c,
    record.websiteName,
    data.websiteName,
    record.Website_Name__c,
    data.Website_Name__c,
    record.name,
    data.name,
    record.Name__c,
    data.Name__c,
    record.label,
    data.label
  );
  const websiteValue = firstNonEmpty(
    record.website,
    data.website,
    record.Website__c,
    data.Website__c,
    record.custom__website,
    data.custom__website,
    record.custom__website_name,
    data.custom__website_name
  );
  const websiteUrl = firstNonEmpty(
    record.websiteUrl,
    data.websiteUrl,
    record.Website_URL__c,
    data.Website_URL__c,
    record.URL__c,
    data.URL__c,
    record.custom__url,
    data.custom__url,
    record.Opt_Out_URL__c,
    data.Opt_Out_URL__c,
    record.opt_url__c,
    data.opt_url__c,
    record.custom__opt_out_url,
    data.custom__opt_out_url
  );

  const websiteDisplayName =
    (!looksLikeTechnicalReference(humanName) ? humanName : '') ||
    (!looksLikeTechnicalReference(websiteValue) ? websiteValue : '') ||
    websiteUrl;

  return {
    websiteDisplayName,
    websiteUrl,
  };
}

export function buildCSAuditTemplateDisplayFields(
  record: Record<string, unknown> | null | undefined
): { auditTypeDisplayName: string } {
  if (!record) return { auditTypeDisplayName: '' };

  const data = (record.data as Record<string, unknown>) ?? {};
  const top = record as Record<string, unknown>;
  const humanName = firstNonEmpty(
    top[CS_AUDIT_TEMPLATE_TRAV_DISPLAY_FIELD],
    data[CS_AUDIT_TEMPLATE_TRAV_DISPLAY_FIELD],
    top.auditTypeDisplayName,
    data.auditTypeDisplayName,
    top.AuditTypeDisplayName__c,
    data.AuditTypeDisplayName__c,
    top.auditTemplateName,
    data.auditTemplateName,
    top.AuditTemplateName__c,
    data.AuditTemplateName__c,
    top.name,
    data.name,
    top.Name__c,
    data.Name__c,
    top.label,
    data.label
  );
  const auditTypeValue = firstNonEmpty(
    top.auditType,
    data.auditType,
    top.Audit_Type__c,
    data.Audit_Type__c,
    top.AuditType__c,
    data.AuditType__c,
    top.custom__audit_type,
    data.custom__audit_type,
    top.Audit_Template__c,
    data.Audit_Template__c
  );

  return {
    auditTypeDisplayName:
      (!looksLikeTechnicalReference(humanName) ? humanName : '') ||
      (!looksLikeTechnicalReference(auditTypeValue) ? auditTypeValue : ''),
  };
}

async function resolveTraversalStartKey(
  objectApiName: string,
  record: Record<string, unknown>
): Promise<string> {
  const directKey = firstNonEmpty(record.key, record.objectKey, record.objectkey);
  if (directKey) return directKey;

  const rawId = firstNonEmpty(record.id, record.Id);
  if (!rawId) return '';

  if (/^[0-9]+$/.test(rawId)) {
    try {
      const resolved = await getRecordById(objectApiName, rawId);
      const resolvedKey = cleanString((resolved as Record<string, unknown> | null)?.key);
      if (resolvedKey) return resolvedKey;
    } catch {
      // Fall through: native Gobodo object keys for numeric ids are the id string (no custom suffix).
    }
    return rawId;
  }

  return rawId.toLowerCase().endsWith(CUSTOM_SUFFIX.toLowerCase()) ? rawId : `${rawId}${CUSTOM_SUFFIX}`;
}

async function fetchAuditWebsiteRecordViaLookup(
  pawRecord: Record<string, unknown>
): Promise<Record<string, unknown> | null> {
  const refCandidates = uniqueLookupTokens([
    pawRecord.AuditWebsiteRelField__c,
    pawRecord.auditWebsiteRelField,
    pawRecord.Audit_Website__c,
    pawRecord.custom__audit_website,
    pawRecord.websiteLookupId,
    pawRecord.website,
    pawRecord.Website__c,
    pawRecord.custom__website,
  ]).filter((value) => looksLikeTechnicalReference(value));

  for (const candidate of refCandidates) {
    try {
      const byKey = await getGobodoRecordByKey(candidate);
      if (byKey) return byKey as Record<string, unknown>;
    } catch {
      // Try next candidate.
    }
    try {
      const byId = await getGobodoRecordById(candidate);
      if (byId) return byId as Record<string, unknown>;
    } catch {
      // Try next candidate.
    }
  }

  return null;
}

async function fetchCSAuditTemplateRecordViaLookup(
  csAuditRecord: Record<string, unknown>
): Promise<Record<string, unknown> | null> {
  const refCandidates = uniqueLookupTokens([
    // Dynamic field with environment suffix (e.g., CSAuditTemplateRelField__stg5_c)
    csAuditRecord[CS_AUDIT_TEMPLATE_REL_FIELD],
    csAuditRecord.CSAuditTemplateRelField__c,
    csAuditRecord.csAuditTemplateRelField,
    csAuditRecord.custom__audit_type,
    csAuditRecord.Audit_Type__c,
    csAuditRecord.AuditType__c,
    csAuditRecord.Audit_Template__c,
    csAuditRecord.auditTemplateLookupId,
  ]).filter((value) => looksLikeTechnicalReference(value));

  for (const candidate of refCandidates) {
    try {
      const byKey = await getGobodoRecordByKey(candidate);
      if (byKey) return byKey as Record<string, unknown>;
    } catch {
      // Try next candidate.
    }
    try {
      const byId = await getGobodoRecordById(candidate);
      if (byId) return byId as Record<string, unknown>;
    } catch {
      // Try next candidate.
    }
  }

  return null;
}

async function fetchPAWWebsiteInfo(
  pawRecord: Record<string, unknown>,
  traversalApi: GobodoApi.GraphTraversalApi
): Promise<Record<string, unknown> | null> {
  const startKey = await resolveTraversalStartKey(PAWRECORD_OBJECT, pawRecord);
  if (startKey) {
    const input = new GobodoApi.GraphTraversalInput();
    input.setGraphId(GobodoConfig.graph());
    input.setStartObjectKey(startKey);
    input.setRelationshipTypes([getPAWAuditWebsiteRelKey()]);
    input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.OUTGOING);
    input.setEndObjectTypes([normalizeToGobodoFormat(AUDIT_WEBSITE_OBJECT)]);
    input.setMaxPathLength(1);

    const result = await retryWithBackoff(
      () => traversalApi.traverseObjects(input),
      { maxRetries: 2, initialDelayMs: 1000 }
    );
    if (Array.isArray(result) && result.length > 0) {
      const gobodoObj = result[0] as Record<string, unknown>;
      const converted = convertFromGobodoRecord(gobodoObj, AUDIT_WEBSITE_OBJECT) as Record<string, unknown>;
      return {
        ...converted,
        key: gobodoObj.key ?? converted.key,
        label: gobodoObj.label ?? converted.label,
      };
    }
  }

  return fetchAuditWebsiteRecordViaLookup(pawRecord);
}

async function fetchCSAuditTemplateInfo(csAuditRecord: Record<string, unknown>): Promise<Record<string, unknown> | null> {
  if (USE_CSAUDIT_TEMPLATE_TRAVERSE_API) {
    const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
    const startKey = await resolveTraversalStartKey(CSAUDIT_OBJECT, csAuditRecord);
    if (startKey) {
      const graphId = GobodoConfig.graph();
      const relKey = getCSAuditTemplateRelKey();
      const endType = normalizeToGobodoFormat(AUDIT_TEMPLATE_OBJECT);
      const input = new GobodoApi.GraphTraversalInput();
      input.setGraphId(graphId);
      input.setStartObjectKey(startKey);
      input.setRelationshipTypes([relKey]);
      input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.OUTGOING);
      input.setEndObjectTypes([endType]);
      input.setMaxPathLength(1);

      const traversalCacheOpts = makeCyberSweepCsAuditToTemplateTraversalCacheOpts(
        graphId,
        startKey,
        relKey,
        endType
      );
      const result = await retryWithBackoff(
        () => traversalApi.traverseObjects(input, traversalCacheOpts),
        { maxRetries: 2, initialDelayMs: 1000 }
      );
      if (Array.isArray(result) && result.length > 0) {
        const gobodoObj = result[0] as Record<string, unknown>;
        const converted = convertFromGobodoRecord(gobodoObj, AUDIT_TEMPLATE_OBJECT) as Record<string, unknown>;
        return {
          ...converted,
          key: gobodoObj.key ?? converted.key,
          label: gobodoObj.label ?? converted.label,
        };
      }
    }
  }

  return fetchCSAuditTemplateRecordViaLookup(csAuditRecord);
}

export async function enrichCSAuditsWithTemplateInfo(
  csAudits: Record<string, unknown>[]
): Promise<Record<string, unknown>[]> {
  if (!Array.isArray(csAudits) || csAudits.length === 0) return [];

  const templateCache = new Map<string, Record<string, unknown> | null>();
  const enriched = [...csAudits];
  const chunkSize = 20;

  const getTemplateRefTokens = (record: Record<string, unknown>) =>
    uniqueLookupTokens([
      // Dynamic field with environment suffix (e.g., CSAuditTemplateRelField__stg5_c)
      record[CS_AUDIT_TEMPLATE_REL_FIELD],
      record.CSAuditTemplateRelField__c,
      record.csAuditTemplateRelField,
      record.custom__audit_type,
      record.Audit_Type__c,
      record.AuditType__c,
      record.Audit_Template__c,
      record.auditTemplateLookupId,
    ]).filter((value) => looksLikeTechnicalReference(value));

  for (let i = 0; i < enriched.length; i += chunkSize) {
    const chunk = enriched.slice(i, i + chunkSize);
    const chunkResults = await Promise.all(
      chunk.map(async (csAuditRecord) => {
        const currentFields = buildCSAuditTemplateDisplayFields(csAuditRecord);
        const templateRefTokens = getTemplateRefTokens(csAuditRecord);
        const cachedToken = templateRefTokens.find((token) => templateCache.has(token));
        const cachedTemplate = cachedToken ? templateCache.get(cachedToken) ?? null : null;

        if (cachedToken && !cachedTemplate) {
          return {
            ...csAuditRecord,
            ...currentFields,
            auditTemplateLookupId: firstNonEmpty(
              csAuditRecord.auditTemplateLookupId,
              ...templateRefTokens
            ),
          };
        }

        if (cachedTemplate) {
          const templateFields = buildCSAuditTemplateDisplayFields(cachedTemplate);
          return {
            ...csAuditRecord,
            ...templateFields,
            auditTemplateLookupId: firstNonEmpty(
              csAuditRecord.auditTemplateLookupId,
              ...templateRefTokens,
              cachedTemplate.key,
              cachedTemplate.id
            ),
          };
        }

        if (currentFields.auditTypeDisplayName && !looksLikeTechnicalReference(currentFields.auditTypeDisplayName)) {
          return {
            ...csAuditRecord,
            ...currentFields,
          };
        }

        try {
          const templateRecord = await fetchCSAuditTemplateInfo(csAuditRecord);
          for (const token of templateRefTokens) {
            templateCache.set(token, templateRecord);
          }
          const templateFields = buildCSAuditTemplateDisplayFields(templateRecord);
          return {
            ...csAuditRecord,
            ...templateFields,
            auditTemplateLookupId: firstNonEmpty(
              csAuditRecord.auditTemplateLookupId,
              ...templateRefTokens,
              templateRecord?.key,
              templateRecord?.id
            ),
          };
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[CyberSweep Traversal] Failed to resolve template info for CSAudit', csAuditRecord.id, error);
          }
          return {
            ...csAuditRecord,
            ...currentFields,
          };
        }
      })
    );
    enriched.splice(i, chunkResults.length, ...chunkResults);
  }

  return enriched;
}

async function enrichPAWRecordsWithWebsiteInfo(
  pawRecords: Record<string, unknown>[]
): Promise<Record<string, unknown>[]> {
  if (!Array.isArray(pawRecords) || pawRecords.length === 0) return [];

  const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
  const enriched = [...pawRecords];
  const chunkSize = 15;

  for (let i = 0; i < enriched.length; i += chunkSize) {
    const chunk = enriched.slice(i, i + chunkSize);
    const chunkResults = await Promise.all(
      chunk.map(async (pawRecord) => {
        const currentFields = buildPAWWebsiteDisplayFields(pawRecord);
        if (currentFields.websiteDisplayName && !looksLikeTechnicalReference(currentFields.websiteDisplayName)) {
          return {
            ...pawRecord,
            ...currentFields,
          };
        }

        try {
          const websiteRecord = await fetchPAWWebsiteInfo(pawRecord, traversalApi);
          const websiteFields = buildPAWWebsiteDisplayFields(websiteRecord);
          return {
            ...pawRecord,
            ...websiteFields,
            websiteLookupId: firstNonEmpty(
              pawRecord.websiteLookupId,
              pawRecord.AuditWebsiteRelField__c,
              pawRecord.auditWebsiteRelField,
              pawRecord.Audit_Website__c,
              pawRecord.custom__audit_website,
              websiteRecord?.key,
              websiteRecord?.id
            ),
            auditWebsiteKey: firstNonEmpty(
              pawRecord.auditWebsiteKey,
              websiteRecord?.key
            ),
          };
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[CyberSweep Traversal] Failed to resolve website info for PAW record', pawRecord.id, error);
          }
          return {
            ...pawRecord,
            ...currentFields,
          };
        }
      })
    );
    enriched.splice(i, chunkResults.length, ...chunkResults);
  }

  return enriched;
}

export function buildCyberSweepClientLookupDisplayFields(
  record: Record<string, unknown> | null | undefined,
  maps: {
    accountNameMap?: Record<string, string>;
    contactNameMap?: Record<string, string>;
    userNameMap?: Record<string, string>;
  }
): Record<string, string> {
  if (!record) return {};

  const accountOwnerRef = firstNonEmpty(record[ACCOUNT_OWNER_REL_FIELD], record.AccountOwnerRelField__c, record.OwnerId__c);
  const referralCompanyRef = firstNonEmpty(record[REFERRAL_COMPANY_REL_FIELD], record.ReferralCompanyRelField__c, record.ReferralCompany__c, record.Referral_Company__c);
  const referralPersonRef = firstNonEmpty(record[REFERRAL_PERSON_ACCOUNT_REL_FIELD], record.ReferralPersonAccountRelField__c, record.PersonReferral__c, record.Person_Referral__c);
  const accountManagerCompanyRef = firstNonEmpty(record[ACCOUNT_MANAGER_COMPANY_REL_FIELD], record.AccountManagerCompanyRelField__c, record.Account_Manager_Company__c);
  const referralPartnerRef = firstNonEmpty(record[REFERRAL_PARTNER_CONTACT_REL_FIELD], record.ReferralPartnerContactRelField__c, record.ReferralContact__c, record.Referral_Contact__c);
  const accountManagerRef = firstNonEmpty(record[ACCOUNT_MANAGER_REL_FIELD], record.AccountManagerRelField__c, record.Contact__c, record.AccountManager__c);
  const dayToDayRef = firstNonEmpty(record[DAY_TO_DAY_CONTACT_REL_FIELD], record.DayToDayContactRelField__c, record.Daily_Contact__c, record.Day_to_Day_Contact__c);
  const createdByRef = firstNonEmpty(record[CREATED_BY_REL_FIELD], record.CreatedByRelField__c, record.CreatedById__c);
  const lastModifiedByRef = firstNonEmpty(record[LAST_MODIFIED_BY_REL_FIELD], record.LastModifiedByRelField__c, record.LastModifiedById__c);

  return {
    accountOwner: firstNonEmpty(
      record.AccountOwnerName__c,
      record.AccountOwner__c,
      lookupResolvedDisplayName(maps.userNameMap, accountOwnerRef),
      accountOwnerRef
    ),
    referralCompany: firstNonEmpty(
      record.ReferralCompanyName__c,
      lookupResolvedDisplayName(maps.accountNameMap, referralCompanyRef),
      record.ReferralCompany__c,
      record.Referral_Company__c,
      referralCompanyRef
    ),
    referralPersonContact: firstNonEmpty(
      record.ReferralPersonAccountName__c,
      lookupResolvedDisplayName(maps.accountNameMap, referralPersonRef),
      record.PersonReferral__c,
      record.Person_Referral__c,
      referralPersonRef
    ),
    accountManagerCompany: firstNonEmpty(
      record.AccountManagerCompanyName__c,
      lookupResolvedDisplayName(maps.accountNameMap, accountManagerCompanyRef),
      record.Account_Manager_Company__c,
      accountManagerCompanyRef
    ),
    referralPartnerContact: firstNonEmpty(
      record.ReferralPartnerContactName__c,
      lookupResolvedDisplayName(maps.contactNameMap, referralPartnerRef),
      record.ReferralContact__c,
      record.Referral_Contact__c,
      referralPartnerRef
    ),
    accountManager: firstNonEmpty(
      record.AccountManagerName__c,
      lookupResolvedDisplayName(maps.contactNameMap, accountManagerRef),
      record.Contact__c,
      record.AccountManager__c,
      accountManagerRef
    ),
    dayToDayContact: firstNonEmpty(
      record.DayToDayContactName__c,
      lookupResolvedDisplayName(maps.contactNameMap, dayToDayRef),
      record.Daily_Contact__c,
      record.Day_to_Day_Contact__c,
      dayToDayRef
    ),
    recordCreatedBy: firstNonEmpty(
      record.CreatedByName__c,
      lookupResolvedDisplayName(maps.userNameMap, createdByRef),
      createdByRef
    ),
    lastModifiedBy: firstNonEmpty(
      record.LastModifiedByName__c,
      lookupResolvedDisplayName(maps.userNameMap, lastModifiedByRef),
      lastModifiedByRef
    ),
  };
}

/** Board card row shape (clients tab kanban); aligns with list view row fields used by filters. */
export type CyberSweepBoardCardRow = {
  id: string;
  clientId: string;
  sfId: string;
  firstName: string;
  lastName: string;
  clientName: string;
  auditId: string;
  stage: string;
  phaseStatus: string;
  auditType: string;
  roadblock: string;
  auditor: string;
  dueDate: string;
  dueDateRaw: string;
  enrolledDate: string;
  quarter: string;
  year: string;
  phase1StartDate: string;
  phase2StartDate: string;
  auditCount: number;
  /** Internal: Gobodo Client record for merging audit hydration; omitted from UI-facing rows. */
  rawClient?: Record<string, unknown>;
};

function stripBoardRowRawClient(row: CyberSweepBoardCardRow): CyberSweepBoardCardRow {
  if (row.rawClient === undefined) return row;
  const { rawClient: _r, ...rest } = row;
  return rest;
}

function formatCyberSweepBoardDate(val: unknown): string {
  return formatCyberSweepDisplayDate(val);
}

function stripCyberSweepBoardSuffix(s: string): string {
  return (s ?? '').replace(/__stg5_c$|__stg3_c$|__stg_c$|__dev_c$|__c$/i, '');
}

function isGobodoLabelCyberSweep(s: string): boolean {
  return /^(CyberAudit|CSAudit|PAWRecord|Client)-\d+$/i.test(s) || /^a\d[a-z]\d/i.test(s);
}

function boardGetField(r: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) {
    if (r[k] !== undefined && r[k] !== null) return r[k];
  }
  return undefined;
}

function boardF(
  r: Record<string, unknown>,
  data: Record<string, unknown>,
  ...keys: string[]
): unknown {
  return boardGetField(r, ...keys) ?? boardGetField(data, ...keys);
}

/**
 * Sort audits by due date descending (latest first). Client traversal board fields on Gobodo use the same
 * ordering: one hop on `cs_audit_client_rel` from Client with `sortField` defaulting to `Due_Date__c` and
 * `sortOrder` `DESC` so the projected CSAudit matches `clientAudits[0]` after this sort.
 */
function sortCSAuditsByDueDateDesc(audits: Record<string, unknown>[]): Record<string, unknown>[] {
  return [...audits].sort((a, b) => {
    const dataA = (a.data as Record<string, unknown>) ?? a;
    const dataB = (b.data as Record<string, unknown>) ?? b;
    const rawA = boardF(a, dataA, 'dueDate', 'Due_Date__c', 'DueDate__c', 'custom__due_date');
    const rawB = boardF(b, dataB, 'dueDate', 'Due_Date__c', 'DueDate__c', 'custom__due_date');
    const timeA = parseCyberSweepDateToMs(rawA) ?? 0;
    const timeB = parseCyberSweepDateToMs(rawB) ?? 0;
    return timeB - timeA;
  });
}

/** Graph index names on CSAudit for client FK (Account / custom account). Comma-separated env override. */
const CSAUDIT_CLIENT_REF_INDEX_NAMES = (
  process.env.REACT_APP_CSAUDIT_CLIENT_REF_INDEX_NAMES ?? 'Account__c,custom__account'
)
  .split(',')
  .map((s) => s.trim())
  .filter(Boolean);

/** Max client ref tokens per `IndexFilter` `EQUALS` `values` chunk (OR within the chunk). */
const CSAUDIT_FIELD_FILTER_CHUNK_SIZE = Math.max(
  10,
  Math.min(200, Number(process.env.REACT_APP_CSAUDIT_CLIENT_KEYS_CHUNK_SIZE ?? 50) || 50)
);

function clientKeysSignatureForQuery(clientKeys: string[]): string {
  const sorted = dedupeStringsPreserveOrder(clientKeys).sort();
  if (sorted.length === 0) return '';
  let h = 0;
  const s = sorted.join('|');
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return `${sorted.length}:${h}`;
}

/**
 * Expand one client id/key into candidate strings that may appear in CSAudit `Account__c` / `custom__account` indexes.
 */
function expandClientKeysForCsAuditFieldFilter(clientKeys: string[]): string[] {
  const out: string[] = [];
  for (const raw of clientKeys) {
    const id = cleanString(raw);
    if (!id) continue;
    out.push(id);
    const stripped = stripKnownSuffix(id);
    if (stripped && stripped !== id) out.push(stripped);
    if (stripped && !id.toLowerCase().endsWith(CUSTOM_SUFFIX.toLowerCase())) {
      out.push(`${stripped}${CUSTOM_SUFFIX}`);
    }
  }
  return dedupeStringsPreserveOrder(out);
}

function buildCSAuditZeroHopAccountRefTraversalInput(
  clientKeysChunk: string[],
  indexName: string
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const csType = normalizeToGobodoFormat(CSAUDIT_OBJECT);
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  input.setStartObjectTypes([csType]);
  input.setEndObjectTypes([csType]);
  input.setMaxPathLength(0);
  const f = new GobodoApi.IndexFilter();
  f.setIndexName(indexName);
  f.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
  f.setValues(clientKeysChunk);
  input.setStartObjectFieldFilters([f]);
  return input;
}

function csAuditFieldIndexUnionCacheOpts(
  indexNames: string[],
  chunk: string[],
  chunkIndex: number
): GobodoCacheOpts {
  const gid = String(GobodoConfig.graph());
  return makeCacheOpts(
    [
      'cybersweep',
      'cs_audit',
      'field-index-union',
      gid,
      indexNames.join('+'),
      String(chunkIndex),
      String(chunk.length),
      String(chunk[0] ?? ''),
    ],
    CACHE_TTL.SHORT,
    { group: [`gobodo-cs-audit-field-union-${gid}`] }
  );
}

/**
 * Zero-hop CSAudit traversals: `IndexFilter` `EQUALS` with chunked client ref values, unioned across configured index
 * names (typically `Account__c` OR `custom__account`). Matches board-column multi-value `EQUALS` semantics.
 */
async function fetchCSAuditsViaAccountRefFieldIndexUnion(clientKeys: string[]): Promise<Record<string, unknown>[]> {
  const expanded = expandClientKeysForCsAuditFieldFilter(clientKeys);
  if (expanded.length === 0 || CSAUDIT_CLIENT_REF_INDEX_NAMES.length === 0) return [];

  const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
  const seen = new Set<string>();
  const merged: Record<string, unknown>[] = [];

  const uniqueIndexNames = dedupeStringsPreserveOrder(CSAUDIT_CLIENT_REF_INDEX_NAMES);

  for (let i = 0; i < expanded.length; i += CSAUDIT_FIELD_FILTER_CHUNK_SIZE) {
    const chunk = expanded.slice(i, i + CSAUDIT_FIELD_FILTER_CHUNK_SIZE);
    let head: InstanceType<typeof GobodoApi.GraphTraversalInput> | null = null;
    const branches: InstanceType<typeof GobodoApi.GraphTraversalInput>[] = [];
    for (const indexName of uniqueIndexNames) {
      const branch = buildCSAuditZeroHopAccountRefTraversalInput(chunk, indexName);
      if (!head) head = branch;
      else branches.push(branch);
    }
    const input = head
      ? branches.length > 0
        ? chainUnionTraversals(head, branches)
        : head
      : null;
    if (!input) continue;

    const cacheOpts = csAuditFieldIndexUnionCacheOpts(uniqueIndexNames, chunk, i);
    const result = await retryWithBackoff(
      () => traversalApi.traverseObjects(input, cacheOpts),
      { maxRetries: 2, initialDelayMs: 1000 }
    );
    const objects = requireCyberSweepTraverseObjectsArray(result);
    for (const gobodoObj of objects) {
      try {
        const rec = convertFromGobodoRecord(gobodoObj as Record<string, unknown>, CSAUDIT_OBJECT) as Record<
          string,
          unknown
        >;
        const id = String(rec.id ?? rec.key ?? '');
        if (!id || seen.has(id)) continue;
        seen.add(id);
        merged.push(rec);
      } catch {
        /* skip */
      }
    }
  }

  return merged;
}

function mergeAuditsById(
  a: Record<string, unknown>[],
  b: Record<string, unknown>[]
): Record<string, unknown>[] {
  const seen = new Set<string>();
  const out: Record<string, unknown>[] = [];
  for (const r of [...a, ...b]) {
    const id = String(r.id ?? r.key ?? '');
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(r);
  }
  return out;
}

/**
 * For each input client id, attach audits whose Account / custom__account refs match (suffix-tolerant).
 */
function groupAuditsByClientIds(
  audits: Record<string, unknown>[],
  clientIds: string[]
): Record<string, Record<string, unknown>[]> {
  const out: Record<string, Record<string, unknown>[]> = {};
  const unique = dedupeStringsPreserveOrder(clientIds);
  for (const cid of unique) {
    out[cid] = [];
  }

  for (const audit of audits) {
    const data = (audit.data as Record<string, unknown>) ?? audit;
    const accountRef = String(
      audit.Account__c ??
        audit.custom__account ??
        audit.ClientId__c ??
        audit.relatedClientId ??
        data.Account__c ??
        data.custom__account ??
        data.ClientId__c ??
        data.relatedClientId ??
        ''
    ).trim();
    if (!accountRef) continue;
    const refLower = accountRef.toLowerCase();
    const strippedRef = stripKnownSuffix(accountRef).toLowerCase();

    for (const cid of unique) {
      const cLower = cid.toLowerCase();
      const cStrip = stripKnownSuffix(cid).toLowerCase();
      if (
        refLower === cLower ||
        refLower === cStrip ||
        strippedRef === cLower ||
        strippedRef === cStrip
      ) {
        out[cid].push(audit);
      }
    }
  }

  for (const cid of unique) {
    out[cid] = sortCSAuditsByDueDateDesc(out[cid]);
  }
  return out;
}

/**
 * Graph traversals often return CSAudit rows without `Account__c` / `custom__account` populated, so
 * {@link groupAuditsByClientIds} would drop them. Re-attach audits that we know belong to `cid` from
 * {@link fetchClientCSAuditsViaTraversal} (same ids as in the merged hydrated list).
 */
function augmentGroupedAuditsWithRelTraversal(
  grouped: Record<string, Record<string, unknown>[]>,
  relByClient: Record<string, Record<string, unknown>[]>,
  clientIds: string[],
  auditById: Map<string, Record<string, unknown>>
): Record<string, Record<string, unknown>[]> {
  const out: Record<string, Record<string, unknown>[]> = { ...grouped };
  for (const cid of clientIds) {
    const rel = relByClient[cid] ?? [];
    const existing = out[cid] ?? [];
    const seen = new Set(existing.map((a) => String(a.id ?? a.key ?? '')));
    for (const r of rel) {
      const rid = String(r.id ?? r.key ?? '');
      if (!rid || seen.has(rid)) continue;
      const full = auditById.get(rid) ?? r;
      if (!out[cid]) out[cid] = [];
      out[cid].push(full);
      seen.add(rid);
    }
    out[cid] = sortCSAuditsByDueDateDesc(out[cid] ?? []);
  }
  return out;
}

async function fetchRelTraversalAuditsByClient(
  unique: string[],
  relConcurrency: number
): Promise<Record<string, Record<string, unknown>[]>> {
  const out: Record<string, Record<string, unknown>[]> = {};
  for (const cid of unique) out[cid] = [];
  for (let i = 0; i < unique.length; i += relConcurrency) {
    const slice = unique.slice(i, i + relConcurrency);
    await Promise.all(
      slice.map(async (clientId) => {
        const rows = await fetchClientCSAuditsViaTraversal(clientId);
        out[clientId] = rows;
      })
    );
  }
  return out;
}

/**
 * When traversal returns CSAudit rows without Account/custom__account, list joins skip them.
 * Stamp `relatedClientId` from the client we traversed from so {@link CyberSweepApp} `auditsByClientId` can match.
 */
function tagSparseAuditsWithSourceClientIdFromRel(
  relByClient: Record<string, Record<string, unknown>[]>,
  merged: Record<string, unknown>[]
): Record<string, unknown>[] {
  const idToClient = new Map<string, string>();
  for (const [cid, rows] of Object.entries(relByClient)) {
    for (const r of rows) {
      const rid = String(r.id ?? r.key ?? '').trim();
      if (rid) idToClient.set(rid, cid);
    }
  }
  return merged.map((a) => {
    const rid = String(a.id ?? a.key ?? '').trim();
    if (!rid) return a;
    const cid = idToClient.get(rid);
    if (!cid) return a;
    const data = (a.data as Record<string, unknown>) ?? {};
    const hasRef = String(
      data.Account__c ??
        data.custom__account ??
        data.relatedClientId ??
        a.Account__c ??
        a.custom__account ??
        a.relatedClientId ??
        ''
    ).trim();
    if (hasRef) return a;
    return { ...a, data: { ...data, relatedClientId: cid }, relatedClientId: cid };
  });
}

async function fetchCSAuditsCore(
  clientKeys: string[],
  options?: { relConcurrency?: number }
): Promise<{
  relByClient: Record<string, Record<string, unknown>[]>;
  mergedBeforeHydrate: Record<string, unknown>[];
}> {
  const unique = dedupeStringsPreserveOrder(clientKeys);
  if (unique.length === 0) {
    return { relByClient: {}, mergedBeforeHydrate: [] };
  }

  const relConcurrency = options?.relConcurrency ?? CYBERSWEEP_BOARD_AUDIT_FETCH_CONCURRENCY;

  const [relByClient, fromField] = await Promise.all([
    fetchRelTraversalAuditsByClient(unique, relConcurrency),
    fetchCSAuditsViaAccountRefFieldIndexUnion(unique),
  ]);

  let acc: Record<string, unknown>[] = [];
  for (const rows of Object.values(relByClient)) {
    acc = mergeAuditsById(acc, rows);
  }
  const merged = mergeAuditsById(acc, fromField);
  return { relByClient, mergedBeforeHydrate: merged };
}

/**
 * Loads CSAudits for a set of clients by **merging**:
 * 1. Per-client `cs_audit_client_rel` graph traversals (bounded concurrency).
 * 2. Zero-hop CSAudit traversals with `IndexFilter` `EQUALS` on client ref field indexes (chunked OR across indexes).
 *
 * Avoids paged full-table `CSAudit__c` scans used previously for list joins.
 */
export async function fetchCSAuditsForClientKeysMerged(
  clientKeys: string[],
  options?: { relConcurrency?: number }
): Promise<Record<string, unknown>[]> {
  const { relByClient, mergedBeforeHydrate } = await fetchCSAuditsCore(clientKeys, options);
  const tagged = tagSparseAuditsWithSourceClientIdFromRel(relByClient, mergedBeforeHydrate);
  return hydrateCSAuditRecordsForListUi(tagged);
}

/**
 * Latest CSAudit rows per client key (Gobodo id or key string), sorted by due date descending.
 * Uses merged traversal + field-index union (no full-table CSAudit scan).
 * Augments {@link groupAuditsByClientIds} with per-client rel traversal so sparse rows still map to the client.
 */
export async function fetchLatestCSAuditsForClientKeys(
  clientIds: string[],
  options?: { concurrency?: number }
): Promise<Record<string, Record<string, unknown>[]>> {
  const unique = Array.from(new Set(clientIds.map((id) => cleanString(id)).filter(Boolean)));
  if (unique.length === 0) return {};

  const { relByClient, mergedBeforeHydrate } = await fetchCSAuditsCore(unique, {
    relConcurrency: options?.concurrency ?? CYBERSWEEP_BOARD_AUDIT_FETCH_CONCURRENCY,
  });
  const tagged = tagSparseAuditsWithSourceClientIdFromRel(relByClient, mergedBeforeHydrate);
  const hydrated = await hydrateCSAuditRecordsForListUi(tagged);
  const enriched = await enrichCSAuditsWithTemplateInfo(hydrated);

  const auditById = new Map<string, Record<string, unknown>>();
  for (const a of enriched) {
    const id = String(a.id ?? a.key ?? '');
    if (id) auditById.set(id, a);
  }

  let grouped = groupAuditsByClientIds(enriched, unique);
  grouped = augmentGroupedAuditsWithRelTraversal(grouped, relByClient, unique, auditById);
  return grouped;
}

/**
 * Build account / Client id → display name map from CyberSweep client traversal rows already in memory.
 * Seeds {@link useAccountIdToName} so we avoid redundant `getObjectByKey` calls for those accounts.
 */
export function buildCyberSweepAccountDisplayNameMapFromClientRecords(
  clientRecords: Record<string, unknown>[]
): Record<string, string> {
  const map: Record<string, string> = {};
  if (!Array.isArray(clientRecords) || clientRecords.length === 0) return map;

  for (const client of clientRecords) {
    const data = (client.data as Record<string, unknown>) ?? client;
    const clientId = String(client.id ?? client.key ?? '');
    const clientKey = String(client.key ?? '');
    const rawName = String(
      boardF(client, data, 'clientName', 'ClientName__c', 'ClientName', 'AccountName__c', 'Name__c', 'Name', 'name') ?? ''
    );
    const recFirstName = String(
      boardF(client, data, 'firstName', 'FirstName__c', 'First_Name__c', 'custom__first_name') ?? ''
    );
    const recLastName = String(
      boardF(client, data, 'lastName', 'LastName__c', 'Last_Name__c', 'custom__last_name') ?? ''
    );
    const fullNameFromParts = [recFirstName, recLastName].filter(Boolean).join(' ').trim();
    const recordLabel = String(boardF(client, data, 'label') ?? '');
    const clientName =
      (rawName && !isGobodoLabelCyberSweep(rawName) ? rawName : '') ||
      (fullNameFromParts && !isGobodoLabelCyberSweep(fullNameFromParts) ? fullNameFromParts : '') ||
      (recordLabel &&
      !isGobodoLabelCyberSweep(recordLabel) &&
      !recordLabel.startsWith('Record ')
        ? recordLabel
        : '') ||
      '';
    const trimmed = clientName.trim();
    if (!trimmed) continue;

    const keys = Array.from(
      new Set(
        [clientId, clientKey, stripCyberSweepBoardSuffix(clientKey), stripKnownSuffix(clientKey)]
          .map((k) => cleanString(k))
          .filter(Boolean)
      )
    );
    for (const k of keys) {
      map[k] = trimmed;
      const stripped = stripKnownSuffix(k);
      if (stripped) map[stripped] = trimmed;
    }
  }
  return map;
}

/**
 * Board card row: prefers Client traversal skin fields (same due-date-desc CSAudit as {@link sortCSAuditsByDueDateDesc})
 * when present on `client` / `client.data`; otherwise uses `clientAudits[0]`.
 * `auditCount` stays `clientAudits.length` so hydrated merged audits still drive the count (traversal columns do not replace it).
 */
export function mapClientAndAuditsToBoardCardRow(
  client: Record<string, unknown>,
  clientAudits: Record<string, unknown>[],
  opts: {
    stage: string;
    customSuffix: string;
    accountIdToName?: Record<string, string>;
    auditorIdToName?: Record<string, string>;
  }
): CyberSweepBoardCardRow {
  const data = (client.data as Record<string, unknown>) ?? client;
  const clientId = String(client.id ?? client.key ?? '');
  const clientKey = String(client.key ?? '');
  const sfId = stripCyberSweepBoardSuffix(clientKey);

  const rawName = String(
    boardF(client, data, 'clientName', 'ClientName__c', 'ClientName', 'AccountName__c', 'Name__c', 'Name', 'name') ?? ''
  );
  const recFirstName = String(
    boardF(client, data, 'firstName', 'FirstName__c', 'First_Name__c', 'custom__first_name') ?? ''
  );
  const recLastName = String(
    boardF(client, data, 'lastName', 'LastName__c', 'Last_Name__c', 'custom__last_name') ?? ''
  );
  const fullNameFromParts = [recFirstName, recLastName].filter(Boolean).join(' ').trim();
  const recordLabel = String(boardF(client, data, 'label') ?? '');
  const lookupName = lookupAccountDisplayName(opts.accountIdToName, clientId);

  const clientName =
    (rawName && !isGobodoLabelCyberSweep(rawName) ? rawName : '') ||
    (fullNameFromParts && !isGobodoLabelCyberSweep(fullNameFromParts) ? fullNameFromParts : '') ||
    (recordLabel &&
    !isGobodoLabelCyberSweep(recordLabel) &&
    !recordLabel.startsWith('Record ')
      ? recordLabel
      : '') ||
    (lookupName && !isGobodoLabelCyberSweep(lookupName) ? lookupName : '') ||
    'Unknown Client';
  const parts = clientName.split(' ');
  const firstName = parts[0] || '';
  const lastName = parts.slice(1).join(' ') || '';

  const latestAudit = clientAudits[0] as Record<string, unknown> | undefined;
  const latestAuditData = latestAudit ? ((latestAudit.data as Record<string, unknown>) ?? latestAudit) : {};
  const suf = opts.customSuffix;

  const phaseStatusFromClient = readCyberSweepClientTraversalString(client, data, 'phaseStatus', suf);
  const phaseStatus =
    phaseStatusFromClient ||
    (latestAudit
      ? String(
          boardF(
            latestAudit,
            latestAuditData,
            'phaseStatus',
            'Phase_Status__c',
            'PhaseStatus__c',
            'custom__phase_status',
            `Phase_Status${CUSTOM_SUFFIX}`,
            'Custom__phase_status__c'
          ) ?? ''
        )
      : '');

  const auditTypeFromClient = readCyberSweepClientTraversalString(client, data, 'auditType', suf);
  const auditType = auditTypeFromClient
    ? auditTypeFromClient
    : latestAudit
      ? (() => {
          const candidates = [
            boardF(latestAudit, latestAuditData, 'auditTypeDisplayName', 'AuditTypeDisplayName__c'),
            boardF(latestAudit, latestAuditData, 'Audit_Type_Name__c', 'auditTypeName'),
            boardF(latestAudit, latestAuditData, 'auditTemplateName', 'AuditTemplateName__c'),
            boardF(latestAudit, latestAuditData, 'auditType', 'Audit_Type__c', 'AuditType__c', 'custom__audit_type'),
          ];
          const isNotSfId = (v: unknown) => {
            const s = String(v || '').trim();
            if (!s) return false;
            if (/^[a-zA-Z0-9]{15,18}$/.test(s) && /^[a-z][0-9][A-Za-z]/.test(s)) return false;
            return true;
          };
          return String(candidates.find(isNotSfId) ?? '').trim();
        })()
      : '';

  const roadblockFromClient = readCyberSweepClientTraversalString(client, data, 'roadblock', suf);
  const roadblock =
    roadblockFromClient ||
    (latestAudit
      ? String(
          boardF(
            latestAudit,
            latestAuditData,
            'roadBlockNote',
            'Road_Block_Note__c',
            'RoadBlockNote__c',
            'custom__road_block_note'
          ) ?? ''
        )
      : '');
  const rawDueDateFromClient = readCyberSweepClientTraversalString(client, data, 'dueDate', suf);
  const rawDueDate =
    rawDueDateFromClient ||
    (latestAudit
      ? (boardF(latestAudit, latestAuditData, 'dueDate', 'Due_Date__c', 'DueDate__c', 'custom__due_date') ?? '')
      : '');
  const auditIdFromClient = readCyberSweepClientTraversalString(client, data, 'auditKey', suf);
  const auditId =
    auditIdFromClient || (latestAudit ? String(latestAudit.id ?? latestAudit.key ?? '') : '');

  const auditorFromClientRaw = readCyberSweepClientTraversalString(client, data, 'auditor', suf);
  const auditor = (() => {
    const rawAuditor = String(
      auditorFromClientRaw ||
        (latestAudit
          ? latestAuditData.Auditor__c ??
            latestAuditData.auditor ??
            latestAuditData.custom__auditor ??
            latestAudit.Auditor__c ??
            latestAudit.auditor ??
            latestAudit.custom__auditor ??
            ''
          : '')
    ).trim();
    if (!rawAuditor) return '';
    return lookupAuditorDisplayName(opts.auditorIdToName, rawAuditor) || rawAuditor;
  })();

  const rawPhase1FromClient = readCyberSweepClientTraversalString(client, data, 'phase1Start', suf);
  const rawPhase1Start =
    rawPhase1FromClient ||
    (latestAudit
      ? (boardF(
          latestAudit,
          latestAuditData,
          'phase1StartDateTime',
          'Phase_1_Start_Date_Time__c',
          'Phase1StartDateTime__c',
          'Phase_1_Start_Date__c',
          'custom__phase_1_start_date_time'
        ) ?? '')
      : '');
  const rawPhase2FromClient = readCyberSweepClientTraversalString(client, data, 'phase2Start', suf);
  const rawPhase2Start =
    rawPhase2FromClient ||
    (latestAudit
      ? (boardF(
          latestAudit,
          latestAuditData,
          'phase2StartDateTime',
          'Phase_2_Start_Date_Time__c',
          'Phase2StartDateTime__c',
          'Phase_2_Start_Date__c',
          'custom__phase_2_start_date_time'
        ) ?? '')
      : '');
  const rawEnrolled =
    boardF(client, data, 'createdDate', 'CreatedDate__c', 'created_date', 'cyberSweepStartDate', 'CyberSweep_Start_Date__c', 'custom__cyber_sweep_start_date') ??
    '';

  return {
    id: clientId,
    clientId,
    sfId,
    firstName,
    lastName,
    clientName,
    auditId,
    stage: opts.stage,
    phaseStatus,
    auditType,
    roadblock,
    auditor,
    dueDate: formatCyberSweepBoardDate(rawDueDate),
    dueDateRaw: String(rawDueDate),
    enrolledDate: formatCyberSweepBoardDate(rawEnrolled),
    quarter: (() => {
      const fromClient = readCyberSweepClientTraversalString(client, data, 'quarter', suf);
      if (fromClient) return fromClient;
      return latestAudit ? String(boardF(latestAudit, latestAuditData, 'quarter', 'Quarter__c') ?? '') : '';
    })(),
    year: (() => {
      const fromClient = readCyberSweepClientTraversalString(client, data, 'fiscalYear', suf);
      if (fromClient) return fromClient;
      return latestAudit
        ? String(
            boardF(latestAudit, latestAuditData, 'fiscalYear', 'Fiscal_Year__c', 'FiscalYear__c', 'custom__fiscal_year') ??
              ''
          )
        : '';
    })(),
    phase1StartDate: String(rawPhase1Start),
    phase2StartDate: String(rawPhase2Start),
    auditCount: clientAudits.length,
  };
}

/**
 * Board column page fetch: **traversal only** so columns render as soon as Client rows return.
 * When Client traversal skin fields are present on the payload, {@link useCyberSweepBoardColumn} only resolves
 * auditor display names (no per-client CSAudit fetch). Otherwise it falls back to legacy CSAudit hydration.
 */
export type CyberSweepBoardColumnPageFetch = {
  rows: CyberSweepBoardCardRow[];
  /**
   * Sum of Gobodo `traverseObjects` array lengths for this logical page (all sub-fetches in the union-dedupe fill).
   * Infinite scroll `pageParam` must advance by this (not `rows.length` and not a fixed page size).
   */
  rawObjectCount: number;
  /** When true, the last sub-fetch was a short or empty batch; no further raw rows exist after this page. */
  endOfStream: boolean;
};

/**
 * @param serverTotalCount — `traverseObjectsCount` for this column (optional). When set, a sub-fetch that returns
 * fewer than a full page still continues until the cursor has reached this total, so "count=3, first page=1 object"
 * does not incorrectly end the stream.
 */
async function fetchCyberSweepBoardColumnPage(
  stageKey: string,
  startIndex: number,
  minUniqueClients: number,
  searchQuery: string | undefined,
  serverTotalCount?: number
): Promise<CyberSweepBoardColumnPageFetch> {
  const hasTotal = Number.isFinite(serverTotalCount) && (serverTotalCount as number) > 0;
  const totalCap = hasTotal ? (serverTotalCount as number) : 0;
  const clientTypeKey = normalizeToGobodoFormat('Client');
  const converted: Record<string, unknown>[] = [];
  const seen = new Set<string>();
  let cursor = startIndex;
  let totalRaw = 0;
  let endOfStream = false;
  let subChunks = 0;

  while (
    converted.length < minUniqueClients &&
    !endOfStream &&
    subChunks < CYBERSWEEP_BOARD_UNION_DEDUPE_MAX_CHUNKS &&
    !(hasTotal && cursor >= totalCap)
  ) {
    subChunks += 1;
    const input = buildCyberSweepBoardColumnTraversalInput(
      stageKey,
      { startIndex: cursor, count: CYBERSWEEP_BOARD_PAGE_SIZE },
      { searchQuery }
    );
    const cacheOpts = cyberSweepBoardColumnCacheOpts(stageKey, cursor, CYBERSWEEP_BOARD_PAGE_SIZE, searchQuery);
    const readOpts = buildTraversalProjectionOpts(cacheOpts, CYBERSWEEP_BOARD_CLIENT_DATA_FIELDS);
    const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
    const result = await retryWithBackoff(
      () => traversalApi.traverseObjects(input, readOpts),
      { maxRetries: 2, initialDelayMs: 1000 }
    );
    const objects = requireCyberSweepTraverseObjectsArray(result);
    const rawLen = objects.length;
    totalRaw += rawLen;
    for (const gobodoObj of objects) {
      try {
        const rec = convertFromGobodoRecord(gobodoObj as Record<string, unknown>, clientTypeKey) as Record<
          string,
          unknown
        >;
        const dedupeKey = String(rec.id ?? rec.key ?? '');
        if (!dedupeKey || seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        converted.push(rec);
      } catch {
        /* skip */
      }
    }
    const nextCursor = cursor + rawLen;
    if (rawLen === 0) {
      endOfStream = true;
    } else if (hasTotal) {
      if (nextCursor >= totalCap) {
        endOfStream = true;
      }
    } else if (rawLen < CYBERSWEEP_BOARD_PAGE_SIZE) {
      endOfStream = true;
    }
    cursor = nextCursor;
  }

  const rows = converted.map((c) => {
    const base = mapClientAndAuditsToBoardCardRow(c, [], {
      stage: stageKey,
      customSuffix: CUSTOM_SUFFIX,
      auditorIdToName: {},
    });
    return { ...base, rawClient: c };
  });
  return { rows, rawObjectCount: totalRaw, endOfStream };
}

type CyberSweepBoardColumnInfinitePage = {
  rows: CyberSweepBoardCardRow[];
  startIndex: number;
  /** Sum of raw rows consumed (sub-fetches) for this RQ page; see {@link CyberSweepBoardColumnPageFetch.rawObjectCount}. */
  rawObjectCount: number;
  endOfStream: boolean;
  /** `traverseObjectsCount` for the column; used to paginate when the first `traverseObjects` batch is short. */
  serverTotalCount: number;
};

function boardInfiniteDataIncludesClientTraversalSkinFields(
  data: InfiniteData<CyberSweepBoardColumnInfinitePage> | undefined
): boolean {
  if (!data?.pages?.length) return false;
  for (const page of data.pages) {
    for (const row of page.rows) {
      const raw = row.rawClient;
      if (!raw) continue;
      if (
        clientRecordExposesCyberSweepBoardTraversalField(raw, 'phaseStatus', CUSTOM_SUFFIX) ||
        clientRecordExposesCyberSweepBoardTraversalField(raw, 'auditKey', CUSTOM_SUFFIX)
      ) {
        return true;
      }
    }
  }
  return false;
}

function collectAuditorSfIdsFromBoardRawClients(
  data: InfiniteData<CyberSweepBoardColumnInfinitePage> | undefined
): string[] {
  const ids = new Set<string>();
  if (!data?.pages?.length) return [];
  for (const page of data.pages) {
    for (const row of page.rows) {
      const raw = row.rawClient;
      if (!raw) continue;
      const d = (raw.data as Record<string, unknown>) ?? raw;
      const rawAuditor = readCyberSweepClientTraversalString(raw, d, 'auditor', CUSTOM_SUFFIX);
      const t = String(rawAuditor || '').trim();
      if (t.length >= 15 || /(__c|__stg)/i.test(t)) ids.add(t);
    }
  }
  return Array.from(ids);
}

function collectAuditorSfIdsFromFlatClientRecords(records: Record<string, unknown>[]): string[] {
  const ids = new Set<string>();
  for (const raw of records) {
    if (!raw) continue;
    const d = (raw.data as Record<string, unknown>) ?? raw;
    const rawAuditor = readCyberSweepClientTraversalString(raw, d, 'auditor', CUSTOM_SUFFIX);
    const t = String(rawAuditor || '').trim();
    if (t.length >= 15 || /(__c|__stg)/i.test(t)) ids.add(t);
  }
  return Array.from(ids);
}

/**
 * Resolves auditor Salesforce IDs from Client traversal `Auditor__c` projections to display names (Clients list).
 * Use when {@link clientRecordsExposesCyberSweepListTraversalFields} is true and legacy {@link useCSAudits} is off.
 */
export function useCyberSweepListTraversalAuditorNames(options: {
  clientRecords: Record<string, unknown>[];
  enabled: boolean;
}) {
  const { clientRecords, enabled } = options;
  const auditorSfIds = useMemo(() => collectAuditorSfIdsFromFlatClientRecords(clientRecords), [clientRecords]);
  const idsSig = auditorSfIds.slice().sort().join('\x1e');

  return useQuery({
    queryKey: ['cybersweep', 'list-traversal-auditor-names', idsSig],
    queryFn: async () => {
      if (auditorSfIds.length === 0) return {} as Record<string, string>;
      const auditorMapRes = await resolveGraphUserNamesBySfIds(auditorSfIds);
      const auditorIdToName: Record<string, string> = {};
      auditorMapRes.forEach((name, id) => {
        auditorIdToName[id] = name;
      });
      return auditorIdToName;
    },
    enabled: USE_GOBODO_API && enabled && auditorSfIds.length > 0,
    staleTime: 60_000,
  });
}

async function buildAuditorIdToNameFromAuditsMap(
  auditsByClient: Record<string, Record<string, unknown>[]>
): Promise<Record<string, string>> {
  const auditorSfIds = new Set<string>();
  for (const id of Object.keys(auditsByClient)) {
    const list = auditsByClient[id] ?? [];
    for (const audit of list) {
      const ad = (audit.data as Record<string, unknown>) ?? audit;
      const raw = String(ad.Auditor__c ?? ad.auditor ?? ad.custom__auditor ?? audit.Auditor__c ?? '').trim();
      if (raw.length >= 15 || /(__c|__stg)/i.test(raw)) auditorSfIds.add(raw);
    }
  }
  const auditorMapRes = await resolveGraphUserNamesBySfIds(Array.from(auditorSfIds));
  const auditorIdToName: Record<string, string> = {};
  auditorMapRes.forEach((name, id) => {
    auditorIdToName[id] = name;
  });
  return auditorIdToName;
}

function mergeBoardColumnInfiniteDataWithHydration(
  data: InfiniteData<CyberSweepBoardColumnInfinitePage>,
  stageKey: string,
  hydration:
    | { auditsByClient: Record<string, Record<string, unknown>[]>; auditorIdToName: Record<string, string> }
    | undefined
): InfiniteData<CyberSweepBoardColumnInfinitePage> {
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      rows: page.rows.map((row) => {
        const raw = row.rawClient;
        if (!raw) return row;
        if (!hydration) return stripBoardRowRawClient(row);
        const cid = String(row.clientId);
        const audits = hydration.auditsByClient[cid] ?? [];
        const mapped = mapClientAndAuditsToBoardCardRow(raw, audits, {
          stage: stageKey,
          customSuffix: CUSTOM_SUFFIX,
          auditorIdToName: hydration.auditorIdToName,
        });
        return mapped;
      }),
    })),
  };
}

/**
 * Per-column infinite query for the CyberSweep kanban. Server uses start date + status, union CSAudit → Client, with
 * status on the `Client` end (see {@link buildCyberSweepBoardColumnTraversalInput}).
 *
 * **List vs board:** When Client rows include CSAudit traversal skin fields (see
 * {@link clientRecordsExposesCyberSweepListTraversalFields} in `cyberSweepClientBoardTraversalFields.ts`), the Clients
 * **list** skips {@link useCSAudits} and resolves auditor names via {@link useCyberSweepListTraversalAuditorNames}
 * instead—same idea as board columns. If those fields are absent (older graphs), list view keeps `useCSAudits` so
 * `displayRecords` can join audits. Board view typically disables the global `useCSAudits()` fetch; summary tiles above
 * the board may reflect client status only until the user switches to list. Board cards read phase and other CSAudit
 * columns from Client traversal skin fields when the API returns them; legacy CSAudit hydration runs only when those
 * fields are absent.
 *
 * **Search:** When `searchQuery` is non-empty, it is passed to the Gobodo traversal as `setSearchQuery` so server-side
 * text search matches the board filter (column counts and pages stay aligned).
 */
export function useCyberSweepBoardColumn(
  stageKey: string,
  options?: { enabled?: boolean; searchQuery?: string }
) {
  const queryClient = useQueryClient();
  const enabled = (options?.enabled ?? true) && USE_GOBODO_API;
  const searchSig = (options?.searchQuery ?? '').trim();
  const searchForApi = searchSig || undefined;

  const infiniteQuery = useInfiniteQuery({
    queryKey: cyberSweepKeys.boardColumn(stageKey, searchSig),
    queryFn: async ({ pageParam }) => {
      const startIndex = pageParam as number;
      const serverTotalCount = await queryClient.fetchQuery({
        queryKey: cyberSweepKeys.boardColumnCount(stageKey, searchSig),
        queryFn: () => fetchCyberSweepBoardColumnTraversalCount(stageKey, searchForApi),
        staleTime: 60_000,
      });
      const { rows, rawObjectCount, endOfStream } = await fetchCyberSweepBoardColumnPage(
        stageKey,
        startIndex,
        CYBERSWEEP_BOARD_PAGE_SIZE,
        searchForApi,
        serverTotalCount
      );
      return { rows, startIndex, rawObjectCount, endOfStream, serverTotalCount };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      const next = (lastPageParam as number) + lastPage.rawObjectCount;
      if (Number.isFinite(lastPage.serverTotalCount) && lastPage.serverTotalCount > 0 && next >= lastPage.serverTotalCount) {
        return undefined;
      }
      if (lastPage.endOfStream) return undefined;
      return next;
    },
    enabled,
    staleTime: 60_000,
  });

  const boardClientIdsForHydration = useMemo(() => {
    const pages = infiniteQuery.data?.pages;
    if (!pages?.length) return [] as string[];
    const ids = new Set<string>();
    for (const p of pages) {
      for (const r of p.rows) {
        const id = String(r.clientId ?? '').trim();
        if (id) ids.add(id);
      }
    }
    return Array.from(ids).sort();
  }, [infiniteQuery.data]);

  const boardUsesClientTraversalFields = useMemo(
    () => boardInfiniteDataIncludesClientTraversalSkinFields(infiniteQuery.data),
    [infiniteQuery.data]
  );

  const boardLegacyAuditHydrationQuery = useQuery({
    queryKey: ['cybersweep', 'board-column-audits', stageKey, searchSig, boardClientIdsForHydration.join('\x1e')],
    queryFn: async () => {
      if (boardClientIdsForHydration.length === 0) {
        return {
          auditsByClient: {} as Record<string, Record<string, unknown>[]>,
          auditorIdToName: {} as Record<string, string>,
        };
      }
      const auditsByClient = await fetchLatestCSAuditsForClientKeys(boardClientIdsForHydration);
      const auditorIdToName = await buildAuditorIdToNameFromAuditsMap(auditsByClient);
      return { auditsByClient, auditorIdToName };
    },
    enabled: enabled && boardClientIdsForHydration.length > 0 && !boardUsesClientTraversalFields,
    staleTime: 60_000,
  });

  const boardAuditorNamesQuery = useQuery({
    queryKey: [
      'cybersweep',
      'board-column-auditor-names',
      stageKey,
      searchSig,
      boardClientIdsForHydration.join('\x1e'),
    ],
    queryFn: async () => {
      const ids = collectAuditorSfIdsFromBoardRawClients(infiniteQuery.data);
      if (ids.length === 0) return {} as Record<string, string>;
      const auditorMapRes = await resolveGraphUserNamesBySfIds(ids);
      const auditorIdToName: Record<string, string> = {};
      auditorMapRes.forEach((name, id) => {
        auditorIdToName[id] = name;
      });
      return auditorIdToName;
    },
    enabled: enabled && boardClientIdsForHydration.length > 0 && boardUsesClientTraversalFields,
    staleTime: 60_000,
  });

  const boardColumnHydration = useMemo(() => {
    if (boardUsesClientTraversalFields) {
      const m = boardAuditorNamesQuery.data;
      if (m === undefined) return undefined;
      return {
        auditsByClient: {} as Record<string, Record<string, unknown>[]>,
        auditorIdToName: m,
      };
    }
    return boardLegacyAuditHydrationQuery.data;
  }, [boardUsesClientTraversalFields, boardAuditorNamesQuery.data, boardLegacyAuditHydrationQuery.data]);

  const mergedBoardData = useMemo(() => {
    if (!infiniteQuery.data) return undefined;
    return mergeBoardColumnInfiniteDataWithHydration(infiniteQuery.data, stageKey, boardColumnHydration);
  }, [infiniteQuery.data, boardColumnHydration, stageKey]);

  const countQuery = useQuery({
    queryKey: cyberSweepKeys.boardColumnCount(stageKey, searchSig),
    queryFn: () => fetchCyberSweepBoardColumnTraversalCount(stageKey, searchForApi),
    enabled,
    staleTime: 60_000,
  });
  return {
    ...infiniteQuery,
    data: mergedBoardData ?? infiniteQuery.data,
    columnTotalCount: countQuery.data,
    columnTotalCountPending: countQuery.isPending,
    /** Board merge still running: legacy CSAudit fetch, or auditor name resolution when using Client traversal fields. */
    isHydratingBoardAudits:
      boardClientIdsForHydration.length > 0 &&
      (boardUsesClientTraversalFields
        ? boardAuditorNamesQuery.isPending
        : boardLegacyAuditHydrationQuery.isPending),
  };
}

export type UseCSAuditsOptions = {
  enabled?: boolean;
  /**
   * When set with no `clientId`, load audits only for these client ids/keys (per-client relationship traversal ∪
   * zero-hop field-index `EQUALS` chunks). Used for Clients list joins.
   */
  clientKeys?: string[];
  /** Paged full `CSAudit__c` scan (e.g. Team dashboard). Prefer {@link clientKeys} for list views. */
  fetchAllCSAudits?: boolean;
};

export function useCSAudits(clientId?: string, options?: UseCSAuditsOptions) {
  const keys = options?.clientKeys ?? [];
  const clientKeysSig = clientKeysSignatureForQuery(keys);
  const mode: 'single' | 'batch' | 'full' =
    clientId != null && clientId !== ''
      ? 'single'
      : options?.fetchAllCSAudits
        ? 'full'
        : keys.length > 0
          ? 'batch'
          : 'batch';

  const shouldRun =
    USE_GOBODO_API &&
    (options?.enabled ?? true) &&
    (clientId != null && clientId !== ''
      ? true
      : options?.fetchAllCSAudits === true || keys.length > 0);

  return useQuery({
    queryKey: cyberSweepKeys.csAudits(clientId, clientKeysSig, mode),
    queryFn: async () => {
      if (clientId != null && clientId !== '') {
        const merged = await fetchCSAuditsForClientKeysMerged([clientId]);
        return enrichCSAuditsWithTemplateInfo(merged);
      }
      if (options?.fetchAllCSAudits) {
        const records = await fetchAllRecordsViaPages(CSAUDIT_OBJECT, {
          dataFields: CSAUDIT_FULL_SCAN_DATA_FIELDS,
          cacheKeyTag: 'csaudit-full-scan-v2-projected',
        });
        return enrichCSAuditsWithTemplateInfo(records);
      }
      if (keys.length > 0) {
        const merged = await fetchCSAuditsForClientKeysMerged(keys);
        return enrichCSAuditsWithTemplateInfo(merged);
      }
      return [];
    },
    enabled: shouldRun,
    staleTime: 60_000,
  });
}

export type MyWorkCSAuditsResult = {
  /**
   * `traversal`: graph hop (+ optional field-index union); `index`: field-index traversal only;
   * `fallback`: full CSAudit list with client-side auditor filter.
   */
  source: 'traversal' | 'index' | 'fallback';
  records: Record<string, unknown>[];
};

/**
 * CyberSweep My Work: prefer one-hop traversal from the current user (auditor) → CSAudit, with
 * union branches on indexed `Auditor__c` / `custom__auditor` when SF user id is known.
 * If that returns nothing (or there is no graph USER key), tries index-only traversal on those fields
 * before falling back to paging all CSAudit rows and filtering in the UI.
 *
 * Caching: Gobodo `traverseObjects` uses server-side cache opts from `csAuditTraversalService` (TTL matches stale time).
 * React Query uses `MY_WORK_CSAUDITS_STALE_MS` / `MY_WORK_CSAUDITS_GC_MS`.
 */
export function useMyWorkCSAudits(options?: { enabled?: boolean }) {
  const { user } = useAuth();
  const email = user?.email ?? '';

  return useQuery({
    queryKey: cyberSweepKeys.myWorkCsAudits(email),
    queryFn: async (): Promise<MyWorkCSAuditsResult> => {
      if (!USE_GOBODO_API) {
        return { source: 'fallback', records: [] };
      }

      const sfId = resolveSfUserIdFromEmail(email);
      if (sfId) {
        const graphUser = await findGraphUserBySfId(sfId);
        if (graphUser?.key) {
          const raw = await fetchMyCSAuditsViaTraversal(graphUser.key, sfId);
          const hydrated = await hydrateCSAuditRecordsForListUi(raw);
          const enriched = await enrichCSAuditsWithTemplateInfo(hydrated);
          if (enriched.length > 0) {
            return { source: 'traversal', records: enriched };
          }
        }

        const rawIndex = await fetchMyCSAuditsViaAuditorFieldIndexes(sfId);
        const hydratedIndex = await hydrateCSAuditRecordsForListUi(rawIndex);
        const enrichedIndex = await enrichCSAuditsWithTemplateInfo(hydratedIndex);
        if (enrichedIndex.length > 0) {
          return { source: 'index', records: enrichedIndex };
        }
      }

      const records = await fetchAllRecordsViaPages(CSAUDIT_OBJECT);
      const enrichedRecords = await enrichCSAuditsWithTemplateInfo(records);
      return { source: 'fallback', records: enrichedRecords };
    },
    enabled: USE_GOBODO_API && (options?.enabled ?? true) && !!email,
    staleTime: MY_WORK_CSAUDITS_STALE_MS,
    gcTime: MY_WORK_CSAUDITS_GC_MS,
  });
}

/**
 * If a CS_Audit is already in a list query cache, return its Gobodo `key` so
 * `GET /graph/data/object/{id}?cachekey=…` can use the object key as the single segment (not only the id).
 */
function findCSAuditGobodoObjectKeyInQueryCache(queryClient: QueryClient, numericId: string): string | null {
  const want = String(numericId).trim();
  if (!/^\d+$/.test(want)) return null;
  const buckets = [
    ...queryClient.getQueriesData<unknown>({ queryKey: ['cybersweep', 'cs_audits'] }),
    ...queryClient.getQueriesData<unknown>({ queryKey: ['cybersweep', 'my_work_cs_audits'] }),
  ];
  for (const [, data] of buckets) {
    if (!Array.isArray(data)) continue;
    for (const row of data) {
      if (!row || typeof row !== 'object') continue;
      const r = row as Record<string, unknown>;
      if (String(r.id ?? '') !== want) continue;
      const k = r.key != null ? String(r.key).trim() : '';
      if (k) return k;
    }
  }
  return null;
}

export function useCSAudit(auditId: string | null) {
  const queryClient = useQueryClient();
  const routeParam = (auditId ?? '').trim();
  const [promotedObjectKey, setPromotedObjectKey] = useState<string | null>(null);
  const prevRouteParamRef = useRef<string | null>(null);
  // Prefer Gobodo object key for the React Query cache (aligns with by-key read / propertyPatch invalidation).
  const effectiveCacheId = (promotedObjectKey || routeParam).trim();

  useEffect(() => {
    if (prevRouteParamRef.current != null && prevRouteParamRef.current !== routeParam) {
      setPromotedObjectKey(null);
    }
    prevRouteParamRef.current = routeParam;
  }, [routeParam]);

  return useQuery({
    queryKey: cyberSweepKeys.csAudit(effectiveCacheId),
    queryFn: async () => {
      const rawId = String(effectiveCacheId);
      if (!rawId) return null;

      const byIdGobodoOpts: GetRecordByIdOptions | undefined = /^\d+$/.test(rawId)
        ? (() => {
            const k = findCSAuditGobodoObjectKeyInQueryCache(queryClient, rawId);
            return k ? { gobodoObjectKeyForGetByIdCache: k } : undefined;
          })()
        : undefined;

      let record: Record<string, unknown> | null = null;

      // 1) Numeric Gobodo ID path
      if (/^\d+$/.test(rawId)) {
        try {
          record = await getRecordById(CSAUDIT_OBJECT, rawId, byIdGobodoOpts);
        } catch {
          record = null;
        }
      }

      // 2) Key path (supports both with/without suffix)
      if (!record) {
        try {
          const { getGobodoRecordByKey } = await import('../gobodo/gobodoRecordService');
          const suffix = GobodoConfig.customSuffix();
          const keyCandidates = [
            rawId,
            rawId.endsWith(suffix) ? rawId : `${rawId}${suffix}`,
          ];
          for (const key of keyCandidates) {
            try {
              const byKey = await getGobodoRecordByKey(key);
              if (byKey) {
                record = byKey as Record<string, unknown>;
                break;
              }
            } catch {
              // try next candidate
            }
          }
        } catch {
          // fall through
        }
      }

      // 3) Final fallback: try recordStorage by provided ID/key string
      if (!record) {
        try {
          record = await getRecordById(
            CSAUDIT_OBJECT,
            rawId,
            /^\d+$/.test(String(rawId).trim()) ? byIdGobodoOpts : undefined
          );
        } catch {
          record = null;
        }
      }

      if (process.env.NODE_ENV === 'development') {
        console.group('[CyberSweep Debug] useCSAudit');
        console.log('Requested auditId:', auditId, 'effectiveCacheId:', effectiveCacheId);
        console.log('Record found:', record ? { id: record.id, key: (record as any).key, fields: Object.keys(record) } : null);
        if (record) console.log('Full record:', record);
        console.groupEnd();
      }
      if (!record) return null;
      const [enrichedRecord] = await enrichCSAuditsWithTemplateInfo([record as Record<string, unknown>]);
      const out = (enrichedRecord ?? record) as Record<string, unknown>;
      const k = out.key != null ? String(out.key).trim() : '';
      if (k && /^\d+$/.test(routeParam) && k !== routeParam) {
        queryClient.setQueryData(cyberSweepKeys.csAudit(k), out);
        setPromotedObjectKey((p) => p ?? k);
        queueMicrotask(() => {
          queryClient.removeQueries({ queryKey: cyberSweepKeys.csAudit(routeParam), exact: true });
        });
      }
      return out;
    },
    enabled: USE_GOBODO_API && !!auditId,
    staleTime: 60_000,
  });
}

async function resolvePAWTraversalStartKey(csAuditId: string): Promise<string> {
  let startKey = csAuditId;
  if (/^[0-9]+$/.test(csAuditId)) {
    try {
      const auditRecord = await getRecordById(CSAUDIT_OBJECT, csAuditId);
      if (auditRecord && (auditRecord as { key?: unknown }).key) {
        startKey = String((auditRecord as { key?: unknown }).key);
        if (process.env.NODE_ENV === 'development') {
          console.log('[CyberSweep Traversal] Resolved numeric ID', csAuditId, 'to key:', startKey);
        }
      }
    } catch {
      // If lookup fails, try with the original ID anyway
    }
  }
  return ensureGobodoCustomSuffixOnRecordKey(startKey);
}

function buildPAWRecordsTraversalInput(
  csAuditId: string,
  startKey: string,
  pagination: { startIndex: number; count: number }
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  input.setStartObjectKey(startKey);
  input.setRelationshipTypes([getPAWCSAuditRelKey()]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
  input.setEndObjectTypes([normalizeToGobodoFormat(PAWRECORD_OBJECT)]);
  input.setMaxPathLength(1);
  input.setStartIndex(pagination.startIndex);
  input.setCount(pagination.count);

  const pawRefValues = uniqueLookupTokens([
    csAuditId,
    startKey,
    stripKnownSuffix(startKey),
  ]);
  const pawFieldIndexNames = [
    'csAuditId',
    'CSAuditRelField__c',
    'CS_Audit__c',
    'custom__cs_audit',
    'csAuditRelField',
  ];
  const pawType = normalizeToGobodoFormat(PAWRECORD_OBJECT);
  const pawFieldBranches = pawFieldIndexNames.flatMap((indexName) => {
    try {
      const filter = new GobodoApi.IndexFilter();
      filter.setIndexName(indexName);
      filter.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
      filter.setValues(pawRefValues);

      const branch = new GobodoApi.GraphTraversalInput();
      branch.setGraphId(GobodoConfig.graph());
      branch.setStartObjectTypes([pawType]);
      branch.setEndObjectTypes([pawType]);
      branch.setMaxPathLength(0);
      branch.setStartIndex(pagination.startIndex);
      branch.setCount(pagination.count);
      branch.setStartObjectFieldFilters([filter]);
      return [branch];
    } catch {
      return [];
    }
  });
  chainUnionTraversals(input, pawFieldBranches);

  if (process.env.NODE_ENV === 'development') {
    console.log(
      '[CyberSweep Traversal] Traversing with startKey:',
      startKey,
      'relType:',
      getPAWCSAuditRelKey(),
      'endType:',
      PAWRECORD_OBJECT,
      'startIndex:',
      pagination.startIndex,
      'count:',
      pagination.count,
      'unionFieldIndexes:',
      pawFieldIndexNames,
      'unionValues:',
      pawRefValues
    );
  }

  return input;
}

function cyberSweepPAWTraversalCacheOpts(csAuditId: string, startIndex: number, count: number): GobodoCacheOpts {
  return makeCacheOpts(
    [
      'cybersweep',
      'paw-records',
      'traverse',
      csAuditId,
      String(GobodoConfig.graph()),
      String(startIndex),
      String(count),
      getPAWCSAuditRelKey(),
      'paw-audit-traversal-v1',
    ],
    CACHE_TTL.SHORT
  );
}

function cyberSweepPAWTraversalCountCacheOpts(csAuditId: string): GobodoCacheOpts {
  return makeCacheOpts(
    [
      'cybersweep',
      'paw-records',
      'traverse-count',
      csAuditId,
      String(GobodoConfig.graph()),
      getPAWCSAuditRelKey(),
      'paw-audit-traversal-v1',
    ],
    CACHE_TTL.SHORT
  );
}

export type CyberSweepPAWTraversalPageFetch = {
  records: Record<string, unknown>[];
  rawObjectCount: number;
};

export async function fetchPAWRecordsTraversalPage(
  csAuditId: string,
  startIndex: number,
  count: number,
  options?: { dataFields?: string; cacheKeyTag?: string }
): Promise<CyberSweepPAWTraversalPageFetch> {
  const startKey = await resolvePAWTraversalStartKey(csAuditId);
  const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
  const input = buildPAWRecordsTraversalInput(csAuditId, startKey, { startIndex, count });
  const cacheOpts = cyberSweepPAWTraversalCacheOpts(csAuditId, startIndex, count);
  const projectedDataFields = options?.dataFields?.trim() ?? '';
  const readOpts = projectedDataFields
    ? buildTraversalProjectionOpts(
        {
          ...cacheOpts,
          cachekey: [...cacheOpts.cachekey, options?.cacheKeyTag ?? 'projected'],
        },
        projectedDataFields
      )
    : cacheOpts;
  const result = await retryWithBackoff(
    () => traversalApi.traverseObjects(input, readOpts),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  const objects = requireCyberSweepTraverseObjectsArray(result);
  const rawObjectCount = objects.length;
  const out: Record<string, unknown>[] = [];
  const seen = new Set<string>();
  for (const gobodoObj of objects) {
    try {
      const converted = convertFromGobodoRecord(
        gobodoObj as Record<string, unknown>,
        PAWRECORD_OBJECT
      ) as Record<string, unknown>;
      const row: Record<string, unknown> = {
        ...converted,
        key: (gobodoObj as Record<string, unknown>).key ?? converted.key,
        label: (gobodoObj as Record<string, unknown>).label ?? converted.label,
      };
      const dedupeKey = String(row.id ?? row.key ?? '');
      if (!dedupeKey || seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      out.push(row);
    } catch {
      /* skip malformed row */
    }
  }
  const enriched = await enrichPAWRecordsWithWebsiteInfo(out);
  if (process.env.NODE_ENV === 'development') {
    console.log(
      '[CyberSweep Traversal] PAW page for audit',
      csAuditId,
      'startIndex:',
      startIndex,
      'raw:',
      rawObjectCount,
      'deduped:',
      enriched.length
    );
  }
  return { records: enriched, rawObjectCount };
}

export async function fetchPAWRecordTraversalCount(csAuditId: string): Promise<number> {
  const startKey = await resolvePAWTraversalStartKey(csAuditId);
  const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
  const countInput = buildPAWRecordsTraversalInput(csAuditId, startKey, { startIndex: 0, count: 1 });
  const cacheOpts = cyberSweepPAWTraversalCountCacheOpts(csAuditId);
  const countRaw = await retryWithBackoff(
    () => traversalApi.traverseObjectsCount(countInput, cacheOpts),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  return parseCyberSweepBoardTraversalCount(countRaw);
}

export async function fetchAllPAWRecordsForAudit(csAuditId: string): Promise<Record<string, unknown>[]> {
  const acc: Record<string, unknown>[] = [];
  const seen = new Set<string>();

  // Don't use the broken traverseObjectsCount API - just page through until we get no more records
  let startIndex = 0;
  for (let page = 0; page < CYBERSWEEP_PAW_TRAVERSAL_MAX_PAGES; page++) {
    const { records, rawObjectCount } = await fetchPAWRecordsTraversalPage(
      csAuditId,
      startIndex,
      CYBERSWEEP_PAW_TRAVERSAL_PAGE_SIZE
    );
    if (rawObjectCount === 0) break;
    for (const row of records) {
      const dedupeKey = String(row.id ?? row.key ?? '');
      if (!dedupeKey || seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      acc.push(row);
    }
    startIndex += rawObjectCount;
    // Stop if we got fewer records than page size (last page)
    if (rawObjectCount < CYBERSWEEP_PAW_TRAVERSAL_PAGE_SIZE) break;
  }

  return acc;
}

export function usePAWRecords(csAuditId?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: cyberSweepKeys.pawRecords(csAuditId),
    queryFn: async () => {
      if (csAuditId) {
        try {
          const traversalResults = await fetchAllPAWRecordsForAudit(csAuditId);
          if (process.env.NODE_ENV === 'development') {
            console.group('[CyberSweep Debug] usePAWRecords (traversal)');
            console.log('csAuditId passed:', csAuditId);
            console.log('Traversal returned:', traversalResults.length, 'records');
            if (traversalResults.length > 0) {
              console.log('Sample PAW keys:', Object.keys(traversalResults[0]));
              console.log('First 3:', traversalResults.slice(0, 3));
            }
            console.groupEnd();
          }
          return traversalResults;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[CyberSweep Traversal] PAW traversal failed:', err);
          }
          return [];
        }
      }
      const records = await getRecordsForObject(PAWRECORD_OBJECT);
      if (process.env.NODE_ENV === 'development') {
        console.group('[CyberSweep Debug] usePAWRecords (full fetch)');
        console.log('Total PAWRecord records fetched:', records.length);
        if (records.length > 0) {
          console.log('Sample PAW record keys:', Object.keys(records[0]));
          console.log('First 3 PAW records:', records.slice(0, 3));
        }
        console.groupEnd();
      }
      return enrichPAWRecordsWithWebsiteInfo(records);
    },
    enabled: USE_GOBODO_API && (options?.enabled ?? true),
    staleTime: 60_000,
  });
}

export function usePAWRecordsInfinite(csAuditId?: string, options?: { enabled?: boolean; pageSize?: number }) {
  const pageSize = options?.pageSize ?? CYBERSWEEP_PAW_TRAVERSAL_PAGE_SIZE;
  return useInfiniteQuery({
    queryKey: csAuditId ? cyberSweepKeys.pawRecordsInfinite(csAuditId, pageSize) : ['cybersweep', 'paw_records', 'infinite', 'none'],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      if (!csAuditId) {
        return { records: [], rawObjectCount: 0 };
      }
      // Don't rely on serverTotalCount - the traverseObjectsCount API is broken for union traversals
      // Just page through until we get fewer records than page size
      const { records, rawObjectCount } = await fetchPAWRecordsTraversalPage(csAuditId, pageParam as number, pageSize);
      return { records, rawObjectCount };
    },
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      const next = (lastPageParam as number) + lastPage.rawObjectCount;
      if (lastPage.rawObjectCount === 0) return undefined;
      // Stop if we got fewer records than page size (last page)
      if (lastPage.rawObjectCount < pageSize) return undefined;
      return next;
    },
    enabled: USE_GOBODO_API && !!csAuditId && (options?.enabled ?? true),
    staleTime: 60_000,
  });
}

export function usePAWRecordCount(csAuditId?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: csAuditId ? cyberSweepKeys.pawRecordsCount(csAuditId) : ['cybersweep', 'paw_records', 'count', 'none'],
    queryFn: async () => {
      if (!csAuditId) return 0;
      // The traversal count API doesn't work correctly with union traversals,
      // so we fetch all records and count them for accuracy
      const allRecords = await fetchAllPAWRecordsForAudit(csAuditId);
      return allRecords.length;
    },
    enabled: USE_GOBODO_API && !!csAuditId && (options?.enabled ?? true),
    staleTime: 60_000,
  });
}

/**
 * Fetch CyberSweep clients: same “listed clients” set as {@link useCyberSweepEnrolledClientsCount} and the list infinite
 * query (start date and/or an existing `cs_audit_client_rel` to the Client).
 *
 * Primary: Gobodo **traversal** (`buildCyberSweepListedClientsTraversalInput`) — not a full Client list scan.
 * Fallback: paged Client + `CSAudit` ref scan if traversal is unavailable.
 */
export function useCyberSweepClients(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: cyberSweepKeys.cyberSweepClients(),
    queryFn: async () => {
      console.log('[CyberSweep] useCyberSweepClients queryFn STARTING (traversal + index filter)...');

      const startDateIndexName = CYBERSWEEP_START_DATE_INDEX_NAME;
      let lastTraversalError: unknown;
      let traversalCompleted = false;

      try {
        const cyberSweepClients = await fetchCyberSweepClientsViaStartDateIndexTraversal(startDateIndexName);
        traversalCompleted = true;
        if (cyberSweepClients.length > 0) {
          console.log(
            `[CyberSweep] Client list via traversal (index=${startDateIndexName}):`,
            cyberSweepClients.length,
            'clients'
          );
          if (process.env.NODE_ENV === 'development') {
            console.group('[CyberSweep] useCyberSweepClients RESULTS');
            console.log('Index name:', startDateIndexName);
            console.log('Clients:', cyberSweepClients.length);
            if (cyberSweepClients[0]) console.log('First client:', cyberSweepClients[0]);
            console.groupEnd();
          }
          return cyberSweepClients;
        }
        if (process.env.NODE_ENV === 'development') {
          console.log(
            `[CyberSweep] Traversal returned 0 clients for index "${startDateIndexName}" (field ${CYBERSWEEP_START_DATE_FIELD}).`
          );
        }
      } catch (err) {
        lastTraversalError = err;
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[CyberSweep] Traversal failed for index "${startDateIndexName}":`, err);
        }
      }

      if (!lastTraversalError && traversalCompleted) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[CyberSweep] Traversal OK with 0 rows; skipping Client paging.');
        }
        return [];
      }

      console.warn(
        '[CyberSweep] Start-date index traversal did not return data; falling back to paged Client scan.',
        lastTraversalError
      );
      try {
        return await fallbackFetchCyberSweepClientsByPaging();
      } catch (err) {
        console.error('[CyberSweep] ERROR fallback paging Client records:', err);
        throw err;
      }
    },
    enabled: USE_GOBODO_API && (options?.enabled ?? true),
    staleTime: 60_000,
  });
}

/**
 * List view: paginated clients via the same graph as {@link fetchCyberSweepClientsTraversalPage} and total
 * {@link useCyberSweepEnrolledClientsCount} (`startIndex` / `count` per page; “Load more”).
 */
export function useCyberSweepClientsInfinite(options?: { enabled?: boolean; pageSize?: number }) {
  const pageSize = options?.pageSize ?? CYBERSWEEP_CLIENT_LIST_TRAVERSAL_PAGE_SIZE;
  const startDateIndexName = CYBERSWEEP_START_DATE_INDEX_NAME;
  return useInfiniteQuery({
    queryKey: cyberSweepKeys.cyberSweepClientsInfinite(GobodoConfig.graph(), startDateIndexName, pageSize),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      fetchCyberSweepClientsTraversalPage(startDateIndexName, pageParam as number, pageSize),
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const prev = lastPageParam as number;
      if (lastPage.rawObjectCount === 0) return undefined;
      if (lastPage.rawObjectCount < pageSize) return undefined;
      return prev + pageSize;
    },
    enabled: USE_GOBODO_API && (options?.enabled ?? true),
    staleTime: 60_000,
  });
}

/**
 * Total CyberSweep “listed” clients via `traverseObjectsCount` on {@link buildCyberSweepListedClientsTraversalInput}
 * (identical graph to list pagination and board full client load). Summary “Total Clients” on list and board.
 */
export function useCyberSweepEnrolledClientsCount(options?: { enabled?: boolean }) {
  const startDateIndexName = CYBERSWEEP_START_DATE_INDEX_NAME;
  return useQuery({
    queryKey: cyberSweepKeys.cyberSweepClientsEnrolledCount(GobodoConfig.graph(), startDateIndexName),
    queryFn: () => fetchCyberSweepEnrolledClientsCount(startDateIndexName),
    enabled: USE_GOBODO_API && (options?.enabled ?? true),
    staleTime: 60_000,
  });
}

/**
 * Summary “Roadblocked” count: one `traverseObjectsCount` on zero-hop CSAudit with Phase_Status picklist OR
 * (see {@link CYBERSWEEP_ROADBLOCK_PHASE_STATUS_VALUES}). Respects board `searchQuery` when provided.
 */
export function useCyberSweepRoadblockedCsAuditCount(options?: { enabled?: boolean; searchQuery?: string }) {
  const searchSig = options?.searchQuery?.trim() ?? '';
  const picklistSig = [...CYBERSWEEP_ROADBLOCK_PHASE_STATUS_VALUES].sort().join('|');
  return useQuery({
    queryKey: cyberSweepKeys.roadblockedCsAuditPhaseCount(
      GobodoConfig.graph(),
      CYBERSWEEP_PHASE_STATUS_INDEX_NAME,
      picklistSig,
      searchSig
    ),
    queryFn: () => fetchCyberSweepRoadblockedCsAuditCount(searchSig || undefined),
    enabled: USE_GOBODO_API && (options?.enabled ?? true),
    staleTime: 60_000,
  });
}

export function useCyberSweepClient(clientId: string | null) {
  return useQuery({
    queryKey: ['cybersweep', 'client', clientId],
    queryFn: async () => {
      console.group('[useCyberSweepClient] Fetching client');
      console.log('Input clientId:', clientId);

      const { getGobodoRecordByKey } = await import('../gobodo/gobodoRecordService');

      // If clientId is a Gobodo numeric ID, fetch directly from Account
      if (/^\d+$/.test(clientId!)) {
        try {
          const r = await getRecordById('Account', clientId!);
          if (r) {
            console.log('Found via numeric ID:', r.id, r.key, (r as any).Name ?? (r as any).name ?? (r as any).label);
            console.groupEnd();
            return r;
          }
        } catch { /* continue */ }
      }

      // Gobodo client keys are sfId + custom suffix; getGobodoRecordByKey appends the suffix when missing
      // (do not pre-concat here — that doubled keys like id__stg5_c__stg5_c with ensureGobodoCustomSuffixOnRecordKey).
      try {
        const r = await getGobodoRecordByKey(clientId!);
        if (r) {
          console.log('Found via getGobodoRecordByKey:', r.id, r.key, (r as any).Name ?? (r as any).name ?? (r as any).label);
          console.groupEnd();
          return r;
        }
      } catch { /* continue */ }

      console.warn('Client not found for clientId:', clientId);
      console.groupEnd();
      return null;
    },
    enabled: USE_GOBODO_API && !!clientId,
    staleTime: 60_000,
  });
}

/**
 * Resolve a display name from {@link useAccountIdToName}’s map using the raw Account__c / client ref
 * string (with or without Gobodo suffix).
 */
export function lookupAccountDisplayName(
  map: Record<string, string> | undefined,
  accountRef: string
): string {
  if (!map || !accountRef) return '';
  const stripped = accountRef.replace(/(__stg3_c|__stg_c|__dev_c|__c)$/i, '');
  return map[accountRef] ?? map[stripped] ?? '';
}

function extractAccountDisplayNameFromGenericRecord(record: GenericRecord): string {
  const rec = record as Record<string, unknown>;
  const name = String(rec.Name__c ?? rec.name ?? rec.label ?? '').trim();
  const firstName = String(rec.FirstName__c ?? rec.First_Name__c ?? '').trim();
  const lastName = String(rec.LastName__c ?? rec.Last_Name__c ?? '').trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || name;
}

/**
 * One logical account ref → display names for map entries (sf id, suffixed key, stripped key).
 * Uses {@link getGobodoRecordByKey} / {@link getGobodoRecordById} so Gobodo server cache applies.
 */
async function fetchCyberSweepAccountDisplayNameLookup(sfId: string): Promise<{ entries: Record<string, string> }> {
  const entries: Record<string, string> = {};
  const numericId = parseInt(sfId, 10);
  const isNumeric = !isNaN(numericId) && String(numericId) === sfId;

  const isBadName = (s: string) => /^(Client|Account|Record)-?\d+$/i.test(s);

  try {
    const rec = await getGobodoRecordByKey(sfId);
    if (rec) {
      const displayName = extractAccountDisplayNameFromGenericRecord(rec);
      if (displayName && !isBadName(displayName)) {
        const key = String((rec as { key?: unknown }).key ?? sfId).trim() || sfId;
        entries[sfId] = displayName;
        entries[key] = displayName;
        const stripped = stripKnownSuffix(key);
        if (stripped) entries[stripped] = displayName;
        return { entries };
      }
    }
  } catch {
    /* fall through to numeric id */
  }

  if (isNumeric) {
    try {
      const rec = await getGobodoRecordById(String(numericId));
      if (rec) {
        const displayName = extractAccountDisplayNameFromGenericRecord(rec);
        if (displayName && !isBadName(displayName)) {
          entries[sfId] = displayName;
          return { entries };
        }
      }
    } catch {
      /* not found */
    }
  }
  return { entries: {} };
}

/**
 * Builds Account__c / client ref → display name for CSAudit rows. Seeds from optional
 * `seedClientRecords` (CyberSweep client traversal), then one TanStack query per missing lookup token
 * (5m stale) backed by {@link getGobodoRecordByKey} (Gobodo server cache on GET by key/id).
 */
export function useAccountIdToName(
  csAuditRecords: Record<string, unknown>[],
  options?: { seedClientRecords?: Record<string, unknown>[] }
) {
  const seedRows = options?.seedClientRecords;
  const accountIds = useMemo(
    () => extractCyberSweepAccountLookups(csAuditRecords),
    [csAuditRecords]
  );
  const seedKey = useMemo(() => {
    if (!seedRows?.length) return '';
    return seedRows.map((r) => `${String(r.id ?? '')}:${String(r.key ?? '')}`).sort().join('|');
  }, [seedRows]);

  const preliminaryMap = useMemo(() => {
    if (!seedRows?.length) return {} as Record<string, string>;
    return buildCyberSweepAccountDisplayNameMapFromClientRecords(seedRows);
  }, [seedRows, seedKey]);

  const missingIds = useMemo(() => {
    const set = new Set<string>();
    for (const id of accountIds) {
      if (!lookupAccountDisplayName(preliminaryMap, id)) {
        set.add(id);
      }
    }
    return Array.from(set).sort();
  }, [accountIds, preliminaryMap]);

  const suffix = GobodoConfig.customSuffix();
  const graphId = GobodoConfig.graph();

  const lookupQueries = useQueries({
    queries: missingIds.map((sfId) => ({
      queryKey: ['gobodo', 'object-by-key', 'cybersweep-account-name', String(graphId), suffix, sfId] as const,
      queryFn: () => fetchCyberSweepAccountDisplayNameLookup(sfId),
      enabled: USE_GOBODO_API && !!sfId,
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,
    })),
  });

  const data: Record<string, string> | undefined = !USE_GOBODO_API
    ? undefined
    : (() => {
        const map: Record<string, string> = { ...preliminaryMap };
        for (const q of lookupQueries) {
          const ent = q.data?.entries;
          if (ent && Object.keys(ent).length > 0) {
            Object.assign(map, ent);
          }
        }
        return map;
      })();

  const isPending = USE_GOBODO_API && lookupQueries.some((q) => q.isPending);
  const isLoading = USE_GOBODO_API && lookupQueries.some((q) => q.isLoading);
  const isFetching = USE_GOBODO_API && lookupQueries.some((q) => q.isFetching);
  const isError = lookupQueries.some((q) => q.isError);
  const error = lookupQueries.find((q) => q.error)?.error ?? null;

  return {
    data,
    isPending,
    isLoading,
    isFetching,
    isError,
    error,
    status: (isPending ? 'pending' : isError ? 'error' : 'success') as 'pending' | 'error' | 'success',
  };
}

export function useContactIdToName(records: Record<string, unknown>[]) {
  const contactIds = useMemo(
    () => extractCyberSweepContactLookups(records),
    [records]
  );

  return useQuery({
    queryKey: ['cybersweep', 'contact_id_to_name', contactIds.sort().join(',')],
    queryFn: async () => {
      const map: Record<string, string> = {};
      for (const lookup of contactIds) {
        const displayName = await fetchLookupDisplayName(CONTACT_OBJECT, lookup).catch(() => '');
        if (!displayName) continue;
        map[lookup] = displayName;
        map[stripKnownSuffix(lookup)] = displayName;
      }
      return map;
    },
    enabled: USE_GOBODO_API && contactIds.length > 0,
    staleTime: 5 * 60_000,
  });
}

export function useUserIdToName(records: Record<string, unknown>[]) {
  const userIds = useMemo(
    () => extractCyberSweepUserLookups(records),
    [records]
  );

  return useQuery({
    queryKey: ['cybersweep', 'user_id_to_name', userIds.sort().join(',')],
    queryFn: async () => {
      const resolved = await resolveGraphUserNamesBySfIds(userIds);
      const map: Record<string, string> = {};
      resolved.forEach((value, key) => {
        map[key] = value;
        map[stripKnownSuffix(key)] = value;
      });
      return map;
    },
    enabled: USE_GOBODO_API && userIds.length > 0,
    staleTime: 5 * 60_000,
  });
}

/**
 * Hook to resolve auditor Salesforce IDs to display names
 * Similar to useAccountIdToName but for User records (auditors)
 */
export function useAuditorIdToName(csAuditRecords: Record<string, unknown>[]) {
  // Extract unique auditor IDs from CSAudit records
  const { auditorIds, allAuditorValues: _allAuditorValues } = useMemo(() => {
    const ids = new Set<string>();
    const allValues = new Set<string>();
    const stats = { empty: 0, shortNames: 0, sfIds: 0 };

    for (const r of csAuditRecords) {
      const data = (r.data as Record<string, unknown>) ?? r;
      const auditorId = data.Auditor__c ?? data.auditor ?? data.custom__auditor ?? r.Auditor__c ?? r.auditor ?? r.custom__auditor;

      if (auditorId == null || auditorId === '') {
        stats.empty++;
        continue;
      }

      const idStr = String(auditorId).trim();
      if (!idStr) {
        stats.empty++;
        continue;
      }

      allValues.add(idStr);

      // Check if it looks like a Salesforce ID (15 or 18 chars, or has suffix)
      if (idStr.length >= 15 || idStr.includes('__')) {
        ids.add(idStr);
        stats.sfIds++;
      } else {
        stats.shortNames++; // Likely already a name, not an ID
      }
    }

    if (process.env.NODE_ENV === 'development') {
      console.group('[CyberSweep Debug] Auditor field extraction');
      console.log('Total records:', csAuditRecords.length);
      console.log('Stats:', stats);
      console.log('All unique auditor values found:', Array.from(allValues).slice(0, 10));
      console.log('Salesforce IDs to resolve:', Array.from(ids).slice(0, 10));
      console.groupEnd();
    }

    return { auditorIds: Array.from(ids), allAuditorValues: allValues };
  }, [csAuditRecords]);

  return useQuery({
    queryKey: ['cybersweep', 'auditor_id_to_name', auditorIds.sort().join(',')],
    queryFn: async () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[CyberSweep] Resolving names for', auditorIds.length, 'unique auditor IDs');
        console.log('[CyberSweep] Auditor IDs:', auditorIds.slice(0, 10));
      }

      const nameMap = await resolveGraphUserNamesBySfIds(auditorIds);

      // Convert Map to plain object for easier consumption
      const result: Record<string, string> = {};
      nameMap.forEach((name, id) => {
        result[id] = name;
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('[CyberSweep] auditorIdToName map built:', Object.keys(result).length, 'entries');
        if (Object.keys(result).length > 0) {
          console.log('[CyberSweep] Sample auditor name entries:', Object.entries(result).slice(0, 5));
        }
      }

      return result;
    },
    enabled: USE_GOBODO_API && auditorIds.length > 0,
    staleTime: 5 * 60_000,
  });
}

/**
 * Lookup auditor display name from the resolved map.
 * If the auditorRef looks like a name already (short string without SF ID pattern),
 * returns it directly. If it looks like a Salesforce ID, looks it up in the map.
 */
export function lookupAuditorDisplayName(
  map: Record<string, string> | undefined,
  auditorRef: string
): string {
  if (!auditorRef) return '';

  const trimmed = auditorRef.trim();
  if (!trimmed) return '';

  // Check if it looks like a Salesforce ID (15 or 18 chars, or has __c suffix)
  const looksLikeSfId = trimmed.length >= 15 || /(__c|__stg_c|__dev_c|__stg3_c)$/i.test(trimmed);

  // If it doesn't look like an ID, it's probably already a name - return it directly
  if (!looksLikeSfId) {
    return trimmed;
  }

  // Try to look up the ID in the map
  if (!map) return '';
  const stripped = auditorRef.replace(/(__stg3_c|__stg_c|__dev_c|__c)$/i, '');
  return map[auditorRef] ?? map[stripped] ?? '';
}

export function useCreateCSAudit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      console.group('[useCreateCSAudit] === MUTATION DEBUG ===');
      console.log('Input data to createRecord:', JSON.stringify(data, null, 2));
      console.log('  *** Account__c value:', data.Account__c);
      console.log('  *** custom__account value:', data.custom__account);

      const result = await createRecord(CSAUDIT_OBJECT, data);

      console.log('Result from createRecord:', result);
      console.log('  - result.id:', (result as any)?.id);
      console.log('  - result.key:', (result as any)?.key);
      console.log('  - result.Account__c:', (result as any)?.Account__c);
      console.groupEnd();

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cybersweep', 'cs_audits'] });
      queryClient.invalidateQueries({ queryKey: ['cybersweep', 'board-column'] });
    },
  });
}

export function useDeleteCSAudit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (auditId: string) => {
      console.log('[useDeleteCSAudit] Deleting CS Audit:', auditId);
      const result = await deleteRecord(CSAUDIT_OBJECT, auditId);
      console.log('[useDeleteCSAudit] Delete result:', result);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cybersweep', 'cs_audits'] });
      queryClient.invalidateQueries({ queryKey: ['cybersweep', 'board-column'] });
      queryClient.invalidateQueries({ queryKey: ['cybersweep', 'cs_audit'] });
    },
  });
}

/** Same suffix stripping as {@link collectCyberSweepClientDetailQueryKeyIds} for CS_Audit `key` / URL aliases. */
const CSAUDIT_QUERY_KEY_SUFFIX_RE = /(__stg3_c|__stg5_c|__stg_c|__dev_c|__c)$/i;

/**
 * `useCSAudit` primary cache is keyed by Gobodo object `key` when available; route param / `id` / stripped key stay as aliases
 * for saves (see {@link useUpdateCSAudit} / {@link useUpdateCyberSweepClient}).
 */
function collectCSAuditDetailQueryKeyIds(params: {
  recordIdForMutation: string;
  detailAuditId?: string | null;
  updatedRecord?: unknown;
}): string[] {
  const ids = new Set<string>();
  const push = (v: unknown) => {
    const s = String(v ?? '').trim();
    if (s) ids.add(s);
  };
  const rec = params.updatedRecord as Record<string, unknown> | null | undefined;
  if (rec && typeof rec === 'object') {
    const keyStr = rec.key != null ? String(rec.key).trim() : '';
    if (keyStr) {
      push(keyStr);
      const stripped = keyStr.replace(CSAUDIT_QUERY_KEY_SUFFIX_RE, '');
      if (stripped) push(stripped);
    }
    push(rec.id);
  }
  push(params.recordIdForMutation);
  push(params.detailAuditId);
  return Array.from(ids);
}

export function useUpdateCSAudit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      updates: Record<string, unknown>;
      /** Same id string as `useCSAudit(…)` on the audit screen (URL / key / numeric) — see {@link collectCSAuditDetailQueryKeyIds}. */
      detailAuditId?: string | null;
    }) => updateRecord(CSAUDIT_OBJECT, vars.id, vars.updates, { gobodoPropertyPatch: true }),
    onSuccess: async (updated, { id, detailAuditId }) => {
      /**
       * Match {@link useUpdateCyberSweepClient}: merge the write response into the cached `useCSAudit` row
       * (Gobodo can return a sparse `data` bag — {@link mergeGobodoDetailRecord}) and update every
       * query key alias. Avoid replacing the cache with a non-merged `enrich` result alone.
       */
      const keyIds = collectCSAuditDetailQueryKeyIds({
        recordIdForMutation: id,
        detailAuditId,
        updatedRecord: updated ?? undefined,
      });
      if (updated && typeof updated === 'object') {
        for (const qid of keyIds) {
          const qk = cyberSweepKeys.csAudit(qid);
          const prev = queryClient.getQueryData(qk);
          const merged = mergeGobodoDetailRecord(prev, updated as Record<string, unknown>);
          const [enriched] = await enrichCSAuditsWithTemplateInfo([merged as Record<string, unknown>]);
          queryClient.setQueryData(qk, enriched ?? merged);
        }
      } else {
        for (const qid of keyIds) {
          await queryClient.invalidateQueries({ queryKey: cyberSweepKeys.csAudit(qid) });
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['cybersweep', 'cs_audits'] });
      await queryClient.invalidateQueries({ queryKey: ['cybersweep', 'board-column'] });
      await queryClient.invalidateQueries({ queryKey: ['cybersweep', 'my_work_cs_audits'] });
    },
  });
}

export function useCreatePAWRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => createRecord(PAWRECORD_OBJECT, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cybersweep', 'paw_records'] });
    },
  });
}

/** Up to this many PAW rows per `createObjects` request; use {@link PAW_RECORD_CREATE_CHUNK_CONCURRENCY} for parallel chunk uploads. */
export const PAW_RECORD_CREATE_BATCH_SIZE = 10;

/** Parallel in-flight `createObjects` calls for checklist bulk PAW creates. */
/** Reduced from 8 to 2 to prevent 504 gateway timeouts on staging */
export const PAW_RECORD_CREATE_CHUNK_CONCURRENCY = 2;

/**
 * Append newly created PAW rows to the audit-scoped query cache immediately after each chunk.
 * Avoids per-chunk `invalidateQueries` (slow traversal + enrichment, often batched by the client).
 * Final `onSuccess` still invalidates to reconcile with the server.
 */
export function mergeCreatedPawRecordsIntoQueryCache(
  queryClient: QueryClient,
  csAuditId: string | undefined,
  createdChunk: Record<string, unknown>[]
): void {
  if (!csAuditId || createdChunk.length === 0) return;
  const queryKey = cyberSweepKeys.pawRecords(csAuditId);
  queryClient.setQueryData<Record<string, unknown>[]>(queryKey, (prev) => {
    const existing = prev ?? [];
    const seen = new Set(
      existing.map((r) => String(r.key ?? r.id ?? '')).filter((id) => id.length > 0)
    );
    const merged = [...existing];
    for (const raw of createdChunk) {
      const row: Record<string, unknown> = { ...raw, ...buildPAWWebsiteDisplayFields(raw) };
      const id = String(row.key ?? row.id ?? '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      merged.push(row);
    }
    return merged;
  });
}

export function useCreatePAWRecordsBatch(
  csAuditId?: string,
  callbacks?: {
    onBatchProgress?: (completed: number, total: number) => void;
  }
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: Record<string, unknown>[]) =>
      createRecordsBatch(PAWRECORD_OBJECT, items as Record<string, any>[], {
        chunkSize: PAW_RECORD_CREATE_BATCH_SIZE,
        chunkConcurrency: PAW_RECORD_CREATE_CHUNK_CONCURRENCY,
        onChunkComplete: async (records) => {
          mergeCreatedPawRecordsIntoQueryCache(queryClient, csAuditId, records as Record<string, unknown>[]);
        },
        onBatchProgress: callbacks?.onBatchProgress,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cybersweep', 'paw_records'] });
    },
  });
}

export function useUpdatePAWRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, unknown> }) => {
      console.log('[useUpdatePAWRecord] Calling updateRecord with:', { id, updates });
      const result = await updateRecord(PAWRECORD_OBJECT, id, updates, { gobodoPropertyPatch: true });
      console.log('[useUpdatePAWRecord] updateRecord returned:', result);
      return result;
    },
    onSuccess: (data, { id }) => {
      console.log('[useUpdatePAWRecord] onSuccess - invalidating queries for id:', id);
      console.log('[useUpdatePAWRecord] Updated record data:', data);
      queryClient.invalidateQueries({ queryKey: ['cybersweep', 'paw_records'] });
      queryClient.invalidateQueries({ queryKey: ['cybersweep', 'paw_record', id] });
    },
    onError: (error, { id, updates }) => {
      console.error('[useUpdatePAWRecord] MUTATION FAILED for id:', id);
      console.error('[useUpdatePAWRecord] Updates that failed:', updates);
      console.error('[useUpdatePAWRecord] Error:', error);
    },
  });
}

/** Gobodo / SF key suffix variants — must align with board navigation and {@link useCyberSweepClient}. */
const CYBERSWEEP_CLIENT_KEY_SUFFIX_RE = /(__stg3_c|__stg5_c|__stg_c|__dev_c|__c)$/i;

/**
 * Merge the response record from `updateRecord` on top of whatever is currently cached so we
 * don't blow away fields the write response didn't include. `updateRecord` round-trips via a
 * `getObjectById` which can return a sparse `data` payload (e.g. only recently-written keys),
 * and a plain `setQueryData(..., data)` would erase untouched fields in the UI until the next
 * full refetch. Merging preserves them while still surfacing newly-saved values.
 * Used for Account (client detail) and CS_Audit (audit detail) in-place UI updates.
 */
function mergeGobodoDetailRecord(
  previous: unknown,
  next: Record<string, unknown>
): Record<string, unknown> {
  if (!previous || typeof previous !== 'object') return { ...next };
  const prev = previous as Record<string, unknown>;
  const merged: Record<string, unknown> = { ...prev, ...next };
  for (const nested of ['data', 'properties', 'fields'] as const) {
    const prevNested = prev[nested];
    const nextNested = next[nested];
    if (prevNested && typeof prevNested === 'object') {
      merged[nested] = {
        ...(prevNested as Record<string, unknown>),
        ...((nextNested && typeof nextNested === 'object'
          ? (nextNested as Record<string, unknown>)
          : {})),
      };
    }
  }
  return merged;
}

/**
 * Segments used in `['cybersweep', 'client', …]` so cache updates hit the active query.
 * Board navigation often passes a stripped SF id while saves use Gobodo numeric `id` or full `key`.
 */
function collectCyberSweepClientDetailQueryKeyIds(params: {
  recordIdForMutation: string;
  /** Value passed to `useCyberSweepClient` on the page (e.g. URL / stripped SF id). */
  detailClientId?: string | null;
  updatedRecord?: unknown;
}): string[] {
  const ids = new Set<string>();
  const push = (v: unknown) => {
    const s = String(v ?? '').trim();
    if (s) ids.add(s);
  };
  push(params.recordIdForMutation);
  push(params.detailClientId);
  const rec = params.updatedRecord as Record<string, unknown> | null | undefined;
  if (rec && typeof rec === 'object') {
    push(rec.id);
    push(rec.key);
    const keyStr = String(rec.key ?? '').trim();
    if (keyStr) {
      const stripped = keyStr.replace(CYBERSWEEP_CLIENT_KEY_SUFFIX_RE, '');
      if (stripped) push(stripped);
    }
  }
  return Array.from(ids);
}

/**
 * True when a Client/Account `updates` payload touches CyberSweep program, identity, or stage fields
 * that drive the board, enrolled list, or CS-audit rollups. Used to avoid refetching those heavy
 * query families after saves that only change contact, address, or last-modified mirrors.
 */
function isCyberSweepClientUpdateWorthBroadInvalidation(updates: Record<string, unknown> | undefined): boolean {
  if (!updates || typeof updates !== 'object') return false;
  for (const key of Object.keys(updates)) {
    const l = key.toLowerCase();
    if (l.includes('lastmodified') || l.includes('last_modified') || l === 'lastmodified' || l === 'lastmodifieddate') {
      continue;
    }
    if (l.includes('lastmodifiedby') || l.includes('last_modified_by')) {
      continue;
    }
    if (
      l.includes('cyber_sweep') ||
      l.includes('cybersweep') ||
      l.includes('cyber_sweep_status') ||
      l.includes('cyber_sweep_stage') ||
      l.includes('cyber_sweep_start') ||
      l.includes('cyber_sweep_end') ||
      l.includes('cybersweepclient')
    ) {
      return true;
    }
    if (l === 'name' || l === 'name__c' || l.includes('firstname') || l.includes('lastname') || l.includes('accountid')) {
      return true;
    }
  }
  return false;
}

/**
 * Update a CyberSweep client record (Account object in Gobodo)
 */
export function useUpdateCyberSweepClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      updates,
      detailClientId,
    }: {
      id: string;
      updates: Record<string, unknown>;
      /** Same id string passed into `useCyberSweepClient` on that screen (often stripped SF id from the URL). */
      detailClientId?: string | null;
    }) => {
      console.group('[useUpdateCyberSweepClient] Mutation');
      console.log('Record ID:', id);
      console.log('Updates:', updates);
      try {
        const result = await updateRecord('Account', id, updates, { gobodoPropertyPatch: true });
        console.log('Update result:', result);
        console.groupEnd();
        return result;
      } catch (error) {
        console.error('Update FAILED:', error);
        console.groupEnd();
        throw error;
      }
    },
    onSuccess: (data, { id, updates, detailClientId }) => {
      const clientKeyIds = collectCyberSweepClientDetailQueryKeyIds({
        recordIdForMutation: id,
        detailClientId,
        updatedRecord: data ?? undefined,
      });
      console.log('[useUpdateCyberSweepClient] onSuccess — syncing client query cache for keys:', clientKeyIds);
      console.log('[useUpdateCyberSweepClient] Updated data returned:', data);
      // `useCyberSweepClient` key is often the URL stripped SF id while saves use Gobodo numeric id — update every alias.
      if (data != null && typeof data === 'object') {
        const nextRecord = data as Record<string, unknown>;
        for (const qid of clientKeyIds) {
          queryClient.setQueryData(['cybersweep', 'client', qid], (prev) =>
            mergeGobodoDetailRecord(prev, nextRecord)
          );
        }
      } else {
        for (const qid of clientKeyIds) {
          void queryClient.invalidateQueries({ queryKey: ['cybersweep', 'client', qid] });
        }
      }
      const broad = isCyberSweepClientUpdateWorthBroadInvalidation(updates);
      if (broad) {
        // List + board + all cs_audits are expensive (many network calls if we refetch now). Mark
        // stale so the next time those views are shown they fetch fresh; avoid refetching every
        // column query + list + audit hooks while the user is still on client detail.
        const staleOnly = { refetchType: 'none' as const };
        queryClient.invalidateQueries({ queryKey: ['cybersweep', 'clients'], ...staleOnly });
        queryClient.invalidateQueries({ queryKey: ['cybersweep', 'cs_audits'], ...staleOnly });
        queryClient.invalidateQueries({ queryKey: ['cybersweep', 'board-column'], ...staleOnly });
      } else if (process.env.NODE_ENV === 'development') {
        console.log(
          '[useUpdateCyberSweepClient] Skipping clients / cs_audits / board-column (not program board–relevant field set)'
        );
      }
    },
    onError: (error, { id, updates }) => {
      console.error('[useUpdateCyberSweepClient] onError:', error);
      console.error('Failed to update record:', id, 'with updates:', updates);
    },
  });
}

// ============================================
// WEBSITE CONFIGURATION QUERIES (Gobodo/RDS)
// ============================================

// Object key in Gobodo is truncated to 'Audit_Website_Configur' (not full 'Audit_Website_Configuration')
// The normalizer will append the correct suffix (e.g., __stg5_c) based on REACT_APP_GOBODO_CUSTOM_SUFFIX
const WEBSITE_CONFIG_OBJECT = 'Audit_Website_Configur';

// Website Category object for resolving category lookup IDs to names
const WEBSITE_CATEGORY_OBJECT = 'Website_Category__c';

export const websiteConfigKeys = {
  all: () => ['cybersweep', 'website_configs'] as const,
  detail: (id: string) => ['cybersweep', 'website_config', id] as const,
  forAuditWebsite: (auditWebsiteId: string, fallbackConfigId?: string) =>
    ['cybersweep', 'audit_website', auditWebsiteId, 'website_config', fallbackConfigId ?? ''] as const,
  relatedAuditWebsites: (configId: string) => ['cybersweep', 'website_config', configId, 'audit_websites'] as const,
  relatedPAWs: (configId: string) => ['cybersweep', 'website_config', configId, 'paws'] as const,
};

/**
 * Map Gobodo/RDS record to WebsiteConfiguration display format
 */
// PII Types that websites may collect
export type PIIType = 'DOB' | 'Phone' | 'Email' | 'Address' | 'SSN' | 'Photos' | 'Employment';

export interface GobodoWebsiteConfig {
  id: string;
  key?: string;
  name: string;
  url: string;
  optOutUrl: string;
  optOutEmail: string;
  optOutMethod: string;
  notes: string;
  requirements: string;
  websiteCategory: string;
  websiteCategoryId: string;
  autoEmail: boolean;
  automatedWebsite: boolean;
  isActive: boolean;
  piiTypes: PIIType[];
  ownerId: string;
  ownerName: string;
  createdById: string;
  createdByName: string;
  createdDate: string;
  lastModifiedById: string;
  lastModifiedByName: string;
  lastModifiedDate: string;
}

function mapGobodoToWebsiteConfig(r: Record<string, unknown>): GobodoWebsiteConfig {
  const id = String(r.id ?? r.key ?? r.Id ?? '');

  // Parse PII types from Gobodo field (stored as semicolon-separated string or array)
  const parsePiiTypes = (value: unknown): PIIType[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value as PIIType[];
    if (typeof value === 'string') {
      return value.split(';').map(s => s.trim()).filter(Boolean) as PIIType[];
    }
    return [];
  };

  // Parse date from either ISO string or Unix timestamp (milliseconds)
  const parseDate = (value: unknown): string => {
    if (!value) return '';
    // If it's already an ISO string, return it
    if (typeof value === 'string' && value.includes('T')) {
      return value;
    }
    // If it's a number (Unix timestamp in ms), convert to ISO string
    if (typeof value === 'number') {
      return new Date(value).toISOString();
    }
    // If it's a numeric string (Unix timestamp), convert to ISO string
    if (typeof value === 'string' && /^\d+$/.test(value)) {
      return new Date(parseInt(value, 10)).toISOString();
    }
    return String(value);
  };

  return {
    id,
    key: String(r.key ?? r.id ?? ''),
    name: String(r.Name ?? r.name ?? r.Name__c ?? ''),
    url: String(r.URL__c ?? r.url ?? ''),
    optOutUrl: String(r.Opt_Out_URL__c ?? r.optOutUrl ?? ''),
    optOutEmail: String(r.Opt_Out_Email__c ?? r.optOutEmail ?? ''),
    optOutMethod: String(r.Opt_Out_Method__c ?? r.optOutMethod ?? ''),
    notes: String(r.Notes__c ?? r.notes ?? ''),
    requirements: String(r.Requirements__c ?? r.requirements ?? ''),
    websiteCategory: String(r.Website_Category__c ?? r.websiteCategory ?? ''),
    websiteCategoryId: String(r.Website_Category__c ?? r.websiteCategoryId ?? ''),
    autoEmail: Boolean(r.Auto_Email__c ?? r.autoEmail ?? false),
    automatedWebsite: Boolean(r.Automated_Website__c ?? r.automatedWebsite ?? false),
    isActive: Boolean(r.Is_Active__c ?? r.isActive ?? true),
    piiTypes: parsePiiTypes(r.PII_Types__c ?? r.piiTypes ?? r.pii_types),
    ownerId: String(r.OwnerId ?? r.OwnerId__c ?? r.ownerId ?? ''),
    ownerName: String(r.OwnerName ?? r.Owner_Name__c ?? r.ownerName ?? r.owner_name ?? ''),
    createdById: String(r.CreatedById ?? r.CreatedById__c ?? r.createdBy ?? r.createdById ?? ''),
    createdByName: String(r.CreatedByName ?? r.Created_By_Name__c ?? r.createdByName ?? r.created_by_name ?? ''),
    // Prefer ISO date strings, but handle Unix timestamps from __c fields
    createdDate: parseDate(r.createdDate ?? r.CreatedDate ?? r.CreatedDate__c ?? ''),
    lastModifiedById: String(r.LastModifiedById ?? r.LastModifiedById__c ?? r.lastModifiedBy ?? r.lastModifiedById ?? ''),
    lastModifiedByName: String(r.LastModifiedByName ?? r.Last_Modified_By_Name__c ?? r.lastModifiedByName ?? r.last_modified_by_name ?? ''),
    // Prefer ISO date strings, but handle Unix timestamps from __c fields
    lastModifiedDate: parseDate(r.lastModified ?? r.LastModifiedDate ?? r.LastModifiedDate__c ?? ''),
  };
}

async function fetchWebsiteConfigurationByReference(websiteId: string): Promise<GobodoWebsiteConfig | null> {
  if (!websiteId || !USE_GOBODO_API) return null;

  console.log('[useWebsiteConfiguration] Fetching from Gobodo:', websiteId);

  try {
    const record = await getGobodoRecordByKey(websiteId);
    if (record) {
      console.log('[useWebsiteConfiguration] Found by key:', record);
      return mapGobodoToWebsiteConfig(record);
    }
  } catch {
    console.log('[useWebsiteConfiguration] Key lookup failed, trying by ID');
  }

  try {
    const record = await getGobodoRecordById(websiteId);
    if (record) {
      console.log('[useWebsiteConfiguration] Found by ID:', record);
      return mapGobodoToWebsiteConfig(record);
    }
  } catch {
    console.log('[useWebsiteConfiguration] ID lookup failed');
  }

  return null;
}

/**
 * Resolve user IDs to names using the existing gobodoUserSfIdLookup service
 * This uses the same approach as CSAudits and Audit Templates
 */
async function resolveUserNamesForWebsiteConfig(
  ownerId: string,
  createdById: string,
  lastModifiedById: string
): Promise<{ ownerName: string; createdByName: string; lastModifiedByName: string }> {
  const idsToResolve = [ownerId, createdById, lastModifiedById].filter(Boolean);

  if (idsToResolve.length === 0) {
    return { ownerName: '', createdByName: '', lastModifiedByName: '' };
  }

  try {
    const userNameMap = await resolveGraphUserNamesBySfIds(idsToResolve);

    return {
      ownerName: ownerId ? (userNameMap.get(ownerId) || '') : '',
      createdByName: createdById ? (userNameMap.get(createdById) || '') : '',
      lastModifiedByName: lastModifiedById ? (userNameMap.get(lastModifiedById) || '') : '',
    };
  } catch (error) {
    console.warn('[resolveUserNamesForWebsiteConfig] Failed to resolve user names:', error);
    return { ownerName: '', createdByName: '', lastModifiedByName: '' };
  }
}

/**
 * Build a map of Website Category SF ID -> Category Name
 * Used to resolve lookup field values to display names
 */
async function fetchWebsiteCategoryMap(): Promise<Map<string, string>> {
  const categoryMap = new Map<string, string>();

  try {
    const categories = await getRecordsForObject(WEBSITE_CATEGORY_OBJECT, { size: 200 });

    for (const cat of categories as Record<string, unknown>[]) {
      // Try multiple possible field names for the SF ID and name
      const sfId = String(cat.sf_id ?? cat.Sf_Id__c ?? cat.key ?? cat.Id ?? cat.id ?? '');
      const name = String(cat.Name__c ?? cat.name ?? cat.Name ?? cat.label ?? '');

      if (sfId && name) {
        // Map by SF ID (the lookup value stored in Website_Category__c field)
        categoryMap.set(sfId, name);

        // Also try mapping with/without suffix variations
        if (sfId.includes('__')) {
          const baseSfId = sfId.split('__')[0];
          categoryMap.set(baseSfId, name);
        }

        // Map by key if different
        const fullKey = String(cat.key ?? '');
        if (fullKey && fullKey !== sfId) {
          categoryMap.set(fullKey, name);
          if (fullKey.includes('__')) {
            const baseKey = fullKey.split('__')[0];
            categoryMap.set(baseKey, name);
          }
        }
      }
    }
  } catch (error) {
    console.warn('[fetchWebsiteCategoryMap] Failed to fetch categories:', error);
  }

  return categoryMap;
}

/**
 * Fetch all website configurations from Gobodo/RDS
 */
export function useWebsiteConfigurations() {
  return useQuery({
    queryKey: websiteConfigKeys.all(),
    queryFn: async () => {
      if (!USE_GOBODO_API) {
        return [];
      }

      // Fetch both website configs and categories in parallel
      const [records, categoryMap] = await Promise.all([
        getRecordsForObject(WEBSITE_CONFIG_OBJECT, { size: 500 }),
        fetchWebsiteCategoryMap(),
      ]);

      // Map records and resolve category names
      return (records as Record<string, unknown>[]).map(r => {
        const config = mapGobodoToWebsiteConfig(r);

        // Resolve category ID to name if we have a mapping
        if (config.websiteCategoryId && categoryMap.size > 0) {
          let categoryName = categoryMap.get(config.websiteCategoryId);

          // If not found, try without any suffix (the raw SF ID)
          if (!categoryName && config.websiteCategoryId.includes('__')) {
            const baseCategoryId = config.websiteCategoryId.split('__')[0];
            categoryName = categoryMap.get(baseCategoryId);
          }

          if (categoryName) {
            config.websiteCategory = categoryName;
          }
        }

        return config;
      });
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Fetch a single website configuration by ID from Gobodo/RDS
 */
export function useWebsiteConfiguration(websiteId: string | null) {
  return useQuery({
    queryKey: websiteConfigKeys.detail(websiteId ?? ''),
    queryFn: async () => {
      if (!websiteId) return null;

      if (!USE_GOBODO_API) {
        return null;
      }

      // Fetch config and category map in parallel
      const [config, categoryMap] = await Promise.all([
        fetchWebsiteConfigurationByReference(websiteId),
        fetchWebsiteCategoryMap(),
      ]);

      if (!config) return null;

      // Resolve category ID to name
      if (config.websiteCategoryId && categoryMap.size > 0) {
        let categoryName = categoryMap.get(config.websiteCategoryId);
        if (!categoryName && config.websiteCategoryId.includes('__')) {
          const baseCategoryId = config.websiteCategoryId.split('__')[0];
          categoryName = categoryMap.get(baseCategoryId);
        }
        if (categoryName) {
          config.websiteCategory = categoryName;
        }
      }

      // Resolve user IDs to names using the existing gobodoUserSfIdLookup service
      // This uses the same approach as CSAudits and Audit Templates
      const userNames = await resolveUserNamesForWebsiteConfig(
        config.ownerId,
        config.createdById,
        config.lastModifiedById
      );

      if (userNames.ownerName) {
        config.ownerName = userNames.ownerName;
      }
      if (userNames.createdByName) {
        config.createdByName = userNames.createdByName;
      }
      if (userNames.lastModifiedByName) {
        config.lastModifiedByName = userNames.lastModifiedByName;
      }

      return config;
    },
    enabled: !!websiteId && USE_GOBODO_API,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAuditWebsiteConfiguration(auditWebsiteId: string | null, fallbackConfigId?: string | null) {
  return useQuery({
    queryKey: websiteConfigKeys.forAuditWebsite(auditWebsiteId ?? '', fallbackConfigId ?? undefined),
    queryFn: async () => {
      if (!auditWebsiteId) return null;
      if (!USE_GOBODO_API) return null;

      let auditWebsiteKey = auditWebsiteId;
      try {
        const auditWebsiteRecord = /^\d+$/.test(auditWebsiteId)
          ? await getRecordById(AUDIT_WEBSITE_OBJECT, auditWebsiteId)
          : await getGobodoRecordByKey(auditWebsiteId);
        if (auditWebsiteRecord && (auditWebsiteRecord as Record<string, unknown>).key) {
          auditWebsiteKey = String((auditWebsiteRecord as Record<string, unknown>).key);
        }
      } catch {
        // fall back to provided reference
      }

      auditWebsiteKey = ensureGobodoCustomSuffixOnRecordKey(auditWebsiteKey);
      const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
      const input = new GobodoApi.GraphTraversalInput();
      input.setGraphId(GobodoConfig.graph());
      input.setStartObjectKey(auditWebsiteKey);
      input.setRelationshipTypes([AUDIT_WEBSITE_CONFIGURATION_REL_KEY]);
      input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.OUTGOING);
      input.setEndObjectTypes([normalizeToGobodoFormat(WEBSITE_CONFIG_OBJECT)]);
      input.setMaxPathLength(1);
      input.setCount(1);

      try {
        const result = await retryWithBackoff(
          () => traversalApi.traverseObjects(input),
          { maxRetries: 2, initialDelayMs: 1000 }
        );
        const objects = requireCyberSweepTraverseObjectsArray(result);
        const first = objects[0] as Record<string, unknown> | undefined;
        if (first) {
          return mapGobodoToWebsiteConfig({
            ...convertFromGobodoRecord(first, WEBSITE_CONFIG_OBJECT),
            id: first.id,
            key: first.key,
            label: first.label,
          });
        }
      } catch (err) {
        console.warn('[useAuditWebsiteConfiguration] Traversal failed, falling back to direct lookup:', err);
      }

      if (fallbackConfigId) {
        return fetchWebsiteConfigurationByReference(fallbackConfigId);
      }
      return null;
    },
    enabled: !!auditWebsiteId && USE_GOBODO_API,
    staleTime: 5 * 60 * 1000,
  });
}

type WebsiteConfigurationTraversalContext = {
  configurationKey: string;
  configurationRefValues: string[];
};

async function resolveWebsiteConfigurationTraversalContext(
  configurationId: string
): Promise<WebsiteConfigurationTraversalContext> {
  let configurationKey = configurationId;
  let configurationSfId: string | undefined;
  try {
    const configurationRecord = /^\d+$/.test(configurationId)
      ? await getRecordById(WEBSITE_CONFIG_OBJECT, configurationId)
      : await getGobodoRecordByKey(configurationId);
    if (configurationRecord) {
      configurationKey = String((configurationRecord as Record<string, unknown>).key ?? configurationKey);
      configurationSfId = String(
        (configurationRecord as Record<string, unknown>).sf_id ??
        (configurationRecord as Record<string, unknown>).sfId ??
        (configurationRecord as Record<string, unknown>).Id ??
        ''
      );
    }
  } catch (err) {
    console.warn('[WebsiteConfigurationTraversal] Could not resolve configuration record:', err);
  }

  configurationKey = ensureGobodoCustomSuffixOnRecordKey(configurationKey);
  return {
    configurationKey,
    configurationRefValues: uniqueLookupTokens([
      configurationId,
      configurationKey,
      stripKnownSuffix(configurationKey),
      configurationSfId,
    ]),
  };
}

function buildAuditWebsitesByConfigurationTraversalInput(
  graphId: number,
  context: WebsiteConfigurationTraversalContext,
  pagination?: { startIndex: number; count: number }
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const auditWebsiteObject = normalizeToGobodoFormat(AUDIT_WEBSITE_OBJECT);
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(graphId);
  input.setStartObjectKey(context.configurationKey);
  input.setRelationshipTypes([AUDIT_WEBSITE_CONFIGURATION_REL_KEY]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
  input.setEndObjectTypes([auditWebsiteObject]);
  input.setMaxPathLength(1);
  if (pagination) {
    input.setStartIndex(pagination.startIndex);
    input.setCount(pagination.count);
  }

  const fieldIndexes = [
    'Website_Configuration__c',
    'websiteConfigurationId',
    AUDIT_WEBSITE_CONFIGURATION_REL_FIELD,
    'WebsiteConfigurationRelField__c',
    'custom__website_configuration',
  ];
  const branches = fieldIndexes.flatMap((indexName) => {
    try {
      const filter = new GobodoApi.IndexFilter();
      filter.setIndexName(indexName);
      filter.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
      filter.setValues(context.configurationRefValues);

      const branch = new GobodoApi.GraphTraversalInput();
      branch.setGraphId(graphId);
      branch.setStartObjectTypes([auditWebsiteObject]);
      branch.setEndObjectTypes([auditWebsiteObject]);
      branch.setMaxPathLength(0);
      if (pagination) {
        branch.setStartIndex(pagination.startIndex);
        branch.setCount(pagination.count);
      }
      branch.setStartObjectFieldFilters([filter]);
      return [branch];
    } catch {
      return [];
    }
  });
  chainUnionTraversals(input, branches);
  return input;
}

function auditWebsiteConfigurationTraversalCacheOpts(configurationId: string, startIndex: number, count: number): GobodoCacheOpts {
  return makeCacheOpts(
    [
      'cybersweep',
      'audit-websites-by-configuration',
      'traverse',
      configurationId,
      String(GobodoConfig.graph()),
      String(startIndex),
      String(count),
      AUDIT_WEBSITE_CONFIGURATION_REL_KEY,
      'audit-website-configuration-traversal-v1',
    ],
    CACHE_TTL.SHORT
  );
}

function auditWebsiteConfigurationTraversalCountCacheOpts(configurationId: string): GobodoCacheOpts {
  return makeCacheOpts(
    [
      'cybersweep',
      'audit-websites-by-configuration',
      'traverse-count',
      configurationId,
      String(GobodoConfig.graph()),
      AUDIT_WEBSITE_CONFIGURATION_REL_KEY,
      'audit-website-configuration-traversal-v1',
    ],
    CACHE_TTL.SHORT
  );
}

export async function fetchAuditWebsitesByConfigurationTraversalPage(
  configurationId: string,
  startIndex: number,
  count: number
): Promise<AuditWebsiteTemplateTraversalPageFetch> {
  const graphId = GobodoConfig.graph();
  const context = await resolveWebsiteConfigurationTraversalContext(configurationId);
  const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
  const input = buildAuditWebsitesByConfigurationTraversalInput(graphId, context, { startIndex, count });
  const cacheOpts = auditWebsiteConfigurationTraversalCacheOpts(configurationId, startIndex, count);
  const result = await retryWithBackoff(
    () => traversalApi.traverseObjects(input, cacheOpts),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  const objects = requireCyberSweepTraverseObjectsArray(result);
  return {
    records: mapAuditWebsiteTraversalObjects(objects),
    rawObjectCount: objects.length,
  };
}

export async function fetchAuditWebsiteConfigurationTraversalCount(configurationId: string): Promise<number> {
  const graphId = GobodoConfig.graph();
  const context = await resolveWebsiteConfigurationTraversalContext(configurationId);
  const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
  const input = buildAuditWebsitesByConfigurationTraversalInput(graphId, context);
  const cacheOpts = auditWebsiteConfigurationTraversalCountCacheOpts(configurationId);
  const countRaw = await retryWithBackoff(
    () => traversalApi.traverseObjectsCount(input, cacheOpts),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  return parseCyberSweepBoardTraversalCount(countRaw);
}

export async function fetchAllAuditWebsitesForConfiguration(configurationId: string): Promise<AuditWebsiteRecord[]> {
  const acc: AuditWebsiteRecord[] = [];
  const seen = new Set<string>();
  let serverTotalCount = 0;
  try {
    serverTotalCount = await fetchAuditWebsiteConfigurationTraversalCount(configurationId);
  } catch {
    serverTotalCount = 0;
  }

  let startIndex = 0;
  for (let page = 0; page < CYBERSWEEP_LIST_MAX_PAGES; page++) {
    const { records, rawObjectCount } = await fetchAuditWebsitesByConfigurationTraversalPage(
      configurationId,
      startIndex,
      CYBERSWEEP_LIST_PAGE_SIZE
    );
    if (rawObjectCount === 0) break;
    for (const row of records) {
      const dedupeKey = String(row.id ?? row.key ?? '');
      if (!dedupeKey || seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      acc.push(row);
    }
    startIndex += rawObjectCount;
    if (rawObjectCount < CYBERSWEEP_LIST_PAGE_SIZE) break;
    if (serverTotalCount > 0 && startIndex >= serverTotalCount) break;
  }
  return acc;
}

export function useAuditWebsitesByConfiguration(configurationId: string | null) {
  return useQuery({
    queryKey: websiteConfigKeys.relatedAuditWebsites(configurationId ?? ''),
    queryFn: async (): Promise<AuditWebsiteRecord[]> => {
      if (!configurationId || !USE_GOBODO_API) return [];
      return fetchAllAuditWebsitesForConfiguration(configurationId);
    },
    enabled: !!configurationId && USE_GOBODO_API,
    staleTime: 5 * 60 * 1000,
  });
}

function buildPAWRecordsByConfigurationTraversalInput(
  graphId: number,
  context: WebsiteConfigurationTraversalContext,
  auditWebsiteRefValues: string[],
  pagination?: { startIndex: number; count: number }
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const pawType = normalizeToGobodoFormat(PAWRECORD_OBJECT);
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(graphId);
  input.setStartObjectKey(context.configurationKey);
  input.setRelationshipTypes([AUDIT_WEBSITE_CONFIGURATION_REL_KEY, getPAWAuditWebsiteRelKey()]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
  input.setEndObjectTypes([pawType]);
  input.setMaxPathLength(2);
  if (pagination) {
    input.setStartIndex(pagination.startIndex);
    input.setCount(pagination.count);
  }

  if (auditWebsiteRefValues.length > 0) {
    const pawFieldIndexes = [
      'Audit_Website__c',
      'auditWebsiteId',
      'AuditWebsiteRelField__c',
      'auditWebsiteRelField',
      'custom__audit_website',
    ];
    const branches = pawFieldIndexes.flatMap((indexName) => {
      try {
        const filter = new GobodoApi.IndexFilter();
        filter.setIndexName(indexName);
        filter.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
        filter.setValues(auditWebsiteRefValues);

        const branch = new GobodoApi.GraphTraversalInput();
        branch.setGraphId(graphId);
        branch.setStartObjectTypes([pawType]);
        branch.setEndObjectTypes([pawType]);
        branch.setMaxPathLength(0);
        if (pagination) {
          branch.setStartIndex(pagination.startIndex);
          branch.setCount(pagination.count);
        }
        branch.setStartObjectFieldFilters([filter]);
        return [branch];
      } catch {
        return [];
      }
    });
    chainUnionTraversals(input, branches);
  }

  return input;
}

function pawRecordsByConfigurationTraversalCacheOpts(configurationId: string, startIndex: number, count: number): GobodoCacheOpts {
  return makeCacheOpts(
    [
      'cybersweep',
      'paws-by-configuration',
      'traverse',
      configurationId,
      String(GobodoConfig.graph()),
      String(startIndex),
      String(count),
      AUDIT_WEBSITE_CONFIGURATION_REL_KEY,
      getPAWAuditWebsiteRelKey(),
      'paws-by-configuration-traversal-v1',
    ],
    CACHE_TTL.SHORT
  );
}

export async function fetchPAWRecordsByConfigurationTraversalPage(
  configurationId: string,
  startIndex: number,
  count: number,
  options?: { dataFields?: string; cacheKeyTag?: string }
): Promise<CyberSweepPAWTraversalPageFetch> {
  const graphId = GobodoConfig.graph();
  const context = await resolveWebsiteConfigurationTraversalContext(configurationId);
  const auditWebsites = await fetchAllAuditWebsitesForConfiguration(configurationId);
  const auditWebsiteRefValues = uniqueLookupTokens(
    auditWebsites.flatMap((website) => [
      website.id,
      website.key,
    ])
  );
  const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
  const input = buildPAWRecordsByConfigurationTraversalInput(
    graphId,
    context,
    auditWebsiteRefValues,
    { startIndex, count }
  );
  const cacheOpts = pawRecordsByConfigurationTraversalCacheOpts(configurationId, startIndex, count);
  const projectedDataFields = options?.dataFields?.trim() ?? '';
  const readOpts = projectedDataFields
    ? buildTraversalProjectionOpts(
        {
          ...cacheOpts,
          cachekey: [...cacheOpts.cachekey, options?.cacheKeyTag ?? 'projected'],
        },
        projectedDataFields
      )
    : cacheOpts;
  const result = await retryWithBackoff(
    () => traversalApi.traverseObjects(input, readOpts),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  const objects = requireCyberSweepTraverseObjectsArray(result);
  const rawObjectCount = objects.length;
  const out: Record<string, unknown>[] = [];
  const seen = new Set<string>();
  for (const gobodoObj of objects) {
    try {
      const converted = convertFromGobodoRecord(
        gobodoObj as Record<string, unknown>,
        PAWRECORD_OBJECT
      ) as Record<string, unknown>;
      const row: Record<string, unknown> = {
        ...converted,
        key: (gobodoObj as Record<string, unknown>).key ?? converted.key,
        label: (gobodoObj as Record<string, unknown>).label ?? converted.label,
      };
      const dedupeKey = String(row.id ?? row.key ?? '');
      if (!dedupeKey || seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      out.push(row);
    } catch {
      /* skip malformed row */
    }
  }
  const enriched = await enrichPAWRecordsWithWebsiteInfo(out);
  return { records: enriched, rawObjectCount };
}

export async function fetchAllPAWRecordsForConfiguration(configurationId: string): Promise<Record<string, unknown>[]> {
  const acc: Record<string, unknown>[] = [];
  const seen = new Set<string>();
  let startIndex = 0;

  for (let page = 0; page < CYBERSWEEP_PAW_TRAVERSAL_MAX_PAGES; page++) {
    const { records, rawObjectCount } = await fetchPAWRecordsByConfigurationTraversalPage(
      configurationId,
      startIndex,
      CYBERSWEEP_PAW_TRAVERSAL_PAGE_SIZE
    );
    if (rawObjectCount === 0) break;
    for (const row of records) {
      const dedupeKey = String(row.id ?? row.key ?? '');
      if (!dedupeKey || seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      acc.push(row);
    }
    startIndex += rawObjectCount;
    if (rawObjectCount < CYBERSWEEP_PAW_TRAVERSAL_PAGE_SIZE) break;
  }
  return acc;
}

/**
 * Fetch PAW records that match a specific website configuration by name.
 * Uses a simpler approach: fetch recent PAW records and filter by website name.
 */
export async function fetchPAWRecordsForWebsiteByName(
  websiteName: string,
  limit: number = 6
): Promise<PAWRecordDetail[]> {
  if (!USE_GOBODO_API || !websiteName) return [];

  try {
    console.log('[fetchPAWRecordsForWebsiteByName] Fetching PAWs for website:', websiteName);

    const pawObject = normalizeToGobodoFormat(PAWRECORD_OBJECT);

    // Fetch PAW records (get more than limit to allow for filtering)
    const allPawRecords = await getRecordsForObject(pawObject, { size: 500 }) as Record<string, unknown>[];
    console.log('[fetchPAWRecordsForWebsiteByName] Total PAW records fetched:', allPawRecords.length);

    // Normalize the search name for matching
    const searchName = websiteName.toLowerCase().trim();
    const searchNameNoExt = searchName.replace(/\.(com|net|org|io|co|us|info|biz)$/i, '');

    // Filter PAW records by website name
    const matchingRecords = allPawRecords.filter((paw) => {
      // Check multiple website-related fields
      const pawWebsite = String(
        paw.websiteDisplayName ??
        paw.Website__c ??
        paw.website ??
        paw.Website_Name__c ??
        ''
      ).toLowerCase().trim();

      if (!pawWebsite) return false;

      const pawWebsiteNoExt = pawWebsite.replace(/\.(com|net|org|io|co|us|info|biz)$/i, '');

      // Match: exact, contains, or without extension
      return (
        pawWebsite === searchName ||
        pawWebsite.includes(searchName) ||
        searchName.includes(pawWebsite) ||
        pawWebsiteNoExt === searchNameNoExt ||
        pawWebsiteNoExt.includes(searchNameNoExt) ||
        searchNameNoExt.includes(pawWebsiteNoExt)
      );
    });

    console.log('[fetchPAWRecordsForWebsiteByName] Matching PAW records:', matchingRecords.length);

    // Map to PAWRecordDetail with proper IDs
    const result: PAWRecordDetail[] = [];
    const seen = new Set<string>();
    const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const GOBODO_PLACEHOLDER_REGEX = /^PAW-\d{10,}-\d+-[a-z0-9]+$/i;

    for (const paw of matchingRecords) {
      if (result.length >= limit) break;

      // Get the numeric ID (preferred for navigation)
      const rawId = paw.id;
      const rawKey = paw.key;

      let numericId: string | undefined;
      const possibleNumericIds = [
        paw.id,
        paw.gobodoId,
        paw.recordId,
      ].map(v => String(v ?? '')).filter(v => /^\d+$/.test(v));

      if (possibleNumericIds.length > 0) {
        numericId = possibleNumericIds[0];
      }

      // Determine best ID for navigation
      let navId: string;
      if (numericId) {
        navId = numericId;
      } else if (rawKey) {
        navId = String(rawKey);
        if (/^\d+$/.test(navId)) {
          numericId = navId;
        }
      } else {
        const idStr = String(rawId ?? '');
        if (idStr) {
          navId = idStr;
        } else {
          continue;
        }
      }

      if (!navId || seen.has(navId)) continue;
      seen.add(navId);

      // Get display name - prefer label (SF auto-number), fallback to generated
      let pawName = String(paw.label ?? paw.Name__c ?? paw.name ?? paw.Name ?? '');

      const isPlaceholderName = (
        !pawName ||
        UUID_REGEX.test(pawName) ||
        GOBODO_PLACEHOLDER_REGEX.test(pawName) ||
        pawName.startsWith('PAW-') && pawName.length > 20
      );

      if (isPlaceholderName && numericId) {
        pawName = `PAW-${numericId.slice(-5).padStart(5, '0')}`;
      }

      result.push({
        id: navId,
        key: String(rawKey ?? navId),
        sfId: String(paw.sf_id ?? paw.sfId ?? ''),
        name: pawName,
        auditPhase: String(paw.Audit_Phase__c ?? paw.auditPhase ?? ''),
        csAuditId: String(paw.CS_Audit__c ?? paw.csAuditId ?? ''),
        csAuditName: String(paw.csAuditName ?? ''),
        auditTemplateName: String(paw.auditTemplateName ?? ''),
        findings: String(paw.Findings__c ?? paw.findings ?? ''),
        method: String(paw.Method__c ?? paw.method ?? ''),
        website: String(paw.Website__c ?? paw.website ?? ''),
        websiteDisplayName: String(paw.websiteDisplayName ?? paw.Website__c ?? ''),
        piiFound: String(paw.PII_Found__c ?? paw.piiFound ?? ''),
        phase2Required: Boolean(paw.Phase_2_Required__c ?? false),
        clientInfoLinks: String(paw.Client_Info_Links__c ?? ''),
        status: String(paw.Status__c ?? paw.status ?? ''),
        ownerId: String(paw.OwnerId__c ?? paw.ownerId ?? ''),
        ownerName: String(paw.ownerName ?? ''),
        createdById: String(paw.CreatedById__c ?? ''),
        createdByName: String(paw.createdByName ?? ''),
        createdDate: String(paw.CreatedDate__c ?? paw.createdDate ?? ''),
        lastModifiedById: String(paw.LastModifiedById__c ?? ''),
        lastModifiedByName: String(paw.lastModifiedByName ?? ''),
        lastModifiedDate: String(paw.LastModifiedDate__c ?? ''),
      });
    }

    console.log('[fetchPAWRecordsForWebsiteByName] Returning', result.length, 'records');
    return result;
  } catch (err) {
    console.error('[fetchPAWRecordsForWebsiteByName] Error:', err);
    return [];
  }
}

export function usePAWRecordsByConfiguration(configurationId: string | null, websiteName?: string | null) {
  // PAW records are fetched via traversal or filtered by website name
  // For static IDs (like 'WC-0000-001'), we filter enriched PAW records by website display name
  return useQuery({
    queryKey: ['cybersweep', 'paw_records_by_configuration', configurationId ?? '', websiteName ?? ''],
    queryFn: async (): Promise<PAWRecordDetail[]> => {
      if (!USE_GOBODO_API || !configurationId) return [];

      let records: Record<string, unknown>[] = [];

      // Check if this is a static ID (e.g., 'WC-0000-001') that needs filtering by website name
      if (configurationId.startsWith('WC-') && websiteName) {
        // This is a static ID - fetch recent PAW records and filter by website name
        try {
          // Fetch recent PAW records (already enriched with website info)
          const pawObject = normalizeToGobodoFormat(PAWRECORD_OBJECT);
          const allPawRecords = await getRecordsForObject(pawObject, { size: 100 }) as Record<string, unknown>[];

          // Enrich records with website display names
          const enrichedRecords = await enrichPAWRecordsWithWebsiteInfo(allPawRecords);

          // Filter by website name matching
          const normalizedName = websiteName.toLowerCase().trim();
          const normalizedNameNoExt = normalizedName.replace(/\.(com|net|org|io|co|us|info|biz)$/i, '');

          records = enrichedRecords.filter((paw) => {
            const pawWebsite = String(paw.websiteDisplayName ?? paw.Website__c ?? paw.website ?? '').toLowerCase().trim();
            const pawUrl = String(paw.websiteUrl ?? paw.Website_URL__c ?? '').toLowerCase().trim();

            if (!pawWebsite && !pawUrl) return false;

            // Match by display name
            if (pawWebsite && (
              pawWebsite === normalizedName ||
              pawWebsite.includes(normalizedName) ||
              normalizedName.includes(pawWebsite)
            )) {
              return true;
            }

            // Match by URL (extract domain)
            if (pawUrl) {
              const urlDomain = pawUrl
                .replace(/^https?:\/\//i, '')
                .replace(/\/.*$/, '')
                .replace(/\.(com|net|org|io|co|us|info|biz)$/i, '');

              if (urlDomain === normalizedNameNoExt || urlDomain.includes(normalizedNameNoExt) || normalizedNameNoExt.includes(urlDomain)) {
                return true;
              }
            }

            return false;
          });
        } catch (err) {
          console.error('[usePAWRecordsByConfiguration] Error filtering PAW records:', err);
          records = [];
        }
      } else {
        // Use the existing traversal-based function for Gobodo IDs
        records = await fetchAllPAWRecordsForConfiguration(configurationId);
      }

      // Convert to PAWRecordDetail format with proper ID handling
      const result: PAWRecordDetail[] = [];
      const seen = new Set<string>();

      for (const r of records) {
        // Get the best ID for navigation
        const rawId = r.id;
        const rawKey = r.key;

        let navId: string;
        const numericIds = [r.id, r.gobodoId, r.recordId]
          .map(v => String(v ?? ''))
          .filter(v => /^\d+$/.test(v));

        if (numericIds.length > 0) {
          navId = numericIds[0];
        } else if (rawKey) {
          navId = String(rawKey);
        } else if (rawId) {
          navId = String(rawId);
        } else {
          continue; // Skip records without valid IDs
        }

        if (!navId || seen.has(navId)) continue;
        seen.add(navId);

        // Get display name - prefer label (SF auto-number)
        let pawName = String(r.label ?? r.Name__c ?? r.name ?? r.Name ?? '');
        const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const PLACEHOLDER_REGEX = /^PAW-\d{10,}-\d+-[a-z0-9]+$/i;

        const isPlaceholder = !pawName || UUID_REGEX.test(pawName) || PLACEHOLDER_REGEX.test(pawName);
        if (isPlaceholder && /^\d+$/.test(navId)) {
          pawName = `PAW-${navId.slice(-5).padStart(5, '0')}`;
        }

        result.push({
          id: navId,
          key: String(rawKey ?? navId),
          sfId: String(r.sf_id ?? r.sfId ?? ''),
          name: pawName,
          auditPhase: String(r.Audit_Phase__c ?? r.auditPhase ?? ''),
          csAuditId: String(r.CS_Audit__c ?? r.csAuditId ?? ''),
          csAuditName: String(r.csAuditName ?? ''),
          auditTemplateName: String(r.auditTemplateName ?? ''),
          findings: String(r.Findings__c ?? r.findings ?? ''),
          method: String(r.Method__c ?? r.method ?? ''),
          website: String(r.Website__c ?? r.website ?? ''),
          websiteDisplayName: String(r.websiteDisplayName ?? r.Website__c ?? ''),
          piiFound: String(r.PII_Found__c ?? r.piiFound ?? ''),
          phase2Required: Boolean(r.Phase_2_Required__c ?? r.phase2Required ?? false),
          clientInfoLinks: String(r.Client_Info_Links__c ?? r.clientInfoLinks ?? ''),
          status: String(r.Status__c ?? r.status ?? ''),
          ownerId: String(r.OwnerId__c ?? r.ownerId ?? ''),
          ownerName: String(r.ownerName ?? ''),
          createdById: String(r.CreatedById__c ?? r.createdById ?? ''),
          createdByName: String(r.createdByName ?? ''),
          createdDate: String(r.CreatedDate__c ?? r.createdDate ?? ''),
          lastModifiedById: String(r.LastModifiedById__c ?? r.lastModifiedById ?? ''),
          lastModifiedByName: String(r.lastModifiedByName ?? ''),
          lastModifiedDate: String(r.LastModifiedDate__c ?? r.lastModifiedDate ?? ''),
        });
      }

      return result;
    },
    enabled: !!configurationId && USE_GOBODO_API,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch a website configuration by name (for modal lookups from PAW records)
 * Uses the all-configs query and filters by name matching with fuzzy logic
 */
export function useWebsiteConfigurationByName(websiteName: string | null) {
  const { data: allConfigs, isLoading, error } = useWebsiteConfigurations();

  const websiteConfig = useMemo(() => {
    if (!websiteName) {
      return null;
    }
    if (!allConfigs || allConfigs.length === 0) {
      return null;
    }

    const searchName = websiteName.toLowerCase().trim();

    // Try exact match first (case-insensitive, trimmed)
    const exactMatch = allConfigs.find(
      (c) => c.name.toLowerCase().trim() === searchName
    );
    if (exactMatch) {
      return exactMatch;
    }

    // Try partial match (name contains or contained by)
    const partialMatch = allConfigs.find((c) => {
      const configName = c.name.toLowerCase().trim();
      return configName.includes(searchName) || searchName.includes(configName);
    });
    if (partialMatch) {
      return partialMatch;
    }

    // Try matching without domain extensions (.com, .net, etc.)
    const searchWithoutExt = searchName.replace(/\.(com|net|org|io|co|us|info|biz)$/i, '');
    const extMatch = allConfigs.find((c) => {
      const configWithoutExt = c.name.toLowerCase().trim().replace(/\.(com|net|org|io|co|us|info|biz)$/i, '');
      return configWithoutExt === searchWithoutExt ||
             configWithoutExt.includes(searchWithoutExt) ||
             searchWithoutExt.includes(configWithoutExt);
    });
    if (extMatch) {
      return extMatch;
    }

    return null;
  }, [websiteName, allConfigs]);

  return {
    data: websiteConfig,
    isLoading,
    error,
  };
}

// ============================================
// AUDIT TEMPLATE QUERIES (Gobodo/RDS)
// ============================================

// Correct object name per backend: Cyber_Sweep_Audit_Temp__stg5_c
const AUDIT_TEMPLATE_GOBODO_OBJECT = 'Cyber_Sweep_Audit_Temp__stg5_c';

/** TanStack: audit templates are reference data; align with server {@link CACHE_TTL.LONG} (~1h). */
const AUDIT_TEMPLATE_QUERY_STALE_MS = 60 * 60 * 1000;
const AUDIT_TEMPLATE_QUERY_GC_MS = 6 * 60 * 60 * 1000;

export const auditTemplateKeys = {
  all: (graphId: number) => ['cybersweep', 'audit_templates', graphId] as const,
  detail: (graphId: number, id: string) => ['cybersweep', 'audit_template', graphId, id] as const,
  relatedAuditWebsites: (graphId: number, templateId: string) =>
    ['cybersweep', 'audit_template', graphId, templateId, 'audit_websites'] as const,
  relatedPAWAuditIds: (graphId: number, templateId: string) =>
    ['cybersweep', 'audit_template', graphId, templateId, 'paw_audit_ids'] as const,
  relatedPAWs: (graphId: number, templateId: string) =>
    ['cybersweep', 'audit_template', graphId, templateId, 'paws'] as const,
};

/**
 * Gobodo Audit Template interface
 */
export interface GobodoAuditTemplate {
  id: string;
  key?: string;
  name: string;
  phase1DueDateDays: number;
  phase2DueDateDays: number;
  phase2Required: boolean;
  checklistType: string;
  ownerId: string;
  ownerName: string;
  createdById: string;
  createdByName: string;
  createdDate: string;
  lastModifiedById: string;
  lastModifiedByName: string;
  lastModifiedDate: string;
}

/**
 * Derive checklistType from template name for website filtering.
 * - "Initial Deletion" → 'initial_deletion'
 * - "Initial Audit", "Final Audit" → 'comprehensive'
 * - "Quarter #X Audit" → 'minimal'
 */
function deriveChecklistTypeFromName(name: string): string {
  const lowerName = name.toLowerCase();
  if (lowerName.includes('initial deletion')) return 'initial_deletion';
  if (lowerName.includes('quarter')) return 'minimal';
  // Default to comprehensive for Initial Audit, Final Audit, etc.
  return 'comprehensive';
}

function mapGobodoToAuditTemplate(r: Record<string, unknown>): GobodoAuditTemplate {
  const data = (r.data as Record<string, unknown>) ?? r;
  const id = String(r.id ?? r.key ?? r.Id ?? data.id ?? data.key ?? data.Id ?? '');
  // Field names per backend: Phase_1_Due_Date_Days_c, Phase_2_Due_Date_Days_c
  // Also check for staging suffix variants and standard __c suffix
  const phase1Days = Number(
    r.Phase_1_Due_Date_Days_c ?? data.Phase_1_Due_Date_Days_c ??
    r.Phase_1_Due_Date_Days__c ?? data.Phase_1_Due_Date_Days__c ??
    r.Phase_1_Due_Date_Days__stg5_c ?? data.Phase_1_Due_Date_Days__stg5_c ??
    r.phase1DueDateDays ?? data.phase1DueDateDays ??
    r.Phase1DueDateDays__c ?? data.Phase1DueDateDays__c ?? 7
  );
  const phase2Days = Number(
    r.Phase_2_Due_Date_Days_c ?? data.Phase_2_Due_Date_Days_c ??
    r.Phase_2_Due_Date_Days__c ?? data.Phase_2_Due_Date_Days__c ??
    r.Phase_2_Due_Date_Days__stg5_c ?? data.Phase_2_Due_Date_Days__stg5_c ??
    r.phase2DueDateDays ?? data.phase2DueDateDays ??
    r.Phase2DueDateDays__c ?? data.Phase2DueDateDays__c ?? 0
  );
  // Name field - standard Salesforce field
  const name = String(r.Name ?? data.Name ?? r.name ?? data.name ?? r.Name__c ?? data.Name__c ?? '');
  // Use stored checklistType if available, otherwise derive from name
  const checklistType = r.Checklist_Type__c ?? data.Checklist_Type__c ?? r.checklistType ?? data.checklistType ?? deriveChecklistTypeFromName(name);

  // Parse date from either ISO string or Unix timestamp (milliseconds)
  const parseDate = (value: unknown): string => {
    if (!value) return '';
    // If it's already an ISO string, return it
    if (typeof value === 'string' && value.includes('T')) {
      return value;
    }
    // If it's a number (Unix timestamp in ms), convert to ISO string
    if (typeof value === 'number') {
      return new Date(value).toISOString();
    }
    // If it's a numeric string (Unix timestamp), convert to ISO string
    if (typeof value === 'string' && /^\d+$/.test(value)) {
      return new Date(parseInt(value, 10)).toISOString();
    }
    return String(value);
  };

  return {
    id,
    key: String(r.key ?? r.id ?? ''),
    name,
    phase1DueDateDays: isNaN(phase1Days) ? 7 : phase1Days,
    phase2DueDateDays: isNaN(phase2Days) ? 0 : phase2Days,
    phase2Required: phase2Days > 0,
    checklistType: String(checklistType),
    // OwnerId - Lookup(User,Group)
    ownerId: String(r.OwnerId ?? data.OwnerId ?? r.OwnerId__c ?? data.OwnerId__c ?? r.ownerId ?? data.ownerId ?? ''),
    ownerName: String(r.OwnerName ?? data.OwnerName ?? r.ownerName ?? data.ownerName ?? ''),
    // CreatedById - Lookup(User)
    createdById: String(r.CreatedById ?? data.CreatedById ?? r.CreatedById__c ?? data.CreatedById__c ?? r.createdById ?? data.createdById ?? ''),
    createdByName: String(r.CreatedByName ?? data.CreatedByName ?? r.createdByName ?? data.createdByName ?? ''),
    // Prefer ISO date strings, but handle Unix timestamps from __c fields
    createdDate: parseDate(r.createdDate ?? data.createdDate ?? r.CreatedDate ?? data.CreatedDate ?? r.CreatedDate__c ?? data.CreatedDate__c ?? ''),
    // LastModifiedById - Lookup(User)
    lastModifiedById: String(r.LastModifiedById ?? data.LastModifiedById ?? r.LastModifiedById__c ?? data.LastModifiedById__c ?? r.lastModifiedById ?? data.lastModifiedById ?? ''),
    lastModifiedByName: String(r.LastModifiedByName ?? data.LastModifiedByName ?? r.lastModifiedByName ?? data.lastModifiedByName ?? ''),
    // Prefer ISO date strings, but handle Unix timestamps from __c fields
    lastModifiedDate: parseDate(r.lastModified ?? data.lastModified ?? r.LastModifiedDate ?? data.LastModifiedDate ?? r.LastModifiedDate__c ?? data.LastModifiedDate__c ?? ''),
  };
}

/**
 * Fetch all audit templates from Gobodo/RDS (non-hook version for business logic)
 */
export async function fetchAllAuditTemplates(): Promise<GobodoAuditTemplate[]> {
  if (!USE_GOBODO_API) {
    return [];
  }

  const graphId = GobodoConfig.graph();
  const listCache = makeAuditTemplateListReadCacheOpts(
    graphId,
    normalizeToGobodoFormat(AUDIT_TEMPLATE_GOBODO_OBJECT),
    0,
    100
  );

  const records = await getRecordsForObject(AUDIT_TEMPLATE_GOBODO_OBJECT, { size: 100, cache: listCache });
  return (records as Record<string, unknown>[]).map(mapGobodoToAuditTemplate);
}

/**
 * Fetch a single audit template by name from Gobodo/RDS (non-hook version for business logic)
 */
export async function fetchAuditTemplateByName(templateName: string): Promise<GobodoAuditTemplate | null> {
  const templates = await fetchAllAuditTemplates();
  const lowerName = templateName.toLowerCase();
  return templates.find((t) => t.name.toLowerCase() === lowerName) || null;
}

/**
 * Fetch all audit templates from Gobodo/RDS (React Query hook)
 */
export function useAuditTemplates() {
  const graphId = GobodoConfig.graph();
  return useQuery({
    queryKey: auditTemplateKeys.all(graphId),
    queryFn: fetchAllAuditTemplates,
    staleTime: AUDIT_TEMPLATE_QUERY_STALE_MS,
    gcTime: AUDIT_TEMPLATE_QUERY_GC_MS,
  });
}

/**
 * Fetch a single audit template by ID from Gobodo/RDS
 */
export function useAuditTemplate(templateId: string | null) {
  const graphId = GobodoConfig.graph();
  const byKeyCache = templateId
    ? makeAuditTemplateByKeyReadCacheOpts(templateId, graphId)
    : undefined;
  const byIdCache = templateId
    ? makeAuditTemplateByIdReadCacheOptsFromRecordId(templateId, graphId)
    : undefined;
  return useQuery({
    queryKey: auditTemplateKeys.detail(graphId, templateId ?? ''),
    queryFn: async () => {
      if (!templateId) return null;

      if (!USE_GOBODO_API) {
        return null;
      }

      let record: Record<string, unknown> | null = null;

      // Try to fetch by key first (most reliable for Gobodo graph records)
      try {
        record = await getGobodoRecordByKey(templateId, byKeyCache);
      } catch {
        // Key lookup failed, try by ID
      }

      // Fallback: fetch by ID
      if (!record) {
        try {
          record = await getGobodoRecordById(templateId, byIdCache);
        } catch {
          // ID lookup failed
        }
      }

      if (!record) return null;

      // Map the record to the template type
      const template = mapGobodoToAuditTemplate(record);

      // Helper to detect if a value looks like an ID (numeric or empty) vs a real name
      const looksLikeId = (v: string): boolean =>
        !v || /^\d+$/.test(v) || v.length < 3;
      const looksLikeSfId = (v: string): boolean =>
        /^[a-zA-Z0-9]{15,18}$/.test(v) && /^[a-zA-Z]/.test(v);

      // If user names look like IDs, try to resolve them via user lookup
      let { createdByName, lastModifiedByName } = template;
      const { createdById, lastModifiedById } = template;

      if (looksLikeId(createdByName) || looksLikeId(lastModifiedByName)) {
        const idsToResolve: string[] = [];
        if (looksLikeId(createdByName) && createdById) {
          idsToResolve.push(createdById);
        }
        if (looksLikeId(lastModifiedByName) && lastModifiedById) {
          idsToResolve.push(lastModifiedById);
        }

        if (idsToResolve.length > 0) {
          try {
            // Try direct object ID lookup for numeric IDs
            for (const userId of idsToResolve) {
              if (/^\d+$/.test(userId)) {
                const userResult = await findGraphUserByObjectId(userId);
                if (userResult?.displayName && !looksLikeId(userResult.displayName) && !looksLikeSfId(userResult.displayName)) {
                  if (userId === createdById && looksLikeId(createdByName)) {
                    createdByName = userResult.displayName;
                  }
                  if (userId === lastModifiedById && looksLikeId(lastModifiedByName)) {
                    lastModifiedByName = userResult.displayName;
                  }
                }
              }
            }

            // If still unresolved, try resolveGraphUserNamesBySfIds as fallback
            if (looksLikeId(createdByName) || looksLikeId(lastModifiedByName)) {
              const userNameMap = await resolveGraphUserNamesBySfIds(idsToResolve);

              if (looksLikeId(createdByName) && createdById) {
                const resolvedName = userNameMap.get(createdById);
                if (resolvedName && !looksLikeId(resolvedName) && !looksLikeSfId(resolvedName)) {
                  createdByName = resolvedName;
                }
              }

              if (looksLikeId(lastModifiedByName) && lastModifiedById) {
                const resolvedName = userNameMap.get(lastModifiedById);
                if (resolvedName && !looksLikeId(resolvedName) && !looksLikeSfId(resolvedName)) {
                  lastModifiedByName = resolvedName;
                }
              }
            }
          } catch {
            // User lookup failed, keep original values
          }
        }
      }

      return {
        ...template,
        createdByName,
        lastModifiedByName,
      };
    },
    enabled: !!templateId && USE_GOBODO_API,
    staleTime: AUDIT_TEMPLATE_QUERY_STALE_MS,
    gcTime: AUDIT_TEMPLATE_QUERY_GC_MS,
  });
}

/**
 * Fetch CS Audits related to a specific template
 * Returns audits where the template relationship field matches the templateId
 */
export function useAuditsByTemplate(templateId: string) {
  const graphId = GobodoConfig.graph();

  return useQuery({
    queryKey: ['cybersweep', 'audits_by_template', graphId, templateId],
    queryFn: async () => {
      if (!USE_GOBODO_API || !templateId) {
        return [];
      }

      console.log('[useAuditsByTemplate] Fetching audits for template:', templateId);

      // Fetch all CS Audits and filter by template
      // Use larger size to get more records (pagination can be added if needed)
      const csAuditObject = normalizeToGobodoFormat(CSAUDIT_OBJECT);
      const records = await getRecordsForObject(csAuditObject, { size: 500 });

      // Filter audits that are linked to this template
      const filteredAudits = (records as Record<string, unknown>[]).filter(record => {
        const templateRef = firstNonEmpty(
          record[CS_AUDIT_TEMPLATE_REL_FIELD],
          record.CSAuditTemplateRelField__c,
          record.csAuditTemplateRelField,
          record.Audit_Template__c,
          record.auditTemplateLookupId,
          record.Audit_Type__c,
          record.auditType
        );
        // Match by key or ID
        return templateRef === templateId ||
               String(templateRef).includes(templateId) ||
               templateId.includes(String(templateRef));
      });

      console.log('[useAuditsByTemplate] Found', filteredAudits.length, 'audits for template');

      // Enrich and return
      const enriched = await enrichCSAuditsWithTemplateInfo(filteredAudits);
      return hydrateCSAuditRecordsForListUi(enriched);
    },
    enabled: !!templateId && USE_GOBODO_API,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Audit Website record type for Related tab display
 */
export interface AuditWebsiteRecord {
  id: string;
  key?: string;
  name: string; // CSAW-#### Auto Number
  auditTemplateId?: string;
  orderOverride?: number;
  websiteConfigurationId?: string;
  createdById?: string;
  lastModifiedById?: string;
  ownerId?: string;
}

/**
 * Extended Audit Website record with additional fields for detail page
 */
export interface AuditWebsiteDetailRecord extends AuditWebsiteRecord {
  sfId?: string;
  createdDate?: string;
  lastModifiedDate?: string;
  templateName?: string;
  websiteConfigurationName?: string;
  ownerName?: string;
  createdByName?: string;
  lastModifiedByName?: string;
}

/**
 * Traversal page fetch result for audit websites by template.
 */
type AuditWebsiteTemplateTraversalContext = {
  templateKey: string;
  templateRefValues: string[];
};

export type AuditWebsiteTemplateTraversalPageFetch = {
  records: AuditWebsiteRecord[];
  rawObjectCount: number;
};

async function resolveAuditWebsiteTemplateTraversalContext(
  templateId: string
): Promise<AuditWebsiteTemplateTraversalContext> {
  let templateKey = templateId;
  let templateSfId: string | undefined;
  try {
    const templateRecord = /^\d+$/.test(templateId)
      ? await getRecordById(AUDIT_TEMPLATE_GOBODO_OBJECT, templateId)
      : await getGobodoRecordByKey(templateId);
    if (templateRecord) {
      templateKey = String((templateRecord as Record<string, unknown>).key ?? templateKey);
      templateSfId = String(
        (templateRecord as Record<string, unknown>).sf_id ??
        (templateRecord as Record<string, unknown>).sfId ??
        (templateRecord as Record<string, unknown>).Id ??
        ''
      );
      console.log('[useAuditWebsitesByTemplate] Template key:', templateKey, 'SF ID:', templateSfId);
    }
  } catch (err) {
    console.warn('[useAuditWebsitesByTemplate] Could not fetch template record:', err);
  }

  templateKey = ensureGobodoCustomSuffixOnRecordKey(templateKey);
  return {
    templateKey,
    templateRefValues: uniqueLookupTokens([
      templateId,
      templateKey,
      stripKnownSuffix(templateKey),
      templateSfId,
    ]),
  };
}

function buildAuditWebsitesByTemplateTraversalInput(
  graphId: number,
  context: AuditWebsiteTemplateTraversalContext,
  pagination?: { startIndex: number; count: number }
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const auditWebsiteObject = normalizeToGobodoFormat(AUDIT_WEBSITE_OBJECT);
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(graphId);
  input.setStartObjectKey(context.templateKey);
  input.setRelationshipTypes([AUDIT_WEBSITE_TEMPLATE_REL_KEY]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
  input.setEndObjectTypes([auditWebsiteObject]);
  input.setMaxPathLength(1);
  if (pagination) {
    input.setStartIndex(pagination.startIndex);
    input.setCount(pagination.count);
  }

  const websiteTemplateFieldIndexes = [
    'Audit_Template__c',
    'auditTemplateId',
    AUDIT_WEBSITE_TEMPLATE_REL_FIELD,
    'AuditTemplateRelField__c',
    'custom__cyber_sweep_audit_template',
  ];
  const websiteTemplateBranches = websiteTemplateFieldIndexes.flatMap((indexName) => {
    try {
      const filter = new GobodoApi.IndexFilter();
      filter.setIndexName(indexName);
      filter.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
      filter.setValues(context.templateRefValues);

      const branch = new GobodoApi.GraphTraversalInput();
      branch.setGraphId(graphId);
      branch.setStartObjectTypes([auditWebsiteObject]);
      branch.setEndObjectTypes([auditWebsiteObject]);
      branch.setMaxPathLength(0);
      if (pagination) {
        branch.setStartIndex(pagination.startIndex);
        branch.setCount(pagination.count);
      }
      branch.setStartObjectFieldFilters([filter]);
      return [branch];
    } catch {
      return [];
    }
  });
  chainUnionTraversals(input, websiteTemplateBranches);

  return input;
}

function mapAuditWebsiteTraversalObjects(objects: unknown[]): AuditWebsiteRecord[] {
  const auditWebsiteObject = normalizeToGobodoFormat(AUDIT_WEBSITE_OBJECT);
  const seen = new Set<string>();
  return objects.flatMap((gobodoObj: unknown) => {
    try {
      const converted = convertFromGobodoRecord(
        gobodoObj as Record<string, unknown>,
        auditWebsiteObject
      ) as Record<string, unknown>;
      const row: Record<string, unknown> = {
        ...converted,
        key: (gobodoObj as Record<string, unknown>).key ?? converted.key,
        label: (gobodoObj as Record<string, unknown>).label ?? converted.label,
      };
      const dedupeKey = String(row.id ?? row.key ?? '');
      if (!dedupeKey || seen.has(dedupeKey)) return [];
      seen.add(dedupeKey);
      return [{
        id: dedupeKey,
        key: String(row.key ?? ''),
        name: String(
          firstNonEmpty(row.Name, row.name, row.Name__c, row.label) ?? ''
        ),
        auditTemplateId: String(row.Audit_Template__c ?? row.auditTemplateId ?? ''),
        orderOverride: Number(row.Order_Override__c ?? row.orderOverride ?? 0),
        websiteConfigurationId: String(row.Website_Configuration__c ?? row.websiteConfigurationId ?? ''),
        createdById: String(row.CreatedById ?? row.createdById ?? ''),
        lastModifiedById: String(row.LastModifiedById ?? row.lastModifiedById ?? ''),
        ownerId: String(row.OwnerId ?? row.ownerId ?? ''),
      } satisfies AuditWebsiteRecord];
    } catch {
      return [];
    }
  });
}

function auditWebsiteTemplateTraversalCacheOpts(templateId: string, startIndex: number, count: number): GobodoCacheOpts {
  return makeCacheOpts(
    [
      'cybersweep',
      'audit-websites-by-template',
      'traverse',
      templateId,
      String(GobodoConfig.graph()),
      String(startIndex),
      String(count),
      AUDIT_WEBSITE_TEMPLATE_REL_KEY,
      'audit-website-template-traversal-v1',
    ],
    CACHE_TTL.SHORT
  );
}

function auditWebsiteTemplateTraversalCountCacheOpts(templateId: string): GobodoCacheOpts {
  return makeCacheOpts(
    [
      'cybersweep',
      'audit-websites-by-template',
      'traverse-count',
      templateId,
      String(GobodoConfig.graph()),
      AUDIT_WEBSITE_TEMPLATE_REL_KEY,
      'audit-website-template-traversal-v1',
    ],
    CACHE_TTL.SHORT
  );
}

export async function fetchAuditWebsitesByTemplateTraversalPage(
  templateId: string,
  startIndex: number,
  count: number
): Promise<AuditWebsiteTemplateTraversalPageFetch> {
  const graphId = GobodoConfig.graph();
  const context = await resolveAuditWebsiteTemplateTraversalContext(templateId);
  const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
  const input = buildAuditWebsitesByTemplateTraversalInput(graphId, context, { startIndex, count });
  const cacheOpts = auditWebsiteTemplateTraversalCacheOpts(templateId, startIndex, count);
  const result = await retryWithBackoff(
    () => traversalApi.traverseObjects(input, cacheOpts),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  const objects = requireCyberSweepTraverseObjectsArray(result);
  return {
    records: mapAuditWebsiteTraversalObjects(objects),
    rawObjectCount: objects.length,
  };
}

export async function fetchAuditWebsiteTraversalCount(templateId: string): Promise<number> {
  const graphId = GobodoConfig.graph();
  const context = await resolveAuditWebsiteTemplateTraversalContext(templateId);
  const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
  const countInput = buildAuditWebsitesByTemplateTraversalInput(graphId, context);
  const cacheOpts = auditWebsiteTemplateTraversalCountCacheOpts(templateId);
  const countRaw = await retryWithBackoff(
    () => traversalApi.traverseObjectsCount(countInput, cacheOpts),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  return parseCyberSweepBoardTraversalCount(countRaw);
}

export async function fetchAllAuditWebsitesForTemplate(templateId: string): Promise<AuditWebsiteRecord[]> {
  const acc: AuditWebsiteRecord[] = [];
  const seen = new Set<string>();
  let serverTotalCount = 0;
  try {
    serverTotalCount = await fetchAuditWebsiteTraversalCount(templateId);
  } catch {
    serverTotalCount = 0;
  }

  let startIndex = 0;
  for (let page = 0; page < AUDIT_WEBSITE_TEMPLATE_MAX_PAGES; page++) {
    const { records, rawObjectCount } = await fetchAuditWebsitesByTemplateTraversalPage(
      templateId,
      startIndex,
      AUDIT_WEBSITE_TEMPLATE_PAGE_SIZE
    );
    if (rawObjectCount === 0) break;
    for (const row of records) {
      const dedupeKey = String(row.id ?? '');
      if (!dedupeKey || seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      acc.push(row);
    }
    startIndex += rawObjectCount;
    if (rawObjectCount < AUDIT_WEBSITE_TEMPLATE_PAGE_SIZE) break;
    if (serverTotalCount > 0 && startIndex >= serverTotalCount) break;
  }
  return acc;
}

/**
 * Fetch Audit Websites (Cyber_Sweep_Audit_Website__c) linked to a specific template.
 * Uses Gobodo traversal from template → audit websites, with union fallback branches that match
 * the same template reference tokens on website fields (for mixed legacy data).
 */
export function useAuditWebsitesByTemplate(templateId: string) {
  return useQuery({
    queryKey: cyberSweepKeys.auditWebsitesByTemplate(templateId),
    queryFn: async (): Promise<AuditWebsiteRecord[]> => {
      if (!USE_GOBODO_API || !templateId) {
        return [];
      }

      console.log('[useAuditWebsitesByTemplate] Fetching audit websites for template:', templateId);
      const records = await fetchAllAuditWebsitesForTemplate(templateId);
      console.log('[useAuditWebsitesByTemplate] Found', records.length, 'audit websites for template via traversal');
      return records;
    },
    enabled: !!templateId && USE_GOBODO_API,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useAuditWebsitesByTemplateInfinite(templateId: string, options?: { enabled?: boolean; pageSize?: number }) {
  const queryClient = useQueryClient();
  const pageSize = options?.pageSize ?? AUDIT_WEBSITE_TEMPLATE_PAGE_SIZE;
  return useInfiniteQuery({
    queryKey: templateId
      ? cyberSweepKeys.auditWebsitesByTemplateInfinite(templateId, pageSize)
      : ['cybersweep', 'audit_websites_by_template', 'infinite', 'none'],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      if (!templateId) {
        return { records: [], rawObjectCount: 0, serverTotalCount: 0 };
      }
      const serverTotalCount = await queryClient.fetchQuery({
        queryKey: cyberSweepKeys.auditWebsiteCountByTemplate(templateId),
        queryFn: () => fetchAuditWebsiteTraversalCount(templateId),
        staleTime: 60_000,
      });
      const { records, rawObjectCount } = await fetchAuditWebsitesByTemplateTraversalPage(
        templateId,
        pageParam as number,
        pageSize
      );
      return { records, rawObjectCount, serverTotalCount };
    },
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      const next = (lastPageParam as number) + lastPage.rawObjectCount;
      if (lastPage.rawObjectCount === 0) return undefined;
      if (Number.isFinite(lastPage.serverTotalCount) && lastPage.serverTotalCount > 0 && next >= lastPage.serverTotalCount) {
        return undefined;
      }
      if (lastPage.rawObjectCount < pageSize) return undefined;
      return next;
    },
    enabled: USE_GOBODO_API && !!templateId && (options?.enabled ?? true),
    staleTime: 60_000,
  });
}

export function useAuditWebsiteCountByTemplate(templateId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: templateId
      ? cyberSweepKeys.auditWebsiteCountByTemplate(templateId)
      : ['cybersweep', 'audit_websites_by_template', 'count', 'none'],
    queryFn: async () => {
      if (!templateId) return 0;
      return fetchAuditWebsiteTraversalCount(templateId);
    },
    enabled: USE_GOBODO_API && !!templateId && (options?.enabled ?? true),
    staleTime: 60_000,
  });
}

/**
 * Fetch a single Audit Website record by ID for the detail page.
 */
export function useAuditWebsite(websiteId: string) {
  const graphId = GobodoConfig.graph();

  return useQuery({
    queryKey: ['cybersweep', 'audit_website', graphId, websiteId],
    queryFn: async (): Promise<AuditWebsiteDetailRecord | null> => {
      if (!USE_GOBODO_API || !websiteId) {
        return null;
      }

      try {
        // Try to fetch by key first, then by ID
        let record: Record<string, unknown> | null = null;
        try {
          record = await getGobodoRecordByKey(websiteId);
        } catch {
          // Try by ID
        }
        if (!record) {
          try {
            record = await getGobodoRecordById(websiteId);
          } catch {
            // Not found
          }
        }

        if (!record) return null;

        const r = record as Record<string, unknown>;
        const detailRecord: AuditWebsiteDetailRecord = {
          id: String(r.id ?? r.key ?? ''),
          key: String(r.key ?? ''),
          sfId: String(r.sf_id ?? r.sfId ?? ''),
          name: String(firstNonEmpty(r.Name, r.name, r.Name__c, r.label) ?? ''),
          auditTemplateId: String(r.Audit_Template__c ?? r.auditTemplateId ?? ''),
          orderOverride: Number(r.Order_Override__c ?? r.orderOverride ?? 0),
          websiteConfigurationId: String(r.Website_Configuration__c ?? r.websiteConfigurationId ?? ''),
          createdById: String(r.CreatedById__c ?? r.CreatedById ?? r.createdById ?? ''),
          createdDate: String(r.CreatedDate__c ?? r.createdDate ?? ''),
          lastModifiedById: String(r.LastModifiedById__c ?? r.LastModifiedById ?? r.lastModifiedById ?? ''),
          lastModifiedDate: String(r.LastModifiedDate__c ?? r.lastModifiedDate ?? r.lastModified ?? ''),
          ownerId: String(r.OwnerId__c ?? r.OwnerId ?? r.ownerId ?? ''),
        };

        return detailRecord;
      } catch (err) {
        console.error('[useAuditWebsite] Error fetching record:', err);
        return null;
      }
    },
    enabled: !!websiteId && USE_GOBODO_API,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * PAW (Performed Audit Website) Record Detail interface for detail page.
 * Extended version with additional metadata fields.
 */
export interface PAWRecordDetail {
  id: string;
  key?: string;
  sfId?: string;
  name?: string;
  auditPhase?: string;
  csAuditId?: string;
  csAuditName?: string;
  auditTemplateName?: string;
  findings?: string;
  method?: string;
  website?: string;
  websiteDisplayName?: string;
  piiFound?: string;
  phase2Required?: boolean;
  clientInfoLinks?: string;
  status?: string;
  ownerId?: string;
  ownerName?: string;
  createdById?: string;
  createdByName?: string;
  createdDate?: string;
  lastModifiedById?: string;
  lastModifiedByName?: string;
  lastModifiedDate?: string;
}

async function fetchMatchingCSAuditIdsForTemplate(templateId: string): Promise<string[]> {
  if (!USE_GOBODO_API || !templateId) return [];

  let templateKey = templateId;
  let templateSfId = '';
  if (/^\d+$/.test(templateId)) {
    try {
      const templateRecord = await getGobodoRecordById(templateId);
      if (templateRecord) {
        templateKey = String(templateRecord.key ?? templateId);
        templateSfId = String(
          (templateRecord as Record<string, unknown>).sf_id ??
          (templateRecord as Record<string, unknown>).sfId ??
          (templateRecord as Record<string, unknown>).Id ??
          ''
        );
      }
    } catch {
      // Keep original templateId
    }
  }

  const templateRefValues = uniqueLookupTokens([
    templateId,
    templateKey,
    stripKnownSuffix(templateKey),
    templateSfId,
  ]);

  const projectedAudits = await fetchAllRecordsViaPages(CSAUDIT_OBJECT, {
    pageSize: 500,
    maxPages: 1,
    dataFields: CSAUDIT_TEMPLATE_PAW_DISCOVERY_DATA_FIELDS,
    cacheKeyTag: 'template-paw-audit-id-discovery-v1',
  });

  const matchingAuditIds: string[] = [];
  for (const audit of projectedAudits) {
    const data = (audit.data as Record<string, unknown>) ?? {};
    const auditTemplateRef = String(
      firstNonEmpty(
        audit[CS_AUDIT_TEMPLATE_REL_FIELD],
        data[CS_AUDIT_TEMPLATE_REL_FIELD],
        audit.CSAuditTemplateRelField__c,
        data.CSAuditTemplateRelField__c,
        audit.csAuditTemplateRelField,
        data.csAuditTemplateRelField,
        audit.Audit_Template__c,
        data.Audit_Template__c,
        audit.Audit_Type__c,
        data.Audit_Type__c,
        audit.AuditType__c,
        data.AuditType__c,
        audit.auditTemplateLookupId,
        data.auditTemplateLookupId
      ) ?? ''
    );
    if (!auditTemplateRef) continue;

    const matches = templateRefValues.some((ref) =>
      auditTemplateRef === ref ||
      auditTemplateRef.includes(ref) ||
      stripKnownSuffix(auditTemplateRef) === stripKnownSuffix(ref)
    );
    if (!matches) continue;

    const auditId = String(audit.id ?? audit.key ?? '').trim();
    if (auditId) matchingAuditIds.push(auditId);
  }

  return Array.from(new Set(matchingAuditIds));
}

/**
 * Fetch a single PAW (Performed Audit Website) record by ID for the detail page.
 * Supports both numeric Gobodo IDs and UUID keys.
 */
export function usePAWRecord(pawId: string) {
  const graphId = GobodoConfig.graph();

  return useQuery({
    queryKey: ['cybersweep', 'paw_record', graphId, pawId],
    queryFn: async (): Promise<PAWRecordDetail | null> => {
      if (!USE_GOBODO_API || !pawId) {
        return null;
      }

      console.log('[usePAWRecord] Fetching PAW record with ID:', pawId);

      try {
        let record: Record<string, unknown> | null = null;
        const isNumericId = /^\d+$/.test(pawId);

        if (isNumericId) {
          // For numeric IDs, try getObjectById first (more reliable)
          console.log('[usePAWRecord] Trying getGobodoRecordById for numeric ID:', pawId);
          try {
            record = await getGobodoRecordById(pawId);
          } catch (err) {
            console.warn('[usePAWRecord] getGobodoRecordById failed:', err);
          }
          // Fallback to key lookup if ID lookup fails
          if (!record) {
            console.log('[usePAWRecord] Fallback: trying getGobodoRecordByKey');
            try {
              record = await getGobodoRecordByKey(pawId);
            } catch {
              // Not found
            }
          }
        } else {
          // For UUID/key format, try getObjectByKey first
          console.log('[usePAWRecord] Trying getGobodoRecordByKey for key:', pawId);
          try {
            record = await getGobodoRecordByKey(pawId);
          } catch (err) {
            console.warn('[usePAWRecord] getGobodoRecordByKey failed:', err);
          }
        }

        if (!record) {
          console.warn('[usePAWRecord] Record not found for ID:', pawId);
          return null;
        }

        console.log('[usePAWRecord] Found record:', record);

        const r = record as Record<string, unknown>;

        // Extract user IDs for lookup - prioritize the actual field names from the record
        // Based on console logs, the Gobodo record uses lowercase field names: createdBy, lastModifiedBy
        const createdById = String(
          r.createdBy ?? r.CreatedBy ?? // Lowercase from Gobodo
          r[CREATED_BY_REL_FIELD] ?? r.CreatedByRelField__c ??
          r.CreatedById__c ?? r.CreatedById ?? r.createdById ??
          r.CreatedBy__c ?? ''
        );
        const lastModifiedById = String(
          r.lastModifiedBy ?? r.LastModifiedBy ?? // Lowercase from Gobodo
          r[LAST_MODIFIED_BY_REL_FIELD] ?? r.LastModifiedByRelField__c ??
          r.LastModifiedById__c ?? r.LastModifiedById ?? r.lastModifiedById ??
          r.LastModifiedBy__c ?? ''
        );

        // Try to get user names from the record first
        let createdByName = String(r.CreatedByName__c ?? r.CreatedByName ?? r.createdByName ?? '');
        let lastModifiedByName = String(r.LastModifiedByName__c ?? r.LastModifiedByName ?? r.lastModifiedByName ?? '');

        // Helper to detect if a value looks like an ID (numeric or empty) vs a real name
        const looksLikeId = (val: string) => /^\d+$/.test(val) || val.length === 0;
        // Also check for SF-style IDs (15-18 char alphanumeric)
        const looksLikeSfId = (val: string) => /^[a-zA-Z0-9]{15,18}$/.test(val);

        // If names are missing or look like IDs, try to resolve them via user lookup
        if (looksLikeId(createdByName) || looksLikeId(lastModifiedByName)) {
          try {
            // Collect all potential user IDs to resolve
            const idsToResolve: string[] = [];
            if (looksLikeId(createdByName) && createdById) {
              idsToResolve.push(createdById);
            }
            if (looksLikeId(lastModifiedByName) && lastModifiedById) {
              idsToResolve.push(lastModifiedById);
            }

            if (idsToResolve.length > 0) {
              // Try direct object ID lookup first for numeric IDs
              for (const userId of idsToResolve) {
                if (/^\d+$/.test(userId)) {
                  const userResult = await findGraphUserByObjectId(userId);
                  if (userResult?.displayName && !looksLikeId(userResult.displayName) && !looksLikeSfId(userResult.displayName)) {
                    if (userId === createdById && looksLikeId(createdByName)) {
                      createdByName = userResult.displayName;
                    }
                    if (userId === lastModifiedById && looksLikeId(lastModifiedByName)) {
                      lastModifiedByName = userResult.displayName;
                    }
                  }
                }
              }

              // If still unresolved, try resolveGraphUserNamesBySfIds as fallback
              if (looksLikeId(createdByName) || looksLikeId(lastModifiedByName)) {
                const userNameMap = await resolveGraphUserNamesBySfIds(idsToResolve);

                // Resolve created by name if needed
                if (looksLikeId(createdByName) && createdById) {
                  const resolvedName = userNameMap.get(createdById);
                  if (resolvedName && !looksLikeId(resolvedName) && !looksLikeSfId(resolvedName)) {
                    createdByName = resolvedName;
                  }
                }

                // Resolve last modified by name if needed
                if (looksLikeId(lastModifiedByName) && lastModifiedById) {
                  const resolvedName = userNameMap.get(lastModifiedById);
                  if (resolvedName && !looksLikeId(resolvedName) && !looksLikeSfId(resolvedName)) {
                    lastModifiedByName = resolvedName;
                  }
                }
              }
            }
          } catch (err) {
            console.warn('[usePAWRecord] User name lookup failed:', err);
          }
        }

        const detailRecord: PAWRecordDetail = {
          id: String(r.id ?? r.key ?? ''),
          key: String(r.key ?? ''),
          sfId: String(r.sf_id ?? r.sfId ?? r.Id ?? ''),
          name: String(firstNonEmpty(r.Name, r.name, r.Name__c, r.label) ?? ''),
          auditPhase: String(r.Audit_Phase__c ?? r.auditPhase ?? ''),
          csAuditId: String(r.CS_Audit__c ?? r.csAuditId ?? r.CSAuditRelField__c ?? ''),
          csAuditName: String(r.csAuditName ?? ''),
          auditTemplateName: String(r.Audit_Template__c ?? r.auditTemplateName ?? ''),
          findings: String(r.Findings__c ?? r.findings ?? ''),
          method: String(r.Method__c ?? r.method ?? ''),
          website: String(r.Website__c ?? r.website ?? ''),
          websiteDisplayName: String(r.websiteDisplayName ?? r.Website__c ?? r.website ?? ''),
          piiFound: String(r.PII_Found__c ?? r.piiFound ?? ''),
          phase2Required: Boolean(r.Phase_2_Required__c ?? r.phase2Required ?? false),
          clientInfoLinks: String(r.Client_Info_Links__c ?? r.clientInfoLinks ?? ''),
          status: String(r.Status__c ?? r.status ?? ''),
          ownerId: String(r.OwnerId__c ?? r.OwnerId ?? r.ownerId ?? ''),
          ownerName: String(r.OwnerName ?? r.ownerName ?? r.Owner_Name__c ?? ''),
          createdById,
          createdByName,
          createdDate: String(r.CreatedDate__c ?? r.createdDate ?? ''),
          lastModifiedById,
          lastModifiedByName,
          lastModifiedDate: String(r.LastModifiedDate__c ?? r.lastModifiedDate ?? r.lastModified ?? ''),
        };

        return detailRecord;
      } catch (err) {
        console.error('[usePAWRecord] Error fetching record:', err);
        return null;
      }
    },
    enabled: !!pawId && USE_GOBODO_API,
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================
// PAW Records by Template Queries
// ============================================

/**
 * Fetch all PAW records for a template by going through CS Audits.
 * PAW records are linked to CS Audits, which are linked to templates.
 * So we: Template -> CS Audits -> PAW Records
 */
/**
 * Fast fetch of latest PAW records for a template.
 * Uses the audits already fetched by useAuditsByTemplate via queryClient cache.
 */
export async function fetchLatestPAWRecordsForTemplate(
  templateId: string,
  limit: number = 6,
  auditIds?: string[]
): Promise<PAWRecordDetail[]> {
  if (!USE_GOBODO_API) return [];

  try {
    // If auditIds provided, use them directly (faster - no extra fetch needed)
    let matchingAuditIds = auditIds ?? [];

    console.log('[fetchLatestPAWRecordsForTemplate] Starting for template:', templateId);

    if (matchingAuditIds.length === 0) {
      matchingAuditIds = await fetchMatchingCSAuditIdsForTemplate(templateId);
    }

    console.log('[fetchLatestPAWRecordsForTemplate] Matching audit IDs:', matchingAuditIds.length, matchingAuditIds.slice(0, 5));

    if (matchingAuditIds.length === 0) {
      console.log('[fetchLatestPAWRecordsForTemplate] No matching audits found');
      return [];
    }

    // Fetch PAW records from first few audits until we have enough
    let allRecords: Record<string, unknown>[] = [];
    for (const auditId of matchingAuditIds.slice(0, 3)) {
      if (allRecords.length >= limit) break;
      try {
        console.log('[fetchLatestPAWRecordsForTemplate] Fetching PAWs for audit:', auditId);
        const { records } = await fetchPAWRecordsTraversalPage(auditId, 0, limit, {
          dataFields: PAW_RELATED_LIST_DATA_FIELDS,
          cacheKeyTag: 'template-related-list-v1',
        });
        console.log('[fetchLatestPAWRecordsForTemplate] Got', records.length, 'PAWs for audit', auditId);
        allRecords = allRecords.concat(records);
      } catch (err) {
        console.warn('[fetchLatestPAWRecordsForTemplate] Error fetching PAWs for audit', auditId, err);
      }
    }

    console.log('[fetchLatestPAWRecordsForTemplate] Total PAW records:', allRecords.length);
    if (allRecords.length > 0) {
      console.log('[fetchLatestPAWRecordsForTemplate] Sample PAW record:', JSON.stringify(allRecords[0], null, 2));
      console.log('[fetchLatestPAWRecordsForTemplate] Sample PAW id field:', allRecords[0].id, 'type:', typeof allRecords[0].id);
      console.log('[fetchLatestPAWRecordsForTemplate] Sample PAW key field:', allRecords[0].key);
      console.log('[fetchLatestPAWRecordsForTemplate] Sample PAW label field:', allRecords[0].label);
    }

    // Map to PAWRecordDetail
    const allPAWs: PAWRecordDetail[] = [];
    const seen = new Set<string>();
    const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    // Gobodo placeholder format: PAW-{timestamp}-{seq}-{random} e.g., PAW-1778099891140-4-la32e
    const GOBODO_PLACEHOLDER_REGEX = /^PAW-\d{10,}-\d+-[a-z0-9]+$/i;

    for (const paw of allRecords) {
      if (allPAWs.length >= limit) break;

      // Get the raw id and key values - check multiple possible fields
      const rawId = paw.id;
      const rawKey = paw.key;

      // Try to find a numeric Gobodo ID from various possible fields
      let numericId: string | undefined;
      const possibleNumericIds = [
        paw.id,
        paw.gobodoId,
        paw.recordId,
      ].map(v => String(v ?? '')).filter(v => /^\d+$/.test(v));

      if (possibleNumericIds.length > 0) {
        numericId = possibleNumericIds[0];
      }

      // Determine the best ID for navigation:
      // - Prefer numeric ID (works with getGobodoRecordById)
      // - Fall back to key (works with getGobodoRecordByKey)
      let navId: string;

      if (numericId) {
        navId = numericId;
      } else if (rawKey) {
        navId = String(rawKey);
        // Check if key is actually numeric
        if (/^\d+$/.test(navId)) {
          numericId = navId;
        }
      } else {
        const idStr = String(rawId ?? '');
        if (idStr) {
          navId = idStr;
        } else {
          continue; // Skip records with no usable ID
        }
      }

      if (!navId || seen.has(navId)) continue;
      seen.add(navId);

      // Get the PAW name - prefer label (SF auto-number like "PAW-00123"), then Name__c
      let pawName = String(paw.label ?? paw.Name__c ?? paw.name ?? paw.Name ?? '');

      // Check if name is a placeholder that needs replacement
      const isPlaceholderName = (
        !pawName ||
        UUID_REGEX.test(pawName) ||
        /^PAWRecord-\d+$/i.test(pawName) ||
        GOBODO_PLACEHOLDER_REGEX.test(pawName) ||
        // Catch other long PAW-* patterns that aren't clean SF auto-numbers
        (pawName.startsWith('PAW-') && pawName.length > 12 && /[a-z]/i.test(pawName.slice(4)))
      );

      if (isPlaceholderName) {
        // Generate a clean display name
        if (numericId && numericId.length <= 8) {
          // Short numeric ID - use as-is (likely a proper Gobodo ID)
          pawName = `PAW-${numericId}`;
        } else if (numericId && numericId.length > 8) {
          // Long numeric ID (likely timestamp) - use last 6 digits
          pawName = `PAW-${numericId.slice(-6)}`;
        } else {
          // No numeric ID - use sequence number based on position
          const seqNum = String(allPAWs.length + 1).padStart(5, '0');
          pawName = `PAW-${seqNum}`;
        }
        console.log('[fetchLatestPAWRecordsForTemplate] Generated display name:', pawName, 'from numericId:', numericId, 'navId:', navId);
      }

      allPAWs.push({
        id: navId,
        key: String(rawKey ?? navId),
        sfId: String(paw.sf_id ?? paw.sfId ?? ''),
        name: pawName,
        auditPhase: String(paw.Audit_Phase__c ?? paw.auditPhase ?? ''),
        csAuditId: String(paw.CS_Audit__c ?? paw.csAuditId ?? ''),
        csAuditName: String(paw.csAuditName ?? ''),
        auditTemplateName: String(paw.auditTemplateName ?? ''),
        findings: String(paw.Findings__c ?? paw.findings ?? ''),
        method: String(paw.Method__c ?? paw.method ?? ''),
        website: String(paw.Website__c ?? paw.website ?? ''),
        websiteDisplayName: String(paw.websiteDisplayName ?? ''),
        piiFound: String(paw.PII_Found__c ?? paw.piiFound ?? ''),
        phase2Required: Boolean(paw.Phase_2_Required__c ?? false),
        clientInfoLinks: String(paw.Client_Info_Links__c ?? ''),
        status: String(paw.Status__c ?? paw.status ?? ''),
        ownerId: String(paw.OwnerId__c ?? paw.ownerId ?? ''),
        ownerName: String(paw.ownerName ?? ''),
        createdById: String(paw.CreatedById__c ?? ''),
        createdByName: String(paw.createdByName ?? ''),
        createdDate: String(paw.CreatedDate__c ?? paw.createdDate ?? ''),
        lastModifiedById: String(paw.LastModifiedById__c ?? ''),
        lastModifiedByName: String(paw.lastModifiedByName ?? ''),
        lastModifiedDate: String(paw.LastModifiedDate__c ?? ''),
      });
    }

    return allPAWs.slice(0, limit);
  } catch (err) {
    console.error('[fetchLatestPAWRecordsForTemplate] Error:', err);
    return [];
  }
}

/**
 * Hook to fetch latest PAW records by template (optimized for related list preview)
 * Fetches only the latest 6 records for fast loading
 */
export function usePAWRecordsByTemplate(templateId: string, options?: { enabled?: boolean; limit?: number }) {
  const limit = options?.limit ?? 6;
  const queryClient = useQueryClient();
  const graphId = GobodoConfig.graph();
  return useQuery({
    queryKey: templateId
      ? [...auditTemplateKeys.relatedPAWs(graphId, templateId), 'latest', limit]
      : ['cybersweep', 'paw_records_by_template', 'none'],
    queryFn: async () => {
      if (!templateId) {
        return [] as PAWRecordDetail[];
      }
      const auditIds = await queryClient.fetchQuery({
        queryKey: auditTemplateKeys.relatedPAWAuditIds(graphId, templateId),
        queryFn: () => fetchMatchingCSAuditIdsForTemplate(templateId),
        staleTime: 60_000,
        gcTime: 5 * 60_000,
      });
      return fetchLatestPAWRecordsForTemplate(templateId, limit, auditIds);
    },
    enabled: USE_GOBODO_API && !!templateId && (options?.enabled ?? true),
    staleTime: 60_000,
  });
}

/**
 * Hook to get estimated count of PAW records for a template
 * Uses a higher limit to check if there are more than 6 records
 */
export function usePAWRecordCountByTemplate(templateId: string, options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  const graphId = GobodoConfig.graph();
  return useQuery({
    queryKey: templateId
      ? [...auditTemplateKeys.relatedPAWs(graphId, templateId), 'count']
      : ['cybersweep', 'paw_records_by_template', 'count', 'none'],
    queryFn: async () => {
      if (!templateId) return 0;
      const auditIds = await queryClient.fetchQuery({
        queryKey: auditTemplateKeys.relatedPAWAuditIds(graphId, templateId),
        queryFn: () => fetchMatchingCSAuditIdsForTemplate(templateId),
        staleTime: 60_000,
        gcTime: 5 * 60_000,
      });
      // Fetch up to 50 records to get an estimate for the count display
      const records = await fetchLatestPAWRecordsForTemplate(templateId, 50, auditIds);
      return records.length;
    },
    enabled: USE_GOBODO_API && !!templateId && (options?.enabled ?? true),
    staleTime: 60_000,
  });
}

// ============================================
// MEETING AGENDA QUERIES (Gobodo/RDS)
// ============================================

/**
 * Meeting Agenda object for weekly team meetings.
 * Stores attendance, tasks, agenda items, and meeting minutes as JSON in Gobodo.
 */
// Object type name in Gobodo staging: CyberSweepMeetingAgenda__stg5_c
export const MEETING_AGENDA_OBJECT = 'CyberSweepMeetingAgenda__stg5_c';

/** Query keys for meeting agenda caching */
export const meetingAgendaKeys = {
  all: () => ['cybersweep', 'meeting_agendas'] as const,
  byWeek: (weekStartDate: string) => ['cybersweep', 'meeting_agenda', weekStartDate] as const,
};

/** Attendance record for a team member */
export interface MeetingAttendanceRecord {
  userId: string;
  userName: string;
  status: 'present' | 'absent' | 'unmarked';
}

/** Agenda checklist item */
export interface MeetingAgendaItem {
  id: string;
  text: string;
  notes: string;
  completed: boolean;
  isDefault: boolean;
  sortOrder: number;
  showNotes: boolean;
}

/** Task tracker item */
export interface MeetingTaskItem {
  id: string;
  auditorId: string;
  auditorName: string;
  task: string;
  dueDate: string;
  status: 'not_started' | 'in_progress' | 'completed';
  isComplete: boolean;
}

/** Action item from meeting minutes */
export interface MeetingActionItem {
  id: string;
  description: string;
  assignedToId: string;
  assignedToName: string;
  dueDate: string;
  isComplete: boolean;
}

/** Decision recorded in meeting minutes */
export interface MeetingDecision {
  id: string;
  description: string;
  decisionDate: string;
}

/** Meeting minutes data structure */
export interface MeetingMinutesData {
  discussionNotes: string;
  actionItems: MeetingActionItem[];
  decisions: MeetingDecision[];
  lastModifiedAt?: string;
  lastModifiedBy?: string;
}

/** Parsed meeting agenda with all data */
export interface ParsedMeetingAgenda {
  id: string;
  key?: string;
  name?: string;
  weekStartDate: string;
  meetingDate: string;
  attendance: MeetingAttendanceRecord[];
  tasks: MeetingTaskItem[];
  agendaItems: MeetingAgendaItem[];
  minutes: MeetingMinutesData | null;
  createdBy?: string;
  createdDate?: string;
  lastModifiedBy?: string;
  lastModifiedDate?: string;
}

/**
 * Get week number from date string (ISO format: YYYY-MM-DD)
 */
function getWeekNumberFromDate(dateStr: string): number {
  const date = new Date(dateStr);
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const days = Math.floor((date.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
  return Math.ceil((days + startOfYear.getDay() + 1) / 7);
}

/**
 * Parse JSON field safely, returning default value on error
 */
function parseJsonField<T>(value: unknown, defaultValue: T): T {
  if (!value) return defaultValue;

  // Handle corrupted data: array of '[object Object]' strings
  if (Array.isArray(value)) {
    // Check if this is corrupted data (array of '[object Object]' strings)
    if (value.length > 0 && typeof value[0] === 'string' && value[0] === '[object Object]') {
      console.warn('[parseJsonField] Detected corrupted array data, returning default');
      return defaultValue;
    }
    // Valid array - check if items are proper objects
    if (value.length > 0 && typeof value[0] === 'object') {
      return value as T;
    }
    // Empty array is valid
    if (value.length === 0) {
      return value as T;
    }
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    // Already parsed object
    return value as T;
  }

  if (typeof value === 'string') {
    try {
      // First parse - might get either the final value or another JSON string
      let parsed = JSON.parse(value);

      // If result is still a string, it was double-encoded - parse again
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }

      return parsed as T;
    } catch {
      return defaultValue;
    }
  }
  return defaultValue;
}

/**
 * Map Gobodo record to parsed meeting agenda
 */
function mapGobodoToMeetingAgenda(record: Record<string, unknown>): ParsedMeetingAgenda {
  const data = (record.data as Record<string, unknown>) ?? record;

  return {
    id: String(record.id ?? ''),
    key: String(record.key ?? ''),
    name: String(data.Name ?? record.Name ?? ''),
    weekStartDate: String(data.Week_Start_Date__c ?? ''),
    meetingDate: String(data.Meeting_Date__c ?? data.Week_Start_Date__c ?? ''),
    attendance: parseJsonField<MeetingAttendanceRecord[]>(data.Attendance_Data__c, []),
    tasks: parseJsonField<MeetingTaskItem[]>(data.Tasks__c, []),
    agendaItems: parseJsonField<MeetingAgendaItem[]>(data.Agenda_Items__c, []),
    minutes: parseJsonField<MeetingMinutesData | null>(data.Minutes__c, null),
    createdBy: String(data.Created_By__c ?? data.CreatedById ?? ''),
    createdDate: String(data.createdDate ?? data.CreatedDate__c ?? ''),
    lastModifiedBy: String(data.Last_Modified_By__c ?? data.LastModifiedById ?? ''),
    lastModifiedDate: String(data.lastModified ?? data.LastModifiedDate__c ?? ''),
  };
}

/**
 * Fetch meeting agenda by week start date
 */
export function useMeetingAgenda(weekStartDate: string | null) {
  return useQuery({
    queryKey: meetingAgendaKeys.byWeek(weekStartDate ?? ''),
    queryFn: async (): Promise<ParsedMeetingAgenda | null> => {
      if (!weekStartDate || !USE_GOBODO_API) return null;

      console.log('[useMeetingAgenda] Fetching agenda for week:', weekStartDate);

      try {
        // Fetch all meeting agendas and find by week
        const records = await getRecordsForObject(MEETING_AGENDA_OBJECT, { size: 100 }) as Record<string, unknown>[];

        console.log('[useMeetingAgenda] Fetched', records.length, 'meeting agenda records');

        // Find the record for this week
        const agenda = records.find(r => {
          const data = (r.data as Record<string, unknown>) ?? r;
          const recordWeekDate = String(data.Week_Start_Date__c ?? '');
          // Match by exact date or by date part (ignore time component)
          return recordWeekDate === weekStartDate ||
            recordWeekDate.split('T')[0] === weekStartDate ||
            weekStartDate.split('T')[0] === recordWeekDate.split('T')[0];
        });

        if (!agenda) {
          console.log('[useMeetingAgenda] No agenda found for week:', weekStartDate);
          return null;
        }

        console.log('[useMeetingAgenda] Found agenda:', agenda);
        return mapGobodoToMeetingAgenda(agenda);
      } catch (error) {
        console.error('[useMeetingAgenda] Error fetching meeting agenda:', error);
        return null;
      }
    },
    enabled: !!weekStartDate && USE_GOBODO_API,
    staleTime: 30_000, // 30 seconds - meeting data changes frequently
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Create a new meeting agenda for a week
 */
export function useCreateMeetingAgenda() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      weekStartDate: string;
      meetingDate?: string;
      attendance?: MeetingAttendanceRecord[];
      tasks?: MeetingTaskItem[];
      agendaItems?: MeetingAgendaItem[];
      minutes?: MeetingMinutesData;
    }) => {
      console.log('[useCreateMeetingAgenda] Creating meeting agenda:', data);

      const weekNumber = getWeekNumberFromDate(data.weekStartDate);
      const year = new Date(data.weekStartDate).getFullYear();

      // Double-stringify to prevent Gobodo from parsing and corrupting JSON arrays
      const record = {
        Name: `MA-${year}-W${String(weekNumber).padStart(2, '0')}`,
        Meeting_Date__c: data.meetingDate || data.weekStartDate,
        Week_Start_Date__c: data.weekStartDate,
        Attendance_Data__c: JSON.stringify(JSON.stringify(data.attendance || [])),
        Tasks__c: JSON.stringify(JSON.stringify(data.tasks || [])),
        Agenda_Items__c: JSON.stringify(JSON.stringify(data.agendaItems || [])),
        Minutes__c: JSON.stringify(JSON.stringify(data.minutes || {})),
      };

      console.log('[useCreateMeetingAgenda] Creating record:', record);
      const result = await createRecord(MEETING_AGENDA_OBJECT, record);
      console.log('[useCreateMeetingAgenda] Created:', result);
      return result;
    },
    onSuccess: (_, variables) => {
      console.log('[useCreateMeetingAgenda] Success, invalidating queries');
      queryClient.invalidateQueries({ queryKey: meetingAgendaKeys.byWeek(variables.weekStartDate) });
      queryClient.invalidateQueries({ queryKey: meetingAgendaKeys.all() });
    },
    onError: (error) => {
      console.error('[useCreateMeetingAgenda] Error:', error);
    },
  });
}

/**
 * Update an existing meeting agenda
 */
export function useUpdateMeetingAgenda() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      id: string;
      weekStartDate: string;
      meetingDate?: string;
      attendance?: MeetingAttendanceRecord[];
      tasks?: MeetingTaskItem[];
      agendaItems?: MeetingAgendaItem[];
      minutes?: MeetingMinutesData;
    }) => {
      console.log('[useUpdateMeetingAgenda] Updating meeting agenda:', data.id);

      const updates: Record<string, unknown> = {};

      if (data.meetingDate !== undefined) {
        updates.Meeting_Date__c = data.meetingDate;
      }
      // Double-stringify to prevent Gobodo from parsing and corrupting JSON arrays
      if (data.attendance !== undefined) {
        updates.Attendance_Data__c = JSON.stringify(JSON.stringify(data.attendance));
      }
      if (data.tasks !== undefined) {
        updates.Tasks__c = JSON.stringify(JSON.stringify(data.tasks));
      }
      if (data.agendaItems !== undefined) {
        updates.Agenda_Items__c = JSON.stringify(JSON.stringify(data.agendaItems));
      }
      if (data.minutes !== undefined) {
        updates.Minutes__c = JSON.stringify(JSON.stringify(data.minutes));
      }

      console.log('[useUpdateMeetingAgenda] Updates:', updates);
      const result = await updateRecord(MEETING_AGENDA_OBJECT, data.id, updates, { gobodoPropertyPatch: true });
      console.log('[useUpdateMeetingAgenda] Updated:', result);
      return result;
    },
    onSuccess: (_, variables) => {
      console.log('[useUpdateMeetingAgenda] Success, invalidating queries');
      queryClient.invalidateQueries({ queryKey: meetingAgendaKeys.byWeek(variables.weekStartDate) });
      queryClient.invalidateQueries({ queryKey: meetingAgendaKeys.all() });
    },
    onError: (error) => {
      console.error('[useUpdateMeetingAgenda] Error:', error);
    },
  });
}
